import "./style.css";
import "./station-national.css";
import Navbar from "../../../../components/navbar/navbar";
import Footer from "../../../../components/footer/Footer";
import TitleSection from "../../../../components/title-section/title-section";
import { useTranslation } from "react-i18next";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import navBarEn from "../../../../translations/en/navbar.json";
import navBarEs from "../../../../translations/es/navbar.json";
import Helmet from "react-helmet";
import axios from "axios";
import ViewQuillComponent from "../../../dashboard/admin/stations/viewQuillComponent";
import StationsForm from "../stations-form/stations-form";
import { FaMapLocationDot } from "react-icons/fa6";
import { formatStationAddressForDisplay } from "../../shared/formatStationAddress";

function GoogleMapsStationButtonIcon() {
  return (
    <span className="maps-icon" aria-hidden>
      <FaMapLocationDot />
    </span>
  );
}

function getMarkerKeyFromElement(markerEl) {
  return Array.from(markerEl.classList)
    .filter(
      (c) =>
        c !== "svg-marker" &&
        c !== "is-active" &&
        c !== "is-hovered" &&
        !String(c).startsWith("is-")
    )
    .join(" ")
    .trim();
}

function findStationByMarkerKey(keyRaw, stationList) {
  const key = (keyRaw || "").toLowerCase();
  if (!key) {
    return (
      stationList.find(
        (s) => s.marker && String(s.marker).toLowerCase() === "morelos"
      ) || null
    );
  }
  return (
    stationList.find(
      (s) => s.marker && String(s.marker).toLowerCase().trim() === key
    ) || null
  );
}

/** IATA: 3 letras; códigos tipo MEX1 se muestran completos. */
function getMapTooltipCode(codeText) {
  const c = (codeText || "").trim();
  if (!c) {
    return "";
  }
  if (/^MEX\d+$/i.test(c)) {
    return c;
  }
  return c.length <= 3 ? c : c.substring(0, 3);
}

function getStationForMarkerElement(markerEl, stationList) {
  if (!markerEl || !stationList.length) {
    return null;
  }
  return findStationByMarkerKey(
    getMarkerKeyFromElement(markerEl),
    stationList
  );
}

const DEFAULT_MAP_EMBED_SRC =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7543.328370719443!2d-98.22936017965105!3d19.03451379680894!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x85cfc730cc904b2b%3A0xb530f1deb31d3a3b!2sSolesta!5e0!3m2!1ses!2smx!4v1769787897805!5m2!1ses!2smx";

/** Campo `maps` del admin: URL de Google Maps o embed. */
function parseStationMapsField(mapsRaw) {
  const maps = (mapsRaw || "").trim();
  if (!maps || !/^https?:\/\//i.test(maps)) {
    return { openHref: null, embedSrc: null };
  }
  const isEmbed =
    /google\.com\/maps\/embed/i.test(maps) || /\/embed\?/i.test(maps);
  if (isEmbed) {
    return { openHref: null, embedSrc: maps };
  }
  return { openHref: maps, embedSrc: null };
}

/** Iframe embed sin API key: búsqueda por dirección (p. ej. cuando `maps` es goo.gl). */
function buildGoogleMapsAddressEmbedSrc(address) {
  const query = (address || "").trim();
  if (!query) {
    return null;
  }
  return `https://www.google.com/maps?q=${encodeURIComponent(
    query
  )}&hl=es&z=15&output=embed`;
}

function getGoogleMapsHrefForStation(station) {
  if (!station) {
    return null;
  }
  const { openHref } = parseStationMapsField(station.maps);
  if (openHref) {
    return openHref;
  }
  if (station.address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      station.address
    )}`;
  }
  return null;
}

function getGoogleMapsEmbedSrcForStation(station) {
  if (!station) {
    return DEFAULT_MAP_EMBED_SRC;
  }
  const { embedSrc } = parseStationMapsField(station.maps);
  if (embedSrc) {
    return embedSrc;
  }
  const fromAddress = buildGoogleMapsAddressEmbedSrc(station.address);
  if (fromAddress) {
    return fromAddress;
  }
  return DEFAULT_MAP_EMBED_SRC;
}

function StatiosNationalsHome() {
  const [tStationsNationals] = useTranslation("stationsNationals");
  const [tSMetaData] = useTranslation("metaData");
  const [selectedOption, setSelectedOption] = useState("");
  const [stations, setStations] = useState([]);
  const [mapLayoutTick, setMapLayoutTick] = useState(0);
  const [hoveredCode, setHoveredCode] = useState("");
  const [mapTooltip, setMapTooltip] = useState({
    visible: false,
    left: 0,
    top: 0,
    code: "",
    name: "",
    placement: "right",
    /** Alineado con cálculo de posición para no desbordar al usar width real */
    maxW: 300,
  });
  const mapContainerRef = useRef(null);
  /** Pin bajo el cursor: tooltip al hacer hover. */
  const hoveredMarkerNodeRef = useRef(null);
  /** Pin concreto al que se hizo clic en el mapa; si se elige en el desplegable, se anula. */
  const selectedMarkerNodeRef = useRef(null);
  /** Retraso al salir del pin para poder mover el cursor al tooltip sin perder el hover. */
  const hoverLeaveTimerRef = useRef(null);

  const cancelHoverLeaveTimer = useCallback(() => {
    if (hoverLeaveTimerRef.current != null) {
      clearTimeout(hoverLeaveTimerRef.current);
      hoverLeaveTimerRef.current = null;
    }
  }, []);

  const scheduleHoverLeave = useCallback(() => {
    cancelHoverLeaveTimer();
    hoverLeaveTimerRef.current = window.setTimeout(() => {
      hoverLeaveTimerRef.current = null;
      hoveredMarkerNodeRef.current = null;
      setHoveredCode("");
    }, 220);
  }, [cancelHoverLeaveTimer]);

  /** No quitar el hover al pasar del globo otra vez al <path> del pin (sin esto, relatedTarget pone hoveredCode a ""). */
  const handleMapTooltipMouseLeave = useCallback(
    (e) => {
      cancelHoverLeaveTimer();
      const next = e.relatedTarget;
      if (next && next.nodeType === 1) {
        const el = next;
        const mapRoot = mapContainerRef.current;
        if (mapRoot && mapRoot.contains(el) && el.closest(".svg-marker")) {
          return;
        }
      }
      hoveredMarkerNodeRef.current = null;
      setHoveredCode("");
    },
    [cancelHoverLeaveTimer]
  );

  const station = stations.find((s) => s.code_text === selectedOption) || null;

  sessionStorage.setItem(
    "routeEs",
    `/${navBarEs["category-services"].stations}/${
      Object.keys(navBarEs["stations-submenu"])[0]
    }`
  );

  sessionStorage.setItem(
    "routeEn",
    `/${navBarEn["category-services"].stations}/${
      Object.keys(navBarEn["stations-submenu"])[0]
    }`
  );

  const handleSelectChange = (value) => {
    cancelHoverLeaveTimer();
    selectedMarkerNodeRef.current = null;
    hoveredMarkerNodeRef.current = null;
    setHoveredCode("");
    setSelectedOption(value);
  };

  const getStations = useCallback(async () => {
    const currentLng = localStorage.getItem("lng") || "es";
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL_BACKEND}/api/stations?lng=${currentLng}`,
        {
          headers: {
            authorization: process.env.REACT_APP_PUBLIC_KEY,
          },
        }
      );
      const orderStations = response.data.stations;
      orderStations.sort((a, b) => {
        const isMEXA = a.code_text.startsWith("MEX");
        const isMEXB = b.code_text.startsWith("MEX");

        const isEdoMexA = a.name.toLowerCase().startsWith("edomex");
        const isEdoMexB = b.name.toLowerCase().startsWith("edomex");

        if (isMEXA && !isMEXB) return -1;
        if (!isMEXA && isMEXB) return 1;

        if (isMEXA && isMEXB) {
          if (isEdoMexA && !isEdoMexB) return 1;
          if (!isEdoMexA && isEdoMexB) return -1;

          const numA = parseInt(a.code_text.substring(3), 10);
          const numB = parseInt(b.code_text.substring(3), 10);
          return numA - numB;
        }

        if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
        if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;

        return 0;
      });
      setStations(orderStations);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    getStations();
  }, [getStations]);

  useLayoutEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }
    const root = mapContainerRef.current;
    const markers = root.querySelectorAll(".svg-marker");
    const defaultFill = "#0b2343";
    const activeFill = "#fa0073";

    markers.forEach((marker) => {
      const paths = marker.querySelectorAll("path");
      if (!paths.length) {
        return;
      }
      const st = findStationByMarkerKey(getMarkerKeyFromElement(marker), stations);
      if (!st) {
        paths.forEach((p) => p.setAttribute("fill", defaultFill));
        marker.classList.remove("is-active");
        return;
      }
      const isActive = hoveredCode
        ? st.code_text === hoveredCode
        : st.code_text === selectedOption;
      const fill = isActive ? activeFill : defaultFill;
      paths.forEach((p) => p.setAttribute("fill", fill));
      marker.classList.toggle("is-active", !!isActive);
    });
  }, [hoveredCode, selectedOption, stations]);

  const findFirstMarkerForStation = useCallback((st) => {
    if (!mapContainerRef.current || !st?.marker) {
      return null;
    }
    const key = String(st.marker).toLowerCase().trim();
    const all = mapContainerRef.current.querySelectorAll(".svg-marker");
    for (const el of all) {
      if (getMarkerKeyFromElement(el) === key) {
        return el;
      }
    }
    return null;
  }, []);

  useLayoutEffect(() => {
    if (!mapContainerRef.current) {
      return;
    }
    const code = hoveredCode || selectedOption;
    if (!code || !stations.length) {
      setMapTooltip((t) => ({ ...t, visible: false }));
      return;
    }
    const st = stations.find((s) => s.code_text === code);
    if (!st) {
      setMapTooltip((t) => ({ ...t, visible: false }));
      return;
    }
    let markerEl = null;
    if (hoveredCode && hoveredMarkerNodeRef.current) {
      const hst = getStationForMarkerElement(
        hoveredMarkerNodeRef.current,
        stations
      );
      if (hst && hst.code_text === st.code_text) {
        markerEl = hoveredMarkerNodeRef.current;
      }
    }
    if (!markerEl) {
      const refNode = selectedMarkerNodeRef.current;
      const stFromRef = getStationForMarkerElement(refNode, stations);
      markerEl =
        refNode && stFromRef && stFromRef.code_text === st.code_text
          ? refNode
          : findFirstMarkerForStation(st);
    }
    if (!markerEl) {
      setMapTooltip((t) => ({ ...t, visible: false }));
      return;
    }
    const cont = mapContainerRef.current.getBoundingClientRect();
    const rect = markerEl.getBoundingClientRect();
    const displayCode =
      getMapTooltipCode(st.code_text) ||
      (st.code_text && String(st.code_text).trim()) ||
      "";
    const pad = 8;
    const gapRight = 10;
    const gapLeft = 1;
    const estW = Math.max(0, Math.min(300, cont.width - 16));
    let left = rect.right - cont.left + gapRight;
    let placement = "right";
    if (left + estW > cont.width - pad) {
      placement = "left";
      left = rect.left - cont.left - gapLeft - estW;
    }
    if (left < pad) {
      left = pad;
    }
    if (left + estW > cont.width - pad) {
      left = Math.max(pad, cont.width - pad - estW);
    }
    const pinCenterY = rect.top - cont.top + rect.height / 2;
    const edge = 4;
    const top = Math.max(edge, Math.min(pinCenterY, cont.height - edge));
    setMapTooltip({
      visible: true,
      left,
      top,
      code: displayCode,
      name: st.name || "",
      placement,
      maxW: estW,
    });
  }, [
    hoveredCode,
    selectedOption,
    stations,
    findFirstMarkerForStation,
    mapLayoutTick,
  ]);

  useEffect(() => {
    const onResize = () => setMapLayoutTick((n) => n + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (!stations.length) {
      return;
    }
    const id = requestAnimationFrame(() => {
      setMapLayoutTick((n) => n + 1);
    });
    return () => cancelAnimationFrame(id);
  }, [stations.length]);

  useEffect(() => {
    if (!mapContainerRef.current || !stations.length) {
      return undefined;
    }
    const cleanups = [];
    const root = mapContainerRef.current;
    root.querySelectorAll(".svg-marker").forEach((marker) => {
      const st = findStationByMarkerKey(
        getMarkerKeyFromElement(marker),
        stations
      );
      if (!st) {
        return;
      }
      const onEnter = () => {
        cancelHoverLeaveTimer();
        hoveredMarkerNodeRef.current = marker;
        setHoveredCode(st.code_text);
      };
      const onLeave = () => {
        scheduleHoverLeave();
      };
      const onClick = (e) => {
        e.stopPropagation();
        cancelHoverLeaveTimer();
        selectedMarkerNodeRef.current = marker;
        setSelectedOption(st.code_text);
      };
      const onKey = (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          e.stopPropagation();
          selectedMarkerNodeRef.current = marker;
          setSelectedOption(st.code_text);
        }
      };
      marker.setAttribute("role", "button");
      marker.setAttribute("tabIndex", "0");
      marker.setAttribute(
        "aria-label",
        `(${getMapTooltipCode(st.code_text)}) ${st.name}`
      );
      marker.style.cursor = "pointer";
      marker.addEventListener("mouseenter", onEnter);
      marker.addEventListener("mouseleave", onLeave);
      marker.addEventListener("click", onClick);
      marker.addEventListener("keydown", onKey);
      cleanups.push(() => {
        marker.removeEventListener("mouseenter", onEnter);
        marker.removeEventListener("mouseleave", onLeave);
        marker.removeEventListener("click", onClick);
        marker.removeEventListener("keydown", onKey);
        marker.removeAttribute("role");
        marker.removeAttribute("tabIndex");
        marker.removeAttribute("aria-label");
        marker.style.cursor = "";
      });
    });
    return () => {
      cancelHoverLeaveTimer();
      cleanups.forEach((fn) => fn());
    };
  }, [stations, cancelHoverLeaveTimer, scheduleHoverLeave]);

  const googleMapsHref = getGoogleMapsHrefForStation(station);
  const googleMapsEmbedSrc = getGoogleMapsEmbedSrcForStation(station);

  return (
    <>
      <Helmet>
        <title>{tSMetaData("estacionesNacionales.title")}</title>
        <meta
          name="description"
          content={tSMetaData("estacionesNacionales.description")}
        />
      </Helmet>
      <Navbar />
      <TitleSection title={tStationsNationals("title")} headingLevel={1} />

      <div className="map-national-container">
        <div className="information-gray">
          <h4 className="sidebar-directory-title">
            {tStationsNationals("text1")} {tStationsNationals("text2")}
          </h4>

          <StationsForm
            onDepartureSelect={handleSelectChange}
            selectedCode={selectedOption}
          />

          <div className="map-contenainer-view">
            <iframe
              key={station?.code_text || "default-map"}
              title={
                station
                  ? `${station.name} — ${tStationsNationals("go-google-maps")}`
                  : tStationsNationals("go-google-maps")
              }
              src={googleMapsEmbedSrc}
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />

            <div className="go-to-map-btn-content">
              {googleMapsHref ? (
                <a
                  href={googleMapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <GoogleMapsStationButtonIcon />
                  {tStationsNationals("go-google-maps")}
                </a>
              ) : (
                <span
                  className="go-to-map-btn-content__disabled"
                  aria-disabled="true"
                >
                  <GoogleMapsStationButtonIcon />
                  {tStationsNationals("go-google-maps")}
                </span>
              )}
            </div>
          </div>


          <div className={`info-station ${station ? "has-station" : "is-empty"}`}>
            {station ? (
              (
                <div className="station__national__direction__container station-detail-card">
                  <h4 className="station-info-heading">
                    <span className="station-info-code-tag">
                      ({getMapTooltipCode(station.code_text) || station.code_text})
                    </span>
                    <span className="js-station-title-text station-info-name">
                      {station.name}
                    </span>
                  </h4>
                  <dl className="station-detail-fields">
                    <div className="station-detail-field">
                      <dt className="station-detail-field__label">
                        {tStationsNationals("address")}
                      </dt>
                      <dd className="station-detail-field__value station-detail-field__value--address">
                        {formatStationAddressForDisplay(station.address)}
                      </dd>
                    </div>
                    <div className="station-detail-field">
                      <dt className="station-detail-field__label">
                        {tStationsNationals("phone")}
                      </dt>
                      <dd className="station-detail-field__value">
                        {(() => {
                          const digits = String(station.phone ?? "").replace(
                            /\D/g,
                            ""
                          );
                          return digits ? (
                            <a href={`tel:${digits}`}>{station.phone}</a>
                          ) : (
                            station.phone
                          );
                        })()}
                      </dd>
                    </div>
                    <div className="station-detail-field">
                      <dt className="station-detail-field__label">
                        {tStationsNationals("schedule")}
                      </dt>
                      <dd className="station-detail-field__value station-detail-field__value--rich">
                        <ViewQuillComponent
                          content={JSON.parse(station.schedule)}
                          type={"json"}
                        />
                      </dd>
                    </div>
                    {station.branch && (
                      <div className="station-detail-field">
                        <dt className="station-detail-field__label">
                          {tStationsNationals("branches")}
                        </dt>
                        <dd className="station-detail-field__value station-detail-field__value--rich">
                          <ViewQuillComponent
                            content={JSON.parse(station.branch)}
                            type={"json"}
                          />
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )
            ) : (
              <div></div>
            )}
          </div>
          

        </div>

        {/* ============================================== */}
        <div className="mex-map">
          <div className="station__national__maps" ref={mapContainerRef}>
          <svg
            className="mapMexico"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 733.923 500"
            preserveAspectRatio="xMidYMid meet"
          >
            <g
              id="Group_1226"
              data-name="Group 1226"
              transform="translate(-82 -222.25)"
            >
              <path
                d="M461.8,353.783l-2.241-2.165-.067-1.159,1.188-2.749,3.19-4.4.891-3.1,1.025-.7,1.715-.364,3.324-1.887,1.159-.881.374-1.207.7-.163.738,1.331,1.274,1.284,4.253,2.519.565,2.337,1.092,1.954h0l.249.565-.23,1.533,1.878,2.05-4.1,1.887-2.443,3.4-.69.479-2.893,1-1.207.057-2.079-.977-1.667-1.351-4.033-.364Z"
                transform="translate(-31.264 183.937)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <g
                id="Group_253"
                data-name="Group 253"
                transform="translate(154.547 369.28)"
              >
                <path
                  id="Path_220"
                  data-name="Path 220"
                  d="M177.7,182.517h-.623l-.728.115.125.421-.785.393-.651.01-.718.824-.278-.709.182-.747,2.95-3.6.594-.144.057.929-.766.728-.019.5Z"
                  transform="translate(-173.793 -178.778)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_221"
                  data-name="Path 221"
                  d="M174.231,184.72l-.449-1.146.247-.916,3-3.664.949-.23.081,1.326-.776.738-.013.338.812,1.577H177.07l-.421.068.1.353-1.068.508-.545.008Zm.053-1.179.105.27.53-.606.7-.011.558-.279-.146-.491,1-.157h.268l-.512-.992.026-.658.756-.718-.033-.533-.239.057-2.865,3.5Z"
                  transform="translate(-173.782 -178.765)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_254"
                data-name="Group 254"
                transform="translate(187.424 352.973)"
              >
                <path
                  id="Path_222"
                  data-name="Path 222"
                  d="M208.654,162.118l.785.172,2.567,2.174-.517.45-.575-.92-1.619-.91Z"
                  transform="translate(-208.127 -161.757)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_223"
                  data-name="Path 223"
                  d="M211.412,165.264l-.692-1.105-1.648-.957-.968-1.461,1.467.351,2.781,2.355Zm-.109-1.078.217.348.1-.083Zm-1.863-1.3.986.554-1.119-.947-.148-.033Z"
                  transform="translate(-208.104 -161.741)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_255"
                data-name="Group 255"
                transform="translate(194.382 351.643)"
              >
                <path
                  id="Path_224"
                  data-name="Path 224"
                  d="M217.053,160.618l.326.613-.259.977-1.111.038-.335-1.494,1.379-.134Z"
                  transform="translate(-215.381 -160.364)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_225"
                  data-name="Path 225"
                  d="M215.806,162.481l-.438-1.952,1.809-.176.444.837-.328,1.24Zm.148-1.529.234,1.036.734-.026.19-.714-.207-.389Z"
                  transform="translate(-215.368 -160.353)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_256"
                data-name="Group 256"
                transform="translate(170.862 328.951)"
              >
                <path
                  id="Path_226"
                  data-name="Path 226"
                  d="M191.993,137.258l-.019.46,1.054-.46.019.307.891.249,1.216,1.571.738.374,1.015,2.117-.565.824.115,1.121,1.763.8,2.778-.172.326.287-.163,4.148.594,1.638,1.149.354-.326,1.82-2.835-2L198.1,148.37l-2.452-1.743-.92-1.8-1.312-.249-.23-1.044-1.954-2.366-.153-.824.738-1.791-.2-.939.383-.354Z"
                  transform="translate(-190.825 -136.688)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_227"
                  data-name="Path 227"
                  d="M202.737,153.078l-3.2-2.276-1.624-2.286-2.494-1.806-.866-1.7-1.353-.256-.251-1.142-1.957-2.37-.178-.954.737-1.787-.215-1,.91-.841-.028.657,1.016-.443.03.478.8.225,1.232,1.59.762.386,1.111,2.315-.594.866.092.9,1.582.714,2.817-.175.486.428-.165,4.215.536,1.482,1.239.381ZM199.9,150.49l2.494,1.762.222-1.241-1.061-.328-.65-1.794.16-4.081-.166-.147-2.739.17-1.942-.876-.139-1.345.537-.783-.921-1.919-.746-.4L193.786,138l-.979-.273-.008-.135-.874.38.124.579-.739,1.8.129.694,1.964,2.419.2.89,1.273.241.946,1.854,2.46,1.765Z"
                  transform="translate(-190.814 -136.663)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_257"
                data-name="Group 257"
                transform="translate(150.979 316.989)"
              >
                <path
                  id="Path_228"
                  data-name="Path 228"
                  d="M170.477,124.728l.613.776-.431.134-.326-.345Z"
                  transform="translate(-170.069 -124.199)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_229"
                  data-name="Path 229"
                  d="M170.576,125.888l-.519-.549.3-1.163,1.13,1.43Z"
                  transform="translate(-170.058 -124.176)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <path
                id="Path_230"
                data-name="Path 230"
                d="M158.129,51.4l-.814.489-.45-.383-1.284.01-.575,1.964-.45.46-.048,1.284-1.734,2.328,1.341,1.906-.594,1.753L153.8,62.3l-.4,2.558.23.939-.575.939.92.91.881.125,3.659,2.644h0l-.45,1.667.881,3.832-1.073,1.4-.709,2.309L157.085,83l-.814,3.027.287.623-.239,2.127.594,2.117,1.312,1.207-.5.632-.019.623,2.72,1.763.565,3.381-.077,4.991,1.648,3.87.019,2.117-.335,1.245.2.785-.5,1.868.354.316-.623,2.069.958,1.973.594.364,1.657,3.832,1.178.584.287.862.92.144.613.853-.019,1.743.431.9.785.307.421.69,1.121.115.326-.824,1.734.747,1.159,1.475.3-.057.92,1.13.9.46.651,1.082,1.552.8,2,2.241,1.456.8.105.584,1.379.661,1.9,2.366,1.379,1.121.287.843-.7.814.01.46,1.992,2.443.757,1.571-.556,1.111.431,1.571-.316.239.776,1.619.785.1.508-.671-.393-.364.4-.594.46.259.45-.345.393.766.575-.115-.038.393.431.115.115,2,.374.91,1.054-.029,1.255-1,1.035.594,1.37,4.32.738.785.565,3.381,2.471,1.4,2.337-.565.46.345.067.7.776-.144.105.575-.345.46.239.632-.565,2.491,1.887,2.682-.45,1.945.594,1.389-.374,1.188.891.546.259.948-.287,2.385h0l-.613-1.638-.833-.249h0l-30.356-.105.22-.508-.335-.469-.048-1.264.824,1.159.757-1.331-1.542-.393-.335-1.648,1.456-2.864-.4-.92-.843.508-.489-.469,1.226-2.787.623-3.027-1.217-2.1-1.494-.6-.067-1.945-.422-.412-2.107-.1.01-.776-.431-.5-1.475-.824.115-.565-.441-1.121-.843-.45-.536-1.791-.441-.067-.22-.575-.92-.7-.929-.067-.163.316-.5-1.044-.651-.422-.22.287-.517-2.605-.613-1.006-1.015-.45-.613.326-.766-.939-.239-1.293-1.111-.527-.115-.354-.431.029-3.286-4.033-2.788-1.437-2.931-.383-1.485-2.4-2.309-1.13-.728-.029-1.916-1.839-1.14.239-1.916-2.079-.853-.1L137,127.9l.239-2.414-.316-1.437-1.092-1.552-1.5-.556.594-3.42-.517-1.15.019-4.014-1.4-2.443-1.973-1.188.182-1.341-.785-.738-.22.383.479.9-.479.335-.441-.671h-.508l.556,1.284-.3.1-1.015-1.964.546-5.1-.6-3.4-.508-1.006-.575-.077L126,97.227l-.508.01-2.2-2.893-1.485.192-.239-.441.709-5.058-1.322-2.6-2-2.107-1.772-3.008-1.236-.92-.651.077-.172-.958-1.217-.881-.259-.69-.393.077-.115-.556.977-.5-.421-.862.45-1.188-1.121-1.542-.316.134-.814-.958.431-.1,1.533.853,1.006-.92.182-2.471-.383-.584-1.207-.259-.546-.738-1.1-.192-.393-1.753-.46-.383h-1.178l-1.178-1.13L108.481,62l-.776-2.711-.45-.718-2.012-.68-.46-.536-2.05-5.652.038-1.284,57.656-5.307-1.514,2.711-.642.182.479,1.715-.335.23-.287,1.456Z"
                transform="translate(-16.244 196.142)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <g
                id="Group_258"
                data-name="Group 258"
                transform="translate(258.592 467.387)"
              >
                <path
                  id="Path_231"
                  data-name="Path 231"
                  d="M282.788,281.948l2.672,4.4.422,1.83-1.494-.278-.68-1.006-1.054-3.65.134-1.293Z"
                  transform="translate(-282.411 -281.216)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_232"
                  data-name="Path 232"
                  d="M286.187,288.444l-1.952-.363-.767-1.156L282.4,283.23l.213-2.046,3.071,5.075Zm-1.668-.8,1.037.193-.33-1.436-2.284-3.756-.056.539,1.03,3.569Z"
                  transform="translate(-282.4 -281.184)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_259"
                data-name="Group 259"
                transform="translate(209.05 463.91)"
              >
                <path
                  id="Path_233"
                  data-name="Path 233"
                  d="M231.3,277.808l2.989.153,1.063,1.379.9.105.776.594,1.274,2.328-.335.8-6.629-4.818-.3-.326Z"
                  transform="translate(-230.696 -277.565)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_234"
                  data-name="Path 234"
                  d="M238.058,283.524l-6.873-5-.5-.545.527-.428,3.19.163,1.07,1.387.865.1.893.709,1.33,2.429Zm-6.674-5.481.119.129,6.348,4.609.173-.411-1.195-2.182-.683-.523-.936-.109-1.058-1.373Z"
                  transform="translate(-230.681 -277.554)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_260"
                data-name="Group 260"
                transform="translate(247.542 463.643)"
              >
                <path
                  id="Path_235"
                  data-name="Path 235"
                  d="M272.009,277.568l1.7,1.523-.891,1.945-.517.029.038-.8-.7-.249.115-.479-.441-.556.172-.642-.259-.441.785-.335Z"
                  transform="translate(-270.879 -277.287)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_236"
                  data-name="Path 236"
                  d="M272.03,281.3l.042-.882-.735-.262.138-.575-.443-.558.177-.662-.346-.589,1.176-.5,1.941,1.743-1.025,2.236Zm-.126-1.45.665.236-.035.708.107-.006.758-1.653-1.45-1.3-.4.17.171.292-.167.622.438.553Z"
                  transform="translate(-270.864 -277.275)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_261"
                data-name="Group 261"
                transform="translate(247.176 462.006)"
              >
                <path
                  id="Path_237"
                  data-name="Path 237"
                  d="M271.165,275.988l.833,1.284-.565.316-.594-.345-.1-.747Z"
                  transform="translate(-270.493 -275.584)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_238"
                  data-name="Path 238"
                  d="M271.42,277.845l-.814-.472-.124-.967.7-.839,1.15,1.772Zm-.369-.767.374.216.223-.124-.517-.8-.147.177Z"
                  transform="translate(-270.482 -275.566)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_262"
                data-name="Group 262"
                transform="translate(239.816 448.312)"
              >
                <path
                  id="Path_239"
                  data-name="Path 239"
                  d="M263.39,261.908l.881,1.389,1.9.517.4.575.057,1.475.929,2.069-.45.086-2.529-1.954-.651-1.935-.853-.862Z"
                  transform="translate(-262.811 -261.297)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_240"
                  data-name="Path 240"
                  d="M267.034,268.248l-2.672-2.065-.656-1.95-.907-.918.475-2.045,1.138,1.793,1.89.516.493.7.058,1.5,1.034,2.3Zm-2.264-2.355,2.385,1.844.047-.01-.826-1.881-.055-1.405L266.01,264l-1.9-.518-.625-.986-.156.675.816.86Z"
                  transform="translate(-262.799 -261.27)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_263"
                data-name="Group 263"
                transform="translate(201.826 443.366)"
              >
                <path
                  id="Path_241"
                  data-name="Path 241"
                  d="M227.551,257.328l.2,1.322-1.054,3.257.105,2.95-.728.651.632,1.274-.316,1.092-.6.067-.3.508.153.69,1.092,1.217-.153.278.891.785.421,1.284-.642,1.054.881.747,1.2,1.734-.067.364-1.015-.517-.048-.632-1.705-1.82.182-.508.546.077.211-.517-.115-1.025-.766-.939-1.274-.661-1.169.354-.671-.431-.038-.556.393-.077,1.868-4.952,1.887-7.069Z"
                  transform="translate(-223.15 -256.158)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_242"
                  data-name="Path 242"
                  d="M229.432,276.909l-1.421-.724-.052-.682-1.747-1.866.3-.829.555.078.117-.287-.1-.895-.69-.846-1.144-.594-1.181.359-.865-.556-.061-.887.469-.092,1.821-4.827,2.175-8.155.382,2.512-1.058,3.27.107,3.02-.675.6.593,1.194-.386,1.335-.644.072-.189.323.12.536,1.163,1.3-.142.259.8.7.476,1.45-.593.972.767.662,1.254,1.815Zm-.964-1.029.542.276-.585-.848Zm-1.31-1.933,1.242,1.326-.455-.659Zm-.4-.424.189.2.208-.342-.33-.046Zm-1.468-3.773,1.138.591,0,0-1.02-1.136-.187-.842.405-.694.563-.062.246-.849-.672-1.354.781-.7-.088-2.458-.578,2.164-1.923,5.1-.316.061.016.225.476.306Zm2.181-11.3-.3,1.117.319-.984Z"
                  transform="translate(-223.139 -256.107)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_264"
                data-name="Group 264"
                transform="translate(239.438 442.876)"
              >
                <path
                  id="Path_243"
                  data-name="Path 243"
                  d="M263.757,256.2l-.431,1.149-.5.23-.144-.709Z"
                  transform="translate(-262.416 -255.62)"
                  fill="#0b2343"
                ></path>
                <path
                  id="Path_244"
                  data-name="Path 244"
                  d="M262.642,257.9l-.238-1.168,1.812-1.133-.713,1.9Zm.3-.938.051.249.134-.061.149-.4Z"
                  transform="translate(-262.404 -255.595)"
                  fill="#123660"
                ></path>
              </g>
              <g
                id="Group_265"
                data-name="Group 265"
                transform="translate(205.781 438.687)"
              >
                <path
                  id="Path_245"
                  data-name="Path 245"
                  d="M228.53,251.268l-.153,4.234-.354,1.312-.479-.335.987-5.211Z"
                  transform="translate(-227.28 -251.224)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_246"
                  data-name="Path 246"
                  d="M228.153,257.2l-.885-.619,1.015-5.362.475.053-.153,4.234Zm-.356-.833.072.051.266-.983.035-1.046Z"
                  transform="translate(-227.268 -251.222)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_266"
                data-name="Group 266"
                transform="translate(206.556 433.731)"
              >
                <path
                  id="Path_247"
                  data-name="Path 247"
                  d="M228.622,246.068l.153,1.992-.163,2.4-.268.01.278-4.406Z"
                  transform="translate(-228.088 -246.05)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_248"
                  data-name="Path 248"
                  d="M228.077,250.722l.295-4.67.478,0,.153,1.992-.177,2.654Z"
                  transform="translate(-228.077 -246.049)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_267"
                data-name="Group 267"
                transform="translate(237.734 432.069)"
              >
                <path
                  id="Path_249"
                  data-name="Path 249"
                  d="M261.66,245.148l.3,3.161-.68-.421-.383-1.341Z"
                  transform="translate(-260.636 -244.348)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_250"
                  data-name="Path 250"
                  d="M262.229,248.731l-1.169-.724-.436-1.524,1.189-2.17Zm-.759-1.034.192.119-.179-1.9-.343.627Z"
                  transform="translate(-260.625 -244.313)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_268"
                data-name="Group 268"
                transform="translate(231.609 432.549)"
              >
                <path
                  id="Path_251"
                  data-name="Path 251"
                  d="M255.128,245.108l.517,1.2-.316.489-.393-.192-.393-1.351.584-.144Z"
                  transform="translate(-254.244 -244.827)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_252"
                  data-name="Path 252"
                  d="M255.4,247.09l-.678-.331-.492-1.693,1.023-.251.649,1.5Zm-.278-.669.106.052.131-.2-.386-.895-.145.036Z"
                  transform="translate(-254.231 -244.815)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_269"
                data-name="Group 269"
                transform="translate(227.486 422.991)"
              >
                <path
                  id="Path_253"
                  data-name="Path 253"
                  d="M254.313,235.328l-.479.977.383,1.6-1.149-.709-1.638,2.558v.881l-.536.527v.651l-.575.048-.125-.728.383-.431.172-1.609,1.006-1.667-.182-1.4.939-.24.172-.412.613.383,1.015-.431Z"
                  transform="translate(-249.938 -234.857)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_254"
                  data-name="Path 254"
                  d="M250.108,242.1l-.181-1.058.408-.46.2-1.632.962-1.594-.2-1.526,1.029-.262.236-.562.746.467,1.491-.633-.723,1.476.5,2.081-1.443-.89-1.473,2.3v.912l-.536.527v.771Zm.33-.912.069.4.136-.012v-.532l.536-.527v-.851l1.8-2.815.856.528-.269-1.12.235-.477-.538.229-.48-.3-.11.262-.849.217.166,1.27-1.021,1.693-.175,1.633Z"
                  transform="translate(-249.927 -234.836)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_270"
                data-name="Group 270"
                transform="translate(226.186 421.256)"
              >
                <path
                  id="Path_255"
                  data-name="Path 255"
                  d="M249.285,233.438l.172.757-.431.048-.163-.517Z"
                  transform="translate(-248.582 -233.042)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_256"
                  data-name="Path 256"
                  d="M248.845,234.485l-.276-.875.859-.585.311,1.361Z"
                  transform="translate(-248.569 -233.025)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_271"
                data-name="Group 271"
                transform="translate(178.639 405.563)"
              >
                <path
                  id="Path_257"
                  data-name="Path 257"
                  d="M200.173,216.908l1.284.307.718,1.5-.92-.6-1.887-.46-.115-.268.92-.479Z"
                  transform="translate(-198.947 -216.654)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_258"
                  data-name="Path 258"
                  d="M202.747,219.378l-1.6-1.051-1.964-.479-.249-.582,1.2-.623,1.482.354Zm-3.146-1.921,1.772.446.2.133-.3-.627-1.084-.26Z"
                  transform="translate(-198.933 -216.643)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_272"
                data-name="Group 272"
                transform="translate(206.577 391.293)"
              >
                <path
                  id="Path_259"
                  data-name="Path 259"
                  d="M228.757,202.138l1.015.958-.326,1.169-.594-.153-.479-1.351Z"
                  transform="translate(-228.111 -201.762)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_260"
                  data-name="Path 260"
                  d="M229.6,204.538l-.944-.243-.559-1.579.6-.971L230.03,203Zm-.578-.644.243.063.224-.8-.7-.657-.17.275Z"
                  transform="translate(-228.099 -201.745)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_273"
                data-name="Group 273"
                transform="translate(211.391 386.144)"
              >
                <path
                  id="Path_261"
                  data-name="Path 261"
                  d="M233.827,196.628l.4.048.268.594-.891.038-.172-.441.393-.24Z"
                  transform="translate(-233.138 -196.381)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_262"
                  data-name="Path 262"
                  d="M233.432,197.543l-.307-.786.634-.387.619.073.47,1.04Zm.285-.587.037.095.366-.016-.067-.148-.186-.022Z"
                  transform="translate(-233.125 -196.37)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <g
                id="Group_274"
                data-name="Group 274"
                transform="translate(131.864 374.172)"
              >
                <path
                  id="Path_263"
                  data-name="Path 263"
                  d="M150.5,184.228l1.312.718-.1.508-1.312-.977Z"
                  transform="translate(-150.115 -183.887)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_264"
                  data-name="Path 264"
                  d="M151.868,185.86l-1.766-1.314.26-.674,1.7.934Z"
                  transform="translate(-150.102 -183.872)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <path
                id="Path_265"
                data-name="Path 265"
                d="M176.227,182.668h.623l.393.393.278-.393,30.365.1h0l.833.249.613,1.638h0l-.067.479,1.236.987.383,1.878,1.035,1.236.4-.038,1.178,1.628,2.663.824.517.594.9.144,1.542,1.063.68.92.671,3.573,1.571,1.734.929,2.117-.393.728,2.491,2.012,2.433.623.45.546.891-.01.144.7-.546.029-.853,1-.22,1.513,1.609,2.318.862.441.038.441.881.632.316,1.312-.671.527-.45,1.236,1.121.7.278,1.121,1.035.632.661,1.619.958.718,1.485-1.322-3.3-3.429-.22-2.787-.651-1.571.709-.45L233.8,212.4l.642,1.044,1.293.891.45.718,2.069,1.312-.182,3.362.335.536,2.606.814-.575,1.561.038,1.188,1.1,1.8.68.3.211.479.067,1.092-.335.632,1.9,4.943-.556.9.1,1.293-.5.69.105.948.431.556.077,1.839.6.642.038,1.006,1.724,1.408.211-.163.766,2.979,1.763,2.3,2.452.3-.211,1.542,1.121,3.008.814.814.287,1.006-.287.163.364,1.466.594.776-.038.958.651.517.422,1.207.824.46.489.862.45-.125.728.594.24,1.245,1.542,2.117.642,1.992-.163,1.14-.709,1.7-.833.891-.211,2.96.211,1,1.006,1.331.125,2.759,1.015,1.188.661,1.5,1.475,1.293,4.157.91,1.159-.115.057.268-1.226.479-.757-.623-.4.24.2,1.4,1.609-.125-.01-.747,1.159-.987.057-.939-.872-2.107.268-1.025.68-.431,1.494.2v.747l1.015.594.4,1.073,2.06.948,1.973,1.513.68,3.094,1.149.287,2.491-.843.038.556.508.469-.671.824.067,1.945,3.1,3.027-.268,1.887.3,1.619.374.45,5.154,2.519-.3.776,1.37,1.983-.211.661.4.412-.489,4.205-.565,1.207-1.791,2.213-1,.69-2.107.517-1.159.785-.019.718-1.983,1.092-.508,1-.9.709-.891.038-.613.642h-1.1l-2.318-1.925-1.121-2.653-1.4-6.8-2.395-4.378-1.753-2.337-5.929-3.142L250.541,284.6,240.2,278.334l.115-.249L239.2,276.9l-1.485-.2.057-.46-.613-.067-.977-2.845.268-.105-.45-.556-.948-.287-.1,1.092-2.309-.191-.057.718-.575-.01.144-1.418-.7-1.925-2.1-2.309.259-.239-.536-.479-.651.259.125.422-1.4-.23-1.925-.929-.3-.661.517-.029.105-.728-.594-.383-.163-.747.623-.929-.259-.153.374-.69-.421-.125.23-.431-.4-.46-.546.881.278.728-.374.469.048,2.423h-.7l-.46-1.1.738-1.485-.623-1.331.776-.431.709-1.6.019-.92-.824.115.153-1.81.833-1.925.259-1.7.68-.115.057-2.347-.479-.105.057-1.121.556-.421-.374-.316-.2-1.025.287-.958.287-.048.125-2.309-.125-1.226-.766-1.025-.326-2.759-1.945-5.134-.9-1.188-1.849-1.015-.833-2.73-1.571-1.858-1.514-.383-.182.632-.431.125-3.056-2.232-.661-.249-.642.268-.757-1.14-1.9-1.341.01-.421-.6-.029-3.439-2.835.929.086,1.217.9.125-.23L206,220.831l-1.035-.307-.661.211-2.174-1.9.182-.182-.383-.479.364-.584-1.657-1.983-.9-1.9-.345,1.006-1.446-.671.3-.307.489.153,1.485-1.494.4-1.236-.785-2.692-.575.623-.364,3.084-.536.469-.671-.22-.364.718.057,1.753-.431.048-2.05-1.274-1.446-.249.2-.508.517.374.067-.364-.508-.192-1.379.211-.766.853-.374,1.14-.6.268-.01.556-.68.383-.785-.249-.766-.5-.671-1.351-1.533-.383-1.542-3.535-1.734-1.284-2.117-.546-.431.153-.172.527-.421.029-1.226-1.284-1.054-1.906-1.035-.766-1.169-.556-1.935-.287-.718.632-2.328-1.485-.489.393-.46-.249.057-.766-.594-.374-.508.105-.23-.354-.019-3.372-.46-1.5-2.251-1.83-3.19-1.159-1.1-2.155-1.6-1.2-.067-.977-.651-.441-.594.91-.738-.335-.3-.872-.987-.508-.536.259-1.035-2.692-.757-.747.766-.393,1.015.872,2.232-.211,1.456.22.345.479,1.312-.172,1.552.287.441.469,1.514.316,2.653-.22.565.335,2.337-1.264.575.335.728-.144.144-.575-.6-.038,1.054-1.149.824.278-.5,2.376.728,1.207,1.437,1.149,1.217.354,1.293,1.149-.048.4.766.249.977-1.025.057-.843,2.012.69,1.494,2.835.853.134.565-1.121-1.5-1.552.115-1.006.642.623.144-.393-.546-.431.259-.584-.441-1.667.22-.862-1.676-1.379-.987,1.81.182,1.092-3.046,1.159-1.571-1.188.23-.517.575.479-.709-1.9,1.121-1.667-.517-.46.2-1.37Z"
                transform="translate(-18.393 190.351)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-CAM"
                d="M742.357,429.5l.479.757-.651.584-.517.211-1.015-.546-.364.316.374.316-1.59,1.284-.517-.642-.278.556-2.031.824-1.044.1-.517-.546.259-.306,2.318-.374,5.1-2.529Zm27.492-51.209-.153,6.993,3.947.488.929,1.925.431.172,1.063-.335.9.948.46-.086.383-.967.757-.7.4.268.987,1.686-.105.872.546,1,1.753,1.121,1.9,4.272,3.7,2.012,1.121,2.385,3.037,3.429.757,3.3.421.441h0l1.284,1.734,5.077,4.071,3.266,4.473-.172,5.6-1.121.632-.393.642.211,2.414-.766,4.914.163,1.217.948,1.188.144.68-.489,1.312.785.441-.546,1.6.45,1.322.268,4.5-.546,1.207-2.337,2.711.24,2.356h0l-.019,3.19-44.006-.01h0l.019-1.571-3.812-1.513-.651.4-.738-.862-1.494-.632-1.571-1.485-3.5-.766-2.672.125-.287,1.245.326,1.743-.287.69-1.609.105-1.236.613-2.117-1.073-1.437.144-1.255-.632-.833-1.37-3.324-2.232-1.169-1.753-1.035-2.481-.431-.306.172-1.887-1.063-1.36-.345-.153-.077.4-.508.125-.661-1.015-.948-.048L721.6,437.2l-.6-.872-1.092-3.563h0l3-.536,9.416-.584.843,1.351,1.427.766-.575,1.13-.259-.374-.3.326-.92-1.226-.7.814-.651-.4-.23.172.163.69-.431.546.565.709,1.1-.239-.431-1.073.757.038.268.441.546-.134,1.054.69.153.536,6.5,1.6,1.37-.125.153-.671.7-.671,4.263-1.466.785-2.8.6-.307.335-1.264-.22-.671-1.054-.958-.881.69-1.1-.584-.278-.939.584-.45-.067-.757.929-1-.738.182-.67,1.274-.728.69-.5-.048-.786.728-.115.536-.6.029v-.23l2.395-2.462,6.523-3.506,4.588-3.3,4.655-4.339.45-.891-.077-.977.5-1.916-.2-2.079.642-1.724-.489-.536-.038-.613.891-1.092.182-.987,3.688-2.74,1.35-2.376-.938-2.6.286-1.312-.392-1.226.364-.278-.374-4.291.287-.747-.432-3.257,1.285-3.41.421-2.893,1.092-1.36Z"
                transform="translate(-42.227 182.115)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-CHP"
                d="M678.782,500.716l.172-.613h0l-.057-.393.383.01.728,1.1Zm-.354-3.841-.01-.747h0L678.3,494.1l.5-1.389-1.2-2.807.077-.814,2.1-6.016,2.816-2.51.766-3.343.728-.977-.019-1.916.326-.9h0l.9-1.082,1.868-.824,3.563-2.768h0l2.423-3.037.057-1.542,1.332-1.791,1.149-.757.565-1.111.718-6.782,1.59-.268.517-.584,2.05.814,1.332,1.619.737.335,2.673-.086-.335,1.619-1.36,2.031-.134.556.364.891,1.983,2.088-.287,1.791.345.316,1.2-.057.422-.335.517.192,1.159,1.858.038.632.632.833.776.3,1.83-.8,2.318-2.155,5.585-4.09.546-.8,3.152-1.14.374-.747-.393-1.676.115-.354.412.077-.038-.422,1.7.46,1.868-.029.68-1.763,1.389.354-.115.565.536.287.565-.421,1.14.172,1.159-.268,1.015,1.3.048,1.925,2.452,1.207,1.217,3.161-.718,2.548,4.157,1.149.182.8,1.255.354,1.054,1.715-.728.268-.508.987h0l-.623.057.546.939.728-.038.939,1.073.8-.335.891.3-.019.8.508.709.6.249.316.92,1.073,1.111,1.006.22.287.987,1.322,1.312.105.613,1.284.383.508.594.259-.278,1.054.421.23.814h.987l.843.69,1.082-.077.038.527,2.05,1.2,1.322,2.088.335,3.084.785-.1.105.527-.642.22.22.383L763.1,490l.345.489.843-.3.546.6.546-.268.508.632-.144.469h1.638l.364.565.115.556-.345.335.326.508-.316.843-.517.038-.172.613.345.335-.738,1.552.766,1.015-.642.421.536.278-.019.393-.613-.048.259,1.3-30.787-.259-11.734,20.183,3.707,4.79-.958,1.533-.814-.086-.335.69.125,2.385-1.006,1.322.881,4.109-.7,1.121-.364,1.475-.824.68-4.272-3.889-10.527-11.093-.1-.814-.766-.182-10.69-10.077-5.211-4.109-5.824-4.042-3.659-1.762.268-.383.718-.057.278.584.6-.259.134-.46-1.073-1.762L679.338,499l-.7-.872-.211-1.255Z"
                transform="translate(-40.446 178.975)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-CHH"
                d="M440.675,148.943l-2.165,3.985.019.335,1.638.584.01,1-.632.939-.8-.307-.671.326-1.657,3.841-1.351,1.063-1.571.584-2.174,3.621-.536,1.054.833,1.36-.9,1.322-2.548,6.523-.575.364-1.7-.68-.01,1.485,1.073.172.469,1.4-.326.46-1.82.364.728,1.379-.057.8-.546.785.69.747.134.69-1.044,1.111.776,4.33,1.082.479.23.8.086,1.015-.46,1.092.651,3.525.211,4.617.517.651,1.753.757-.393,1.475-1.1.038-1.025.958-1.839.872.421.766-.1,1.255,2.059,1.82,1.035,1.753h0l-1.054.23-1.1-.824-.488.651-.268-.163.421-.69-.757-.115-3.075,1.006-1.446-.546-.843.776-1.37-.268-.651-.958-1.37-.23-1.322-.853-.7.307-.3.709.46.632,1.475.115-.067.958-2.395.374-1.1,1.456-1.446.517-1.81,2.95.23,1.284-.239.738-.421.23-1.638-.192-1.667.872-.939-.2-1.648-1.14-1.868-1.81-2.433.23-1.169-.316-.709-.651-.517-.038-.01,1.035-1.533.671-.5.556-.546.086-.393-.517-1.609.92-.22-.575-1.494-.45-1.216.239-1.054-1.81-1.571.278L386,214.865h-.939l-1.513-1.3-1.858-.22-.268-.556-1.207-.68-.92-1.858-1.015-.22-1.83-1.178-1.217-.057-.642-.853-.738.517-.2,1.236-1.686.546.048,1.763-.6.45-.862,3.247.029,1.533-.862,1.466.144.441,1.073.479-.029,1.025-.316.556-1.9.833-.824.833v1.3l-.958-.153-.393.345.345,2.05-.881.249-1.648,3.822.929,1.494.584,1.82-2.031,3.276-3.381,2.605-1.178,1.542-.613-.268L358,239.79l-1.925-.316-.5-.824-1.533.211-1.035-.632-.3-1.264-2.213.747-.307.623h0l-2.05-2.673-.565-3.094-2.768-4.684-2.261-.661-3.056-.3-3.65-1.082-1.169-4.483-2.979-4.809-.067-1.8-1.36-.881-.306-1.044.468-1.916-.468-.785.382-2.174-1.014-2.232-1.169.239-.584-.23-.115-2.778-.651-.364-4.684.211h0l-1.686-.374-1.935.594-3.391-1.763.019-.441.7-.4-.46-3.3,1.2-3.008-.038-1.648-1.561-3.9-.929-1.37-.6-2-2.692-1.628-.5-.623.671-2.347-.364-1.59-2-2.855-1.743-.709-.374-.661.565-2.644-1.494-1.025-.182-.584,1-1.331,2.423-.469,1.389-.814.738.134,1.7,1.255.479-.029,1.351-1.006,1.092-.115.929-1.169.891-.086.575-1.14-2.155-.824-1.485-1.734.4-2.778-.8-1.868.182-1,1.418-2.51-.766-2.538.8-1.226-.442-2.443,1.428-1.207.1-2.577,1.322-.642.278-.489-.249-5.431.153-.862.766-.91.01-2.491-.632-5.958-.718-.92-.546-.019-.326-.958.565-4.722,1.217-2.462v-1.063l-1.389-1.274-.153-.948.814-.5.268-1.743-1.858-5.891.057-1.14-1.054-1.4-.105-.776.641-1.542-1-.891-.086-2.318-2.615-2.538.038-.584,1.5-.077-.019-1.734.469-1.217-.23-1.533-1.571-2.72-.5-1.839.9-1.226-1.006-1h0l13.506-.038V72.6h40.279l.958,1.025,1.523-.125.968.536.833.987,1.676,3.86.728.393,1.082,2.27,3.065,1.878,1.734.172,1.2.766.565,1.418.546-.01.354.632h.5l2.088,2.548.24.8,1.226.431.393.546,1.437.565,1.111.958,1.159,2.6,1.149.556.345.728,1.79,1.13.527.862-.163.45.939.153,1.4.967.268-.354.872.833.853-.239.2.594.383-.22.536.632.4.01-.1.421,1.149.182.766,1.293.814.508.872-.134.546.7.239,1.245,1.1.412.814.977.728,2.031.019,1.4.441.718-.182.814,1.062.7-.211.316,1.456,1.523-.077.508,1.3,1.054.441,1.523-.153,1.437.268.958-.469,1.983.345.2-.038,1.035.412.7-.3.393.259,1.025,1.035.891,1.092,1.83,1.255,3.745,3.3,2.625.383.8,1.082.594.508-.316,1.887.958.115.421.546.172-.115.6,1.169,1.648,1.379.536,1.331,1.255,1.887.862,2.883.4,1.322.613.383-.153.211,1.226.383-.278.958,1.331,1.408.3.249.374,2.241-.019,2.021,2-.105.46.575-.067.469.718Z"
                transform="translate(-24.773 194.985)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-COA"
                d="M530.871,185.42l-.249.584-1.044.757-.91.057-1.839,1.245-5.288-.881-3.516.249-1.082.6-.527,2.241-1.619,2.491-.278.757.153,2.194-.6.469-2.136-.115-2.011.881-1.14-.364-1.14,1-1.753,2.855.239,3.2,2.7,1.983.565-.048.671-2.989.6.067,1.964,1.418.4,4.914-.8,2.146-1.006.738-1.092,2.1-3.065-.038-2.5,1.628-3.362.268-.833.862-1,2.615-2.318,2.318.019,1.245,4.349,4.579,2.586,1.935,1.006,2.979,1.36,1.1.824.077.364.613-.948,2.778.23,2.366.8,1.705,1.063.134.8.632,2.04.077.335.345.536,3.266.785.814,3.142,1.8-.489,1.006,3.448.6.479.766.843.307.374.91-.718.853-.144,1.1-.326.192-1.858-.029-3.266-.843-1.255-1.466-1.667-.163-.9.3.278.776-1.715.546-1.628,3.084.853,2.807.086,2.069-.192.374-2.309.057-.153.354.278.8-.307,1.149,1.322,1.485.105,1.332.623,1.178-.958,3.123h0l-2.031-.45-.718.307-.172.747-1.379.354-2-.776-1.312-1.245-1.839-3.6-1.2-.786-2.835-.354-2.174.489-.881.843-2.711-.019-.594-3.41-1.111-.307-1.408.393-2.069-2.941-1.877-1.59-2.395-.929-2.433-1.638-9-.584-5.173,1.025-.6.508-.019,1.561h0L458.3,260.27l-.556.967-.489,2.05.479,1.648-.268.5-.661.2-2.012-1.293-2.663-.153-3.276-1.523-1.245-1.15-.594-.824L446.5,257.7l-2.328-1.849-.575-1.667-1.7-2.031-.6-3.017,1.3-1.3,2.165-.431L444,246.227l-1.284-.249-.642-.785.086-.824,1.341-2.059,1.015-.776,1.15-2.979-1.15-4.282-.172-1.906.393-2.184-1.609-3.784-.163-1.331.421-2.807-1.322-3.8-2.2-2.04-.785-1.159-1.169-1.13-2.855-.757-.69-.6h0l-1.035-1.753-2.06-1.82.1-1.255-.421-.766,1.839-.872,1.025-.958,1.1-.038.393-1.475-1.753-.757-.517-.651-.211-4.617-.651-3.525.46-1.092-.086-1.015-.23-.8-1.082-.479-.776-4.33,1.044-1.111-.134-.69-.69-.747.546-.785.057-.8-.728-1.379,1.82-.364.326-.46-.469-1.4-1.073-.172.01-1.485,1.7.68.575-.364,2.548-6.523.9-1.322-.833-1.36.536-1.054,2.174-3.621,1.571-.584,1.351-1.063,1.657-3.841.671-.326.8.307.632-.939-.01-1-1.638-.584-.019-.335,2.165-3.985h0l.661.067.259-.6.4.077-.249.546,1.3.68.1.489.421-.431.326.383,1.542-.105.814.364.853-.345.488-2.155.259.192.278-1.015.785-.105,1.044-1.648.019-.565.594-.239.354.278.948-.45.163-.68.6.211.441-.335-.192-.623-.766-.431.412-.776.163-1.686.987-.211-.077-.316.68-.872-.364-.239.048-.862.671-1.2-.125-1.006.709-.575.306-1.485.517.125.067-1.188,1.13-1.054-.115-.4.661-1.494,1.063.22,1.322-.565.6.307.824-.565-.077-.479,2.567.5.671-.613.393-1.552,1.035-.948,1.341.307.01.374,2.04.335,1.542,1.36,1.341.163.479-.134.019-.316.987-.124,1.513.22.259.383.431-.374,1.964-.307.45.278-.057.517.747-.239,1.609.718.412-.182.354.307.393-.278.268.383,1.83-.536.077-.536.584-.479.182,1.427,1.9-.8.192,1.006,1.015-.508.22.2-.393.3.364.977.527.383.316,1.36,1.15-.2.364.565-.268,1.466.584.421.22-1.274.3-.335.441.134.144,1.552-.469,1.025.287.326,1.83.172.536,1.006,2.117.469,1.245,2.414,1.571.718,1.37.987.287.766,1.887.967.728,1.609-.23.4,2.28,1.715.383.843.335,3.583.441,1.169.709.067.632,1.925.651.105.326.584.1,1.264.584.575.182,2.04,1.255.613.019.776,1.2.738-.355,1.073.613.814L519,165.5l-.115.393-.623.163.661.9-.22,1.312,1.437,2.3L520.1,171.6l.929,1.073.728.086.287,1.054,3.008,1.312.843,1.724.776.441.182.977.785,1.14.872.115-.019,1.044.958.967-.172.977.68,1.015v1.015l.642.105Z"
                transform="translate(-30.041 192.657)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-COL"
                d="M442.67,426.36l-2.366,2.146-.977-.3-.364.268.029,1.715-1.782,1.13-.651,1.628h0l-5.278-4.176-4.962-2.653-3.851-1.3.134-.8.441-.048.144-.594-.431-.5L421.7,422.3l-1.763.9-.239-.6-1.676-.709-4.339-1.159.316-.421.594.393.172-.249-.565-.211h0l1.839.048,1.418-1.715,1.092.46.536-.105.46-.8.833-.556.527.383,3.611-.843,1.217.364,1.082-.278,2.969-2.222,3.113.307,1.351-.2,3.084-1.609,2.4-2.309.584.125.46,2.768-.153.977.929.8.2.747,1.025.46-.479,1.724.067,2.989-.536,1.264.786,1.475Z"
                transform="translate(-29.335 180.731)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.25"
              ></path>
              <path
                id="MX-DIF"
                d="M559.279,423.539l-.91-.019-2.251.718-.872-.766-.6.115L553.4,424.63l-1.274-1.121-1.274.23h0l-2.519-4.2-.086-.939,3.037-2.634.527-1.9,2.146-2.356.316.345-.048,1.015,1.5,1.839.46,1.485,2.05,1.715.307,1.418.45-.364.22.469.335,1.513-.45,1.6Z"
                transform="translate(-35 180.708)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-DUR"
                d="M352.048,244.282l.307-.623,2.213-.747.3,1.264,1.035.632,1.533-.211.5.824,1.925.316.642,1.159.613.268,1.178-1.542,3.381-2.605,2.031-3.276-.584-1.82-.929-1.494,1.648-3.822.881-.249-.345-2.05.393-.345.958.153v-1.3l.824-.833,1.9-.833.316-.556.029-1.025-1.073-.479-.144-.441.862-1.466-.029-1.533.862-3.247.6-.45-.048-1.763,1.686-.546.2-1.236.738-.517.642.853,1.217.057,1.83,1.178,1.015.22.92,1.858,1.207.68.268.556,1.858.22,1.513,1.3h.939l.728,1.044,1.571-.278,1.054,1.81,1.217-.239,1.494.45.22.575,1.609-.92.393.517.546-.086.5-.556,1.533-.671.01-1.035.517.038.709.651,1.169.316,2.433-.23,1.868,1.81,1.648,1.14.939.2,1.667-.872,1.638.192.421-.23.239-.738-.23-1.284,1.81-2.95,1.446-.517,1.1-1.456,2.395-.374.067-.958-1.475-.115-.46-.632.3-.709.7-.307,1.322.853,1.37.23.651.958,1.37.268.843-.776,1.446.546,3.075-1.006.757.115-.421.69.268.163.489-.651,1.1.824,1.054-.23h0l.69.6,2.854.757,1.169,1.13.785,1.159,2.2,2.04,1.322,3.8-.421,2.807.163,1.331,1.609,3.784-.393,2.184.172,1.906,1.149,4.282-1.149,2.979-1.015.776-1.341,2.06-.086.824.642.785,1.284.249.766,1.178-2.165.431-1.3,1.3.6,3.017,1.7,2.031.575,1.667,2.328,1.849.517,2.989.594.824,1.245,1.15,3.276,1.523,2.663.153,2.012,1.293.661-.2.268-.5-.479-1.648.489-2.05.556-.967,2.922-1.945h0l.335.5.038,1.581.718.326.345,1.054,1.466.68.766.9-.776,4.694,1.159.431.977,3.832-.929.5-.019.393.479.354-.115.651-5.814.556-1.609-.22-.728.872-2.548-.833-.958-.671-1.073-.1-2.155.766-2.136-.671-3.736-.259-1.638,1.312-.45,1.284-2.3,1.743-1.983,2.711-2.165,2.05-2.3.69-.22,1.264.747.489.354,1.581-.3,1.609.354,1.81-.431,1.149.853,4.09-2.682,2.318-2.222,2.768-.086,1.8-1.236,5.527-.584.354-1.312,2.615-.077,1.226.814,2.96-.153,5.316-.24.6-3.075,1.36-.968,1.446-1.111,2.922h0l-2.816,1.245-.383,1.322-.46.029-1.743-1.063-.91-1.983-2.385-2.289-1.638-.412-1.331-1.686-1.293,2.452-.776.814-.661-.029-2.107-4.387,1.619-1.686,1.628-.833.575-.853-.115-1.188-1.1.029-.5-.354-.556-2.356-2-.153-1.274-.575L399.4,314.65l-.517-.029-.872,1.255-1.609.335-2.107-2.529h0l-2.031-2.088-.575-.057-.814.575-1.954-.144-.584-1.226-.125-2.165-1.169-1.284-.709-1.667.259-.891-1.006-2.8-.422-2.692-1.648-1.581-.862-1.9-1.035-1.159-.6-2.826-.68-1.015-.757-2.481.249-.862-.987-.891-.086-2.107-1.667-2.174-.134-1.025-1.035-.249-.853-1.322-1.389-.92-3.87-1.121-.642,1-2.673,2.031-1.274.182-.661.527-.671-.469-.8.172-.565-1.877-.939-1.351-.776-3.947-.192-.239-.546.4-.546-.192-1.159-4.282-1.264-.3-.853-2.5-2.2-.211-.508.422-1.149-.92.316-1.379-1.149-2.28-.718-.623-.46-1.743-.967-.584.489-1.878.086-4.9,1.274-3.075-.077-.651-.45.077-.575-.69,1.657-2.127,1.14-.738-.144-.7-.7.153Z"
                transform="translate(-26.629 189.036)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-GUA"
                d="M534.349,358.852l.479.469-.105,1.973-1.1,1.83,2.376.584-.038,1.121-.68,1.379-1.389.642-.536.987L532.3,368l-1.657-1.676-1.456.536-2.155,3.2.1,1.389-.642,1.533-.785-.46-.211,1.054-.632.383-2.606-.364-1.437.68-4.387.268-.69,1.351-.623,3.64.134.728.92.68.623,1.667-.01,2.6,1.044,1.782,1.935,1.351.69,1.6h0l-.412,1.044.575,1.13.1,1.264-.489,1.676.642.048-.077.948-1.13,1.1-1.973.268-.7.757-.46,1.341-.594.172.316-1.437-.479-.23-1.858.46-1.322.977-3.3-.163-2.471-.872-1.245-.134-.584.278-.249-.1.2-.45-.441-2.241-2.117.575-1.332-.421-.632.556.316.785-1.169.5-1.724-.709-2.433-.23-.45-.479-.019-3.1-.431-1.657,1,.5.6-.259-1.284-1.724-1.044-.718-1.37.23-.6-.374-2.012.661-1.925,2.682L487,392.3l-2.117.134-.46-.412-1.13.671-.776-.086-.421-.431-.651-2.73.345-.383-.546-.469-.527.067-.316-.517-.824-.048h0l.364-2.653,1.715-3.286-1.5-2.749L478.571,379l-.527-1.255,1.925-1.877.115-.8,1.734-1.437,1.83-3.784,2.385-1.83,1.073-2.6L487,364.8l.5-.709,1.408-.086.412-.354.642-1.312-.776-.45.556-2.1-.671-.115,1.456-5.3-.115-.671,1.149-1.3-.709-.8,1.169-1.236h0l.929.6.776-.9h0l1.111.642,2.787.3,1.207.7,4.435,1.015,1.178.718,1.025-.038,4.761,3.563,2.625,1.37,2.634-.029,1.456-.584-.144-1.073.594-1.753,1.034-.7,3.726,1.226,3.017,1.916.259.661.46-.086,1.628,2.04,1.734-.163.374-.517.651.8,1.542.038Z"
                transform="translate(-32.045 183.303)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.25"
              ></path>
              <path
                id="MX-GRO"
                d="M519,424.358l.287,2.816-1.006,1.839-.709,4.052.354,1.533,1.676.728.1,1.14-.239.69-1.59,1.734.622.632,1.274-.259,1.446.316.211,1.035,1.5,1.877.038-.412.594.029.326-.374.182.354,1.9-2.088,2.366-.556-.661-1.322.335-.575.977-.469.757.412,3.362-.22.278-.556.843-.4.814.278.421-1.494,1.686-.307-.345-1.647.776-.048.872,1.035,1.9.642.785-.469.776.125h0l.489.8-.623,1.073.422.335,1.035-.22,1.073.441,1.935,2.558,5.278.671,2.2,2.232h0l-.048,1.178,1.293,1.13,1.284.594,1.169,2.155,2.864-.192.211.766-.8.929,4.033,1.705.115,1.025,1.341-.287.45.259.824-.824,1.485-.134,1.542,1.9,1.542.766h0l-1.667,3.784-.163,1.351,2.615,5.8.575,4.387-.881.8-.172,1.973.536.5,2.2.747.431,1.121-.105,1.83.259.565,1.111.239.4.517.345,1.494-.881,2.759-.872.881-1.169.22-.891.642-.172,1.293-3.937,3.439-.441,1.935-4.445,3.391h0l-3.41-3.458-1.849-2.4-1.619.153-.316.536-1.226-.182-10.173-3.065-2.979-.623-3.832-.038-1.561-.508-1.992-1.044-1.734-1.418-.948-.249.594-.613-.22-.422-.939.038.182.412-.546.239-1.063-1.657-.843-.422-25.471-9.081.125-.556-.489-.613L501.01,466.3l-.728-.939-5.977-2.519.22-.8-.872-1.035-.776-.546-2.088-.268-.1-.517-1.006-.364-.728-1.81-1.782-1.274-1.312-2.251-3.65-2.031-2.107-.651-1.312.172-.709.786-.508-.307h0l-.786-1.207.105-3.765.728-.6,2.529-.785.824.239,1.4-.613.728-.738,1.552.354.785-.268.172-.307-1.763.029.316-.862-.105-1.178-.623-1.351.556-1.054-.6-1.178.125-.326.613-.029-.45-.757.651-.374.594.134-.2-.441.536-.105.508.785.46-.738-.316-.393,1.015-.316.057,1.006.575-.364.278.364.144-.642.642-.23,1.082.029,1.159,1.82,1.916.872,3.314-.488.2-.441,1.945-.441,1.648.038.45.469,1.945.45,1.5-.182,1.082.268,1.13,1.59.077.786.613.124,1.36-1.054.594.546,1.379.22.383.757,1.427.814.326-.4.383.287.259,1.015.776-.029.738-.967-2.711-3.63-.287-3.008-1.169-4.061.45-.987,1.054-.814.4.038.546,1.5,2.337-2.8.891-.508,1.044-2.347L519,424.358Zm4.445,17.961.067.307.364-.326Z"
                transform="translate(-31.992 180.176)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-HID"
                d="M566.606,362.007l.8.163.287.718-.287,2.548,1.753,1.638,1.207.632,1.255.067.824-.862,1.944.594,2.424,3.276-1.782,2.356-.6,2.117.536,1.015-.249.6-1.389.22-1.284-1.331h-.441l-2.289,2.069-.307.824-.623.517-.316,3.506-.556,1.245-.24,1.992L568.4,388.4l.843,1.035.479-.211,1.274-1.485,2.376-1.111,1.8-2.136,1.533-.7,2.05-2.232.8-.335,1.4.91h0l.067,2.874-2.74,2.414-.872,1.322-3.468,1.772.671,1.351,2.031-.46.441,2.1-.776,4.6-1.475,2.95.671,4.531h0l-2.28-.479-.785,1.408-1.389,1.159-2,.268-.958-.22-4.8,1.638h0l-.192-1.351,1.763-3.975-.163-1.667-5.565.584-1.648,2.471-.23.2-.623-.307.019-.862-.833-1.7.192-.5.872-.508.814-.077,1.063-4.1-.45-1.284-.575-.067-2.615,1.331-3.717,1.025-.508,3.592-.661.728-2.04-.967-.163,2.146-1.159.948.1-2.385-.383-1.284-.661-.623-.326-1.2-1.169-1.207.671-.948-1.447-3-.613-.24-.125-.556-4.454-.661-2.519-1.427-1.13-1.331h0l.967-6.265,1.513-.469.872-.651,2.807-.69,2.222-1.542-.6-1.935,1.1-2.452,1.111-.4.776-1.15.45-2.117.8-1.494-.259-1.063,1.581-1.37,1.159.68,3.937-1.236,1.226-3h0l2.165-.718,1.092,2.3L559,367.649l1.034-.038,1.293-2.28.881,1.255,1.274-2.835L565,361.988Z"
                transform="translate(-34.434 182.801)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-JAL"
                d="M458.855,353.415l1.964-1.073,4.033.364,1.667,1.351,2.079.977,1.207-.057,2.893-1,.69-.479,2.443-3.4,4.1-1.887-1.878-2.05.23-1.533-.249-.565h0l3.391.872,2.04,1.092,2.558,2.251,2,.718.268.374h0l-1.169,1.236.709.8-1.15,1.3.115.671-1.456,5.3.671.115-.556,2.1.776.45-.642,1.312-.412.354-1.408.086-.5.709.105.623-1.073,2.6-2.385,1.83-1.83,3.784-1.734,1.437-.115.8-1.925,1.877.527,1.255,1.581.412,1.5,2.749-1.715,3.286-.364,2.653h0l-2.682.01-2.05,1-1.638-.163-1.274.441-1.609-.125-1.025.326-1.973,1.571-1.015-.077-.967,1.006-.9.316-.268,1.37-2.845.7-.22.776-.422.038-1.006-1.245-2.366-.431-.287,1.1-.891-.192-.345.259-.019,2.251-.852,1.446.479.8.479.048.431-.536,2.174.268,3.075.9.632.613.584,2.117-.316.776-.891.709,1.83,4.023-.6,2.481,3.219-.067.584.651.057,1.494-2.261,2.395-.814,2.213-2.213-.556-1.312.69-3.851,3.429-2.107,1.159-1.523,1.667-.278.086-1.207-1.686-.508-.1-2.127,1.628-1.485-.287-1.59.393h0l-.048-1.878-.786-1.475.536-1.264-.067-2.989.479-1.724-1.025-.46-.2-.747-.929-.8.153-.977-.46-2.768-.584-.125-2.4,2.309-3.084,1.609-1.351.2-3.113-.307-2.969,2.222-1.082.278-1.217-.364-3.611.843-.527-.383-.833.556-.46.8-.536.105-1.092-.46-1.418,1.715-1.839-.048h0l-.536-.508-.6.086-.077-.489-.632.249-.469-.287-.814.125.01-.613.488-.527-.124-.441-.853-.278-.22.412-.891.182-1.916-1.025-1.81-1.648.345-.69-.354-.7-.8-.45.086-.814-.431-.211-.211-1.427-.728-.709-.8-.029.038-.345-2.711-2.021-.249-.7-1.37-1.447-.383-1.063L393.4,396.2l-1.073-2.222.2-2.117-.326-1.169-.651-.23-.125-.575-2.146-2.241-.441-1.581,1.006-.489.91-1.082,7.1-1.111,1.437-1.054.632-1.542-.3-.929-1.226-1.015h0l4.263-5.383,1.121-.431,2.232.45,3.056-2.337,1.2-.2,1.437.7,1.159,1.37,2.95.747,2.548,2.883,1.035.536,2.2,2.031.642.747.479,1.485.488.306.029-1.4-1.13-1.4.278-.335-.7-.718-.354-1.466.431-.613.785-.172,1.437-8.152,3.87-.22.91-.383.929-.929,1.638-.268,2.155-1.226.383-.786.383-4.042-.23-1.418-1.331-1.188-1.667-4.109-4.311-.575L425.9,350.8l-.105-2.605-.508-.46-1.063-.086-1.149-1.111-1.58.5-.441-2.893,1.188-4.684-.172-.46h0l1.035-1.159,1.293.326.345-2.9,2.97-1.121.508.354-1.006,3.1.259.469,1.724-1.092.987-1.13-.153-1.37-1.667-3.678.105-.757,2.069-1.466,2.548-.364,1.734.967-.163.872-1.054.785-1.389,1.791-1.619,4.042-.038,3.18,1.159,1.705,1.111.163.364-1.111v-6.744l.6-.546,3.286-.642,1.36.489,1.341,1.264.575,1.849-.038,1.111L436.7,343.3l-.086.431.556.632,1.849.019.661-.766,1.217.192.68.5.862-.421.891-1.657,2.289-1.81-1.188-.987.3-1.791,1.8-.7,2.079.163.393.824-.728,1.446.479.422,2.06.134.489.364-.671,3.324-1.14,2.788-.709.661-1.207.192-3.142,1.609-1.7,1.657-2.8,1.983.172,1.715-.316,2.366-1.552,1.8-1.715.441.814,2.376.891.517.977-.469.642.134.326.556-.1.977-1.121,1.073-3.87,1.734.163.479,2.395.594.718.776.718,1.628.9.469,2.165-.182,2.778,1.542.948-.46.843.067.211-.536,1.284-.249,1.638.412.105-.354,1.408-.5.316-1.935.6-.517,1.312-.469,1.456.479,3.429-2.787,2.194-1.341.92-1.255.46-2.577-2.663-1.705-.623-1.772Z"
                transform="translate(-28.289 184.223)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.25"
              ></path>
              <path
                id="Path_266"
                data-name="Path 266"
                d="M525.925,443.088l-.364.326-.067-.306Z"
                transform="translate(-34.042 179.387)"
                fill="#0b2343"
              ></path>
              <path
                d="M534.153,391.408l1.121,1.341,2.529,1.427,4.445.661.125.556.613.249,1.447,2.989-.671.958,1.169,1.2.326,1.2.661.623.383,1.284-.1,2.385,1.159-.958.163-2.146,2.04.967.661-.738.508-3.592,3.717-1.035,2.615-1.322.565.057.45,1.284-1.063,4.1-.814.077-.872.5-.2.5.833,1.705-.01.853.613.307.23-.192,1.648-2.471,5.575-.584.163,1.667-1.763,3.975.182,1.36h0l.929,1.743.728-.23.5.575h0l.374,1.743L564.6,415l-.038,1.044,1,3.477.115,4.215h0l-.852.172-2.5,1.973-2.443-.9-.891-1.523-.939-.8h0l-.182-.8.45-1.6-.335-1.513-.22-.479-.45.364-.3-1.418-2.05-1.724-.46-1.475-1.5-1.839.048-1.015-.316-.345-2.146,2.356-.536,1.9-3.037,2.634.1.939,2.51,4.2h0l-3.784,6.035-.431,1.37-1.494,1.763-.23.958h0l-.776-.125-.786.469-1.9-.642-.872-1.034-.776.048.345,1.648-1.686.307-.422,1.494-.814-.278-.843.4-.278.556-3.362.22-.757-.412-.977.469-.335.575.661,1.322-2.366.556-1.9,2.088-.182-.354-.326.374-.594-.029-.038.412-1.5-1.878-.211-1.034-1.446-.316-1.274.259-.623-.632,1.59-1.734.24-.69-.1-1.14-1.676-.728-.354-1.533.709-4.052,1.006-1.839-.287-2.816h0l.536-.364.22-1.082,1.437-1.753,1.054-2.289-.268-.939.728-1.705.412-.575.642-.115.287-.594-.421-4.962-.546-.45-.92-1.868,1.446.1,1.379-.479.278-.489.268-1.973,1.274-1.628-.038-.584-.833-.785.077-2.328h0l2.443-.3,1.839-1.925-.278-1.657-.517-.92.23-.3.843.575.623-.125-.2-.69.287-.6,1.1-.69Z"
                transform="translate(-33.783 181.563)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-MIC"
                d="M477.872,389.768l.824.048.316.517.527-.067.546.469-.345.383.651,2.73.422.431.776.086,1.13-.671.46.412,2.117-.134.872.287,1.925-2.682,2.012-.661.6.374,1.37-.23,1.044.718,1.284,1.724-.6.259-1-.5.431,1.657.019,3.1.45.479,2.433.23,1.724.709,1.169-.5-.316-.785.632-.556,1.332.421,2.117-.575.441,2.241-.2.45.249.1.584-.278,1.245.134,2.471.872,3.3.163,1.322-.977,1.858-.46.479.23-.316,1.437.594-.172.46-1.341.7-.757,1.973-.268,1.13-1.1.077-.948-.642-.048.489-1.676-.1-1.264-.575-1.13.412-1.044h0l1.485.948,1.169,1.839-.172.718,3.151,2.912h0l-.077,2.328.833.776.029.594-1.274,1.628-.268,1.964-.278.5-1.37.469-1.456-.1.92,1.877.546.45.412,4.962-.287.594-.632.105-.422.575-.728,1.715.268.939-1.054,2.289-1.437,1.753-.22,1.082-.536.374h0l-2.155,1.322-1.044,2.356-.891.508-2.337,2.8-.546-1.5-.4-.038-1.054.814-.45.987,1.169,4.052.287,3.017,2.711,3.63-.738.967-.776.019-.259-1.015-.383-.287-.326.393-1.418-.8-.383-.757-1.379-.22-.594-.546-1.351,1.054-.623-.115-.077-.8-1.13-1.59-1.082-.259-1.5.182-1.945-.45-.45-.479-1.648-.038-1.944.441-.2.441-3.314.479-1.906-.872-1.159-1.82-1.082-.029-.632.239-.144.642-.278-.364-.575.364-.057-1.015-1.015.326.307.383-.46.738-.508-.786-.536.105.2.441-.594-.134-.651.374.45.757-.613.029-.125.326.6,1.178-.556,1.054.623,1.341.105,1.178-.316.862,1.763-.019-.172.3-.786.268-1.552-.354-.728.747-1.408.613-.824-.239-2.519.785-.728.6-.105,3.765.785,1.217h0l.182.728-1.226.508-2.835-1.4-3.879-1.159-7.577-1.657-4.914-2.289-3.621-.671-4.742-1.782-.5.22-3.65-2.222-1.7-3.937-2.749-2.136.345-.8-1.494-1.81h0l.651-1.628,1.782-1.13-.029-1.715.364-.268.977.3,2.366-2.146h0l1.59-.393,1.485.287,2.126-1.628.508.1,1.207,1.686.278-.086,1.523-1.667L454.5,422.6l3.851-3.429,1.312-.69,2.213.556.814-2.213,2.261-2.395-.057-1.494-.584-.651-3.219.067.6-2.481-1.83-4.023.891-.709.316-.776-.584-2.117-.632-.613-3.075-.9-2.174-.268-.431.536-.479-.048-.479-.8.853-1.446.019-2.251.345-.259.891.192.287-1.1,2.366.431,1.006,1.245.421-.038.22-.776,2.845-.7.268-1.37.9-.316.967-1.006,1.015.077,1.973-1.571,1.025-.326,1.609.125,1.274-.441,1.638.163,2.05-1Z"
                transform="translate(-30.34 181.632)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.25"
              ></path>
              <path
                id="MX-MOR"
                d="M550.7,424.256l1.274-.23,1.274,1.121,1.245-1.044.6-.115.872.766,2.251-.718.91.019h0l.939.8.891,1.523,2.443.9,2.51-1.973.852-.172h0l-2.644,6.782,1.217,7.51-.527.69-2.644-2.529-.489.115-.766,1.207-1.111.613-.584.948-2.653,1.82h0l-2.2-2.241-5.278-.661-1.935-2.567-1.082-.431-1.025.22-.421-.345.623-1.073-.479-.8h0l.23-.967,1.494-1.763.421-1.37Z"
                transform="translate(-34.847 180.191)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <g
                id="Group_275"
                data-name="Group 275"
                transform="translate(358.752 514.559)"
              >
                <path
                  id="Path_268"
                  data-name="Path 268"
                  d="M388.988,330.858l.556,1.379-.163,1.293.527.46-.2.642.9,6.083-.575.029-.172-.383-.718-3.985-1.878-4.3.268-.239,1.054,1.7.728-.239-.709-2.079.383-.354Z"
                  transform="translate(-386.975 -330.447)"
                  fill="#c4c4c4"
                ></path>
                <path
                  id="Path_269"
                  data-name="Path 269"
                  d="M389.868,340.973l-.239-.532-.737-4.041-1.93-4.406.61-.545,1.1,1.778.319-.105-.68-2,.753-.694.708,1.759-.155,1.228.549.479-.231.738.93,6.289Zm-2.06-8.236,1.539,3.526.735,4.038.084.178.151-.008-.87-5.877.17-.544-.5-.442,0-.023-.648.213Zm1.06-1.472.395,1.158.022-.171-.4-1Z"
                  transform="translate(-386.962 -330.429)"
                  fill="#c4c4c4"
                ></path>
              </g>
              <path
                id="Path_270"
                data-name="Path 270"
                d="M395.964,318.068l2.107,2.529,1.609-.335.872-1.255.517.029,1.456,1.676,1.274.575,2,.153.556,2.366.5.354,1.1-.029.115,1.188-.565.853-1.628.833-1.619,1.686,2.107,4.387.661.029.776-.814,1.293-2.452,1.332,1.686,1.628.412,2.385,2.289.92,1.983,1.734,1.073.46-.038.383-1.322,2.816-1.245h0l.2,2.653.4.92.7.412h0l.172.46-1.2,4.684.441,2.893,1.59-.5,1.149,1.1,1.063.086.508.46.105,2.605.814,1.552,4.311.575,1.667,4.109,1.331,1.188.23,1.408-.383,4.042-.383.8-2.155,1.226-1.648.268-.929.929-.92.383-3.87.22-1.437,8.152-.785.172-.431.613.364,1.466.7.718-.278.335,1.13,1.4-.038,1.4-.489-.307-.479-1.485-.651-.747-2.2-2.031-1.035-.527-2.548-2.883-2.95-.757-1.159-1.37-1.437-.7-1.2.211-3.056,2.328-2.232-.441-1.121.431-4.263,5.383h0l-1.169-1.676-.45-.077-.843.7-2.874-.948.738-.6.881-2.059.345.086.833-1.5,1.159-.766.843-1.121.144-.8.977.335,1.092-1.552-.268-.939.479-3.017-.623-2.711,1.332-2.577-.2-1.2-.709-.8-.891.3-4.464-3.037-1.37-3.42-2.845-4.847-.7-2.548.172-3.075.766-.364.441,1.14.575.1.3.766,1.9.929.287-1.456-.977-.68-.239.239-.335-.718-.977.326-.182-.546.364-.335-.067-.584-.431-.057-.316.4-.316-.287.125-.671-.5-.929-.2-3.247-.287-.546.182-.814-.556-.374.239-.651-.6-2.021.316-.163h0l2.012,1.188,3.314.546.345-.853-.22-1.954-.383-.345-.2-1.274-1.255-1.245-.431-1.245.316-.977,1.619-2.012.536-3.266.862-.594Z"
                transform="translate(-28.301 184.651)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-NEL"
                d="M536.6,190.556l-.623.326-1.226,1.657-2.663.766-.594.661.259.757-.335,1.025.575.431,3.832.182.048.843-1.121,1.36,1.217.833-.211,2.356.23,1.322,1.1,1.082.4,3.056-1.571,1.331-.776,2.289.125,1.073,1.408-.048,1.2.632.5.747-.45,1.236-.22,3.084.766.785.843-.354.642.383.8,1.82,1.8.211,1.188,3.966,1.743,2.385,1.351-.125-.057.958,1.121,1.552.661.086.718-.7.6.048.173.575-.345.7.728,1.284-.852.316.785.441.354.623,3.142.441.622.565,1.14,2.242.91.517,2.548-1.466,1.025.144,3.238-.661.661.431.134,1.408.757.967-.287,3.209.565,1.456-.45.738-.191,1.983.661,1.5-.393.766.948.8,1.906.6.383.249.029.5-1.475,1.513-.565,1.312-1.2.594-1.3,1.379-.747.077-.948,1.082-4.2.814-.948,1.092-.718,1.945-2.682-.211-.584-1.121-.488-.038-1.4,1.887-1.207.393-.24.383.575,2.146.642.345-.364,1.6-.862.086-.881.661-.45,1.533-1.073.431-.364-1.456-.364.125-.345,1-1.1.45-1.073,1.533-1.686-.1-1.245,2.04-2.749,1.216-1.906,1.293-.661,2.012.211.268,1.753-.086,1.207,2.51-.824,6.485,1.7,2,1.341,3.487,1.312.738.728,2.241-2.155-.421-.345.1-.326,1.446-1.686.929-.68.144-2.443-.651-2.213,1.178-.632,2.692-1.418,1.7-.316.92.374,2.72-.2,1.7-2.96-.364-.766.651.527,1.293-.412.738h0l-3.736-.709-2.241,2.682-1.437-.69-1.188-1.408-.536-1.581-.239-3.918-.489-1.217,1.1-5.364-.105-.853-.929-1.4.2-4.186-.843-2.155-2.376-3,.125-1.37-1.715-1.638-.278-.776,1.15-1.5.048-.316-.661-.249.46-1.5-1.331-.479-1.006.182-1.648-2.222h0l.527-.4h0l.958-3.123-.623-1.178-.105-1.332L509,266.134l.307-1.149-.278-.8.153-.354,2.309-.057.192-.374-.086-2.069-.853-2.807,1.629-3.084,1.715-.546-.278-.776.9-.3,1.667.163,1.255,1.466,3.266.843,1.858.029.326-.192.144-1.1.718-.852-.374-.91-.843-.307-.479-.766-3.448-.6.489-1.006-3.142-1.8-.786-.814-.536-3.266-.335-.345-2.04-.077-.8-.632-1.063-.134-.8-1.705-.23-2.366.948-2.778-.364-.613-.824-.077-1.36-1.1-1.006-2.979-2.586-1.935L500,225.385l-.019-1.245,2.318-2.318,1-2.615.833-.862,3.362-.268,2.5-1.628,3.065.038,1.092-2.1,1.006-.738.8-2.146-.4-4.914-1.964-1.418-.6-.067-.671,2.989-.565.048-2.7-1.983-.239-3.2,1.753-2.855,1.14-1,1.14.364,2.012-.881,2.136.115.6-.469-.153-2.194.278-.757,1.619-2.491.527-2.241,1.082-.6,3.516-.249,5.288.881,1.839-1.245.91-.057,1.044-.757.249-.584h0l.718-.163.23.891.738.24Z"
                transform="translate(-32.968 190.136)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-OAX"
                d="M613.234,440.7l1.121-2.088.287-.92-.469-.709.048-.824.623-.584,1.1,1.111.958.335-.555,1.217.584,2.625.613.862,1.877,1.485,1.245-.364,1.331.776.977,1.81,1.322.086-.517.862.671.335,2.366-.747,1.37.5,1.1-.182,1.945.46.5.421.958,3.63-.211,1.2-.776,1.073-.948,2.462-.69.651.326.69-.163,1.13,2.079,4.234,1.878.556,1.207-.623,2.749-.163,2.883-2.012,3.506-.881,1.159-1.044.4.067,1.312.8-.172.651-1.427.632-.22,1.082,3.219,4.224,3.3,2.673-.009.948.613.546-.316,1.312,27.367,1.676h0l-.326.9.029,1.916-.728.977-.766,3.343-2.816,2.51-2.1,6.025-.077.814,1.188,2.807-.5,1.4.115,2.021h0l.039.421-1.9.853-.469-.086-.5-.412-.69.067-.757-.833-.833.019-.853-1.446-1.073.374.019.948-.632.048-.019.6,1.073.92,4.186.307,2.941,2.194h0l-.172.613h0L669.635,498l-2.835-.872-7.366-1.073-.23-.431,2.529-.862.7-.623.383-1.082-2.28-.8-1.236.862-.374.738-.919.048-.259.584-.488-.144-.1-.374,1.054-.9-1.054-.891.23-.584-1.331-1.006-1.6.422-1.245,2.136-1.2-.029-.881.785.2.824-.584.057-.421.766,6.15-.01,1.763.23.115.278-3.487.057-3.822.642-1.772-.153-.153.584-1.935.163-.46.441h-.6l-.153.958-1.935.853-.326.5.2.747-1.312.441-.172.517-1.485-.172-.105.556h-1.887l-.105.45-1.944.853-2.337.326-1.226.814-3.688.968-2.615,1.207.01.249-1.4.383.172.316L623,509.116l-4.263-.259-1.791.929-1.5-.029-3.391-1.427-3.841-.575-1.485-.91-2.328-.728-.738-.517-.259-.738-3.6-1.628-3.123-.584-1.935.125-2.529-.7-.709.172-2.462-.383-.422.374-2.328-.354-5.441-3.583-5.211-2.778-4.473-.8L567.5,493.4h0l4.445-3.4.441-1.925,3.937-3.439.182-1.293.891-.642,1.169-.22.862-.872.881-2.759-.345-1.485-.4-.517-1.111-.249-.259-.565.105-1.83-.431-1.121-2.194-.747-.536-.489.172-1.983.881-.8-.584-4.387-2.615-5.8.163-1.36,1.667-3.784h0l1.149-1.341,1.983-1.293,1.5-.345,3.314-.01,1.389-.565,2.1,1.542,1.13.345.69-.2,1.7-1.571-.067-.412-2.251-1.571-.421-.728,1.945-4.148,1.494-.651L592,442.59l.46.326.22.929-.1.441-1.274.862.1.383,1.255.92.709,1.035,1.379,2.7.345,1.571.785.671.833-.814.872-1.82,2.491-2.471,1.3-.278,3.563.853,1.705-1.418.105-.853.278.536,1.3-.862,2.289-.488,1.839-3.3Z"
                transform="translate(-35.811 179.703)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-PUE"
                d="M581.882,382.761l.671-2.452.919-.718.086-.8.843-.9.259-.977,1.13.374,1.945-.307-.01,1.715-1.178.718.153.287.833-.182.776.575-.354.8.153.8.776.8,2.136,1.169.46,1.073-1.437,2.366-1.542-.881-1.121.728.057,3.841.441,2.27.651.527,1.954-.211.824,1.705.565.192,1.542-1.341,1.169-1.868.785-.632,6.763,2.376.077.488-2.069,2.864-1.581,4.1-.192.776.307.939-.326.929-.642.833-.929,2.519-.546.23-.239.671,1.322,2.864-.527,1.322.326.4,1.37.948,3.477.786,2.807,1.217.45.565.105,1.226-.153.671-.508.115-4.617-.211-.182.862.5,5.862-.067.527-1.782,2.309V430.5l.345,1.063.814.7,3.65,1.063-.163.718,1.782,3,1.37.1.91-1.006,1.331-.766,2.471.8,1.772,2.069h0l-.776.8-1.839,3.3-2.289.489-1.3.862-.278-.536-.105.853-1.705,1.418-3.563-.853-1.3.278-2.49,2.471-.872,1.82-.833.814-.786-.671-.345-1.571-1.379-2.7-.709-1.035-1.255-.92-.1-.383,1.274-.862.1-.441-.22-.929-.46-.326-1.523.192-1.494.651-1.945,4.148.421.728,2.251,1.571.067.412-1.7,1.571-.69.2-1.13-.345-2.1-1.542-1.389.565-3.314.01-1.5.345-1.983,1.293-1.149,1.341h0l-1.542-.766-1.533-1.9-1.485.134-.824.824-.45-.259-1.341.3-.125-1.034-4.023-1.705.8-.929-.211-.766-2.855.192L559.62,443.2l-1.293-.594-1.293-1.13.048-1.169h0l2.653-1.82.584-.948,1.111-.613.766-1.207.488-.115,2.644,2.529.527-.69-1.217-7.51,2.644-6.782h0l-.115-4.215-1-3.477.038-1.044.527-.584-.364-1.753h0l.671-.278,1.111.354,2.9,2.213,1.264,1.322.517,1.322,1.13,1.255,3.889,2.146.824-.134,2.941-2.567.546-.038,1.4.948.738-.048,1.188-.8,1.341-2.232.8.307,2.567-.489.785-.527.268-1.1-.9-.192-.642-.575-.3-1.121-1.159-1.188-.565-.364-1.466.479-.8-.163.125-1.657-3.927-3.266-.68-1.015-.469-.067-1.485,1.753-.584.23-1.973-.364h0l-.671-4.531,1.475-2.95.776-4.6-.441-2.1-2.031.46-.671-1.351,3.468-1.772.872-1.322,2.74-2.414Z"
                transform="translate(-35.37 182.173)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-QUE"
                d="M553.7,364.82l-1.226,3-3.937,1.236-1.159-.68-1.581,1.37.259,1.063-.8,1.494-.45,2.117-.776,1.15-1.111.4-1.1,2.452.6,1.935L540.2,381.9l-2.807.69-.872.651-1.513.469-.967,6.265h0l-1.054.757-.287.6.2.69-.632.115-.843-.575-.23.3.517.92.278,1.657-1.839,1.925-2.443.3h0l-3.151-2.912.172-.718-1.168-1.839-1.485-.948h0l-.69-1.6-1.935-1.351-1.044-1.782.01-2.6-.623-1.667-.92-.68-.134-.728.623-3.64.69-1.351,4.387-.268,1.437-.68,2.606.364.632-.383.211-1.054.786.46.642-1.533-.1-1.389,2.155-3.2,1.456-.536,1.657,1.676,1.054-.163.536-.987,1.389-.642.68-1.379.038-1.121-2.376-.584,1.1-1.83.105-1.973-.479-.469h0l-.565-.546,1.475-.364-.268-.431.172-.46,1.054.038,1.447.958.565,1.81.6.632,2.385.239,1.858-.479.556.345,1.015-2.653.536-.354.766.23.46-.268.948,2.107,3.084,3.209.786,1.4Z"
                transform="translate(-33.674 182.996)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.25"
              ></path>
              <path
                id="MX-ROO"
                d="M860.732,382.707l-1.035,2.471L858.6,386.6l-.335.048-2.452,3.209-1.5.929-.642-1.638.115-1.83,2.194-3.592.671-.345.69.728.326-.316.441.268,1.485-.383,1.14-.968ZM802.75,449.635l-.24-2.356,2.337-2.711.546-1.207-.268-4.5-.45-1.322.546-1.6-.785-.441.488-1.312-.144-.68-.948-1.188-.163-1.217.766-4.914-.211-2.414.393-.642,1.121-.632.172-5.6-3.266-4.473-5.077-4.071-1.284-1.734h0l.891-1.916.316-1.4-.163-.833.536-.469,3.391-.163,1.331-1.332,2.328-1.006,2.328-.038,1.13-.393,1.839-1.705,1.035.881.287-.307-.259-.661,1.791-1.533,1.705-1,5.278-1.9.441-.45.757-2.27.488-.4.958.01.891,1.609.345-.048-.24-2.1,1.437-2.644,1.121-.536,1.092.958,1.475-.422,1.446.1,2.979-1.753,3-2.586,1.647-.785.824-1.121.259-1.513.824-1.035.46-2.356,1.1-1.964.1-.929-1.274-3.075.45-3.027.843-.824.125-.728-.316-5.086h0l1.274.086-.038.393.546.268.642-.307,2,.565.316.374.623-.24,1.561.182.421.383.278-.508,2.6-.833.22-1.878-.6-.259.613-.747.441-.153.421.479,1.379.374.536.8.326-.153.517.431.048.757,1.513,2.088.929,3.879.412-.1.211-1.542.709-.642-.067-.862-.623-1.025.623.211.105,1.648L858.6,365.1l.383,1.111-.153,1.312.182.766,1.15.814-.661,2.385-.718.048-.3.68-1.217,3.88-1.13,1.092-1.063,1.523.038.469-1.1.651-.316.776-1.839,2.069-3.717,2.912-1.657,2.318-.977,2.366-1.4,1.657-1.351,3.812-.01,1.81.613,1.849.4.134.153,1.1-.556,1.312-.814.8.393-.891.421.01.536-1.274-.058-.565-.594-.364.067,1.236-1.006,1.715-.738.268-.326-.364-.881.527-1.552,2.605-2.328.4-.182,1.82.958.661.144.565.671-.326-.1.747.345.134-.239.68.556-.077.852-1.207,1.513-.326.575.623.785-.747-.258-.393,1.648-.211.517.766-.709,2.395-1.9,1.83-.45-.029.077-.757-2.356,1.418-.594,1.619v1.2l-.642.757.345.077-.057.709,1.341-.048,1.945-2.672.766-.364.431.163-.326,1.341.335-1.073.929-.077-2.117,2.519-.268,3.075-1.954,3.966-1.073,4.55-.862.92-.785,4.56-.431.862-.038,1.954-1.379,2.242-.536,3.19-.7-.517-.613.057.019-.651.536-.364-.115-.92-.393.1.383-1.418-.5-.287-.144-.594-.776-.23.182-.987-2.51-.757-1-1.178.709-.441.326-1.954.891-1.628-.45-.508.364-1.188-.393-1.37-.7-.594.23-.872-.757-.278-2.165,3.429-1.044.364.01.239.939-.412-3.1,5.671-2.165.124-.2-.259-1.015.4-1.149-.268-.881.785-.843,2.883-1.015,1.082-.431,1.944-1.044,1.322-1.1,2.759-1.36.747-.556,1.36-1.552,1.255.105,1.533-.891-1-.872-.374-.6-.91-1.006.038-.92-1.111-1.724.374-1.216,1.149Zm48.6-92.418-.2.517-1.916,1.15-1.4-.029-.536.364-.872-.5-.46-.728,1.648.536,1.341-.1,2.395-1.217Z"
                transform="translate(-45.442 183.003)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-SLP"
                d="M508.472,277.428l1.648,2.222,1.006-.182,1.331.479-.46,1.5.661.249-.048.316-1.15,1.5.278.776,1.715,1.638-.125,1.37,2.376,3,.843,2.155-.2,4.186.929,1.4.105.853-1.1,5.364.489,1.217.239,3.918.537,1.581,1.188,1.408,1.437.69,2.242-2.682,3.736.709h0l.862,1.015.134,1.973.987.747.46,1.1-.316,3.477.211,2.855.192.661,1.044.881,4.023,1.312,3.132.249,2.107.7.623-.412-.048-.987.929-.536.594.316.776,1.236,1.619.536,1.322,1.035.8,1.121.881,2.423,2.069,2.73,1.7-.22,1.916.374,2.222-.067.68-.766,1.81,1.14,2.05-.431.92.163h0l.278.891.508.249,2.261-.038,4.243,1.312.632.7,1.149,3.41-2.423.048-1.025.69.306,2.443-.249.642-.393.268-2.079-.249-.259.393.364.364h1.082l-.555.508.335.249-.125,1.178.565.69.431-.105.029.46,1.14,1.063-1.628.757-.278.785-1.015-.057.048.671H561.98l-.192.824.852,3.171,1.274.211.134,3.2h0l-1.609-.019-1.513,1.763-1.274,2.835-.881-1.255-1.293,2.28-1.034.038-1.284-1.054-1.092-2.3-2.165.718h0l-.843-.249-.785-1.4L547.19,356.6l-.948-2.107-.46.268-.766-.23-.536.354-1.015,2.653-.556-.345-1.858.479-2.385-.239-.6-.632-.565-1.81-1.446-.958L535,353.993l-.172.46.268.431-1.475.364.565.546h0l-2.529,1.274-1.542-.038-.651-.8-.374.517-1.734.163-1.628-2.04-.46.086-.258-.661-3.018-1.916-3.726-1.226-1.034.7-.594,1.753.144,1.073-1.456.584-2.634.029-2.625-1.37-4.761-3.563-1.025.038-1.178-.718-4.435-1.015-1.207-.7-2.787-.3-1.111-.642h0l1.849-3.8.335-.92-.249-.172.125-.527.374.029.345-1.571-1.322-3.7,3.161-6.993.182-1.581-1.887-2.471-.68.048-1.4.671-4.263,4.416-2.328.7-1.992-.048-1.418-1.935-.881-1.916-1.025-.556-2.213-.326-1.063-1.264-4-2.423-.9-.958-.24-1.446.192-1.207,1.207-3.822-.987-2.893.488-1.619-.594-.776.833-1.6.632-.192,2.222.891,2.816-.556.5-1.379.814-.987,2.184-.8,3.41-2.04,3.334-4.924,4.54-1.523,1.054-1.475,1.13-2.835-.967-1.13.8-3.3.613-.594,1.791-.46,1.3-.843.881-1.7.2-1.427.642-.5,1.514.067.939-.479.661-1.648,1.14-.958.345-1.705Z"
                transform="translate(-31.877 186.361)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-SIN"
                d="M384.8,323.735l.4.057.517.9-.431.7Zm-58.547-62.647,1.121,1.293.479-.182.259.3.01.862.565,1.082-.259,2.586-.46-.01L327.309,265l-1.035-1.121-.019-2.787Zm-4.617-1.964,1.351.891,3.439,4.368.7,1.092.048,1.322-.374.153-2.586-4.924-2.7-2.481.125-.421Zm-.527-2.2,1.705,1.188.2.546-1.59-.766-.316-.967Zm-20.672-29.5.987.115h0l.441.364-.125.967-.364.268,1.705,1.408-.335-1.092.738-.613-.163-.45-.3.268-.441-.239-.881-1.724-.019-.709h0l-.144-1.494.278-.182.23,1.791.374.163.479-.9.757.872.125.747.46-.077-.048.776.326.335,2.807-1.628,1.226-1.121,1.916-.556.105-2.385.45-.833,3.918-.939,1.973-3.7,1.782-.527-.278-2.385,2.989-3.717.3-1.705h0l4.675-.211.651.364.115,2.778.594.23,1.169-.249,1.006,2.232-.383,2.165.479.785-.479,1.925.316,1.044,1.351.872.067,1.8,2.979,4.809,1.169,4.483,3.65,1.082,3.056.3,2.261.671,2.768,4.675.565,3.094,2.05,2.673h0l.057.642.7-.153.153.7-1.15.738-1.657,2.117.575.69.45-.077.067.651-1.274,3.075-.077,4.9-.488,1.887.958.584.46,1.743.718.623,1.15,2.27-.316,1.389,1.149.91.508-.422,2.2.22.862,2.5,1.264.287,1.159,4.282.546.192.546-.4.2.24.776,3.947.939,1.351.565,1.868.8-.163.671.469.661-.527,1.274-.182,2.673-2.04.642-1,3.87,1.13,1.389.92.862,1.312,1.035.249.144,1.025,1.667,2.174.077,2.107.987.9-.239.862.757,2.481.68,1.025.6,2.816,1.035,1.159.872,1.9,1.648,1.581.422,2.692,1.006,2.8-.259.891.709,1.667,1.169,1.274.125,2.165.575,1.226,1.954.134.814-.575.575.067,2.031,2.088h0l-.8.584-.536,3.266-1.619,2.012-.316.977.431,1.245,1.255,1.245.2,1.274.383.345.22,1.954-.345.853-3.314-.546-2.012-1.188h0l.22-.977-1.437-1.561.24-.565.709-.335-.661-.546-.517.671.5,1.82.517.489-.536,1.609.5,1.609-.354.536-3.563-5.565-2.845-2.749-1.705-1.054-4.042-4.952-4.473-4.387.421-.316,1.1,1.217.077-.316-1.389-1.1-.517.2-.182.536-.345-.316.1-.661-.671-.479-.881-1.58.3-.393-1.2-1.877L362.492,302l.077-.335-1.331-1.13-.613-1.054-2.452-1.858.393-.479-2.107-3.257-.22-1.169-7.2-6.667.881.192.3-.345-.431-.287.105-.766-.536-.105-.172.469-.517.2L345,282.5l-1.341-.326-4.828-3.793-4.445-2.73.22-.536.594.661,3.008,1.628.431.709.958.125.489.776.594-.067.718,1.063.479-.125-.517-.565.067-.671.68-.211.038-.383-.278-1.13-1.418-1.935-1.226.029.105,1.331-1.073.23-1.044-1.37-.575.508-1-.182-.172-.891-1.561-1.427-2.012-.833-.239-.441-.556.383-1.035-.479-.757-1.015.067-1.1-1.331-1.533.594-.268,1.159,1.121.661-1.13-.575-.431-.163-.738-.278.029.077-.814.45-.5-.68-.278-.038-.479.5-.613.077-.747-.987-.651-.268-1.8-.489-.671-1.485-.977-.019.5-.843-.153-1.81-1.628-.3-.785h-1.571l.105-1.082-1.2-.556-.536.8.268.623-.335.556-1.839-2.059-5.173-2.586.5-.6-.364-.469-1.264.335-1.456-2.222.651-1.274-.421-.6-1.14.412-.383-.92-1.217-.086-.393.22.23.575-.23.68.46.259-.651,1.044-.479.22-.057-.565-.364-.144.2-.421-.776-.3-1.14.192.268.776-.776.22-1.753-2.04,1.121-.163.4-.421.048-.766.891.833.853-.412.22-.948.651.144.441-.469-.326-1.255-1.054-.038-.977.613-.833,1.753-1.5-.46-.153.5-.6-.105-.354-1.073-1.648-1.5-.45.431,1.533,1.111.077.718-1.533-.967-2.749-.5-.172-.316,1.954.259.067-1.877-.479-.038-1.542-1.264-1.035.268-.354-.661.163-2.548.23-.086-.335-1.035-.431-.153-.038-.642.671-2.04.335.038.019-.8,2.05-3.276.671-2.309,1-1.063.239.105-.2.948-.824.268-.565,2.6.4.527.3-.163.364-.939-.326-.556.163-.259.326.489.776-1.648.172-1.6Z"
                transform="translate(-24.333 189.271)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-SON"
                d="M295.523,220.94l-.987-.115h0l.1-.5h.46Zm-1.609-.68.1-1.475.211.929-.307.546Zm-32.607-27.31.6.833-.182.326,1.284.584-.278.374L261.3,194.2l.01-1.245Zm-40.3-50.587.46,1.494-.259,1.092.92,1.149,1,2.491-.4.354-.1,1.322-1.475,3.793-.048.977-.709.6-.46-.45-.738.105-1.159-1.092-1.284-.067-.3-.536-.5-.01-.125-.354-1.945-1.082,1.59-1.169.565-.833-.374-3.113.68-2.864.565-.833,1.284.22.948-.862,1.858-.335ZM164.568,72.475l.8.125-.048.613-1.4-.393-.019-.335h.671Zm-3.573-1.734.958.46.68,1.379,1.274.91.508,1.044-.268.651-.766-.623-.671.077-1-.517-.843-.872L160.5,72.3l.153-1.379ZM305.188,84.363l1.006,1-.9,1.226.5,1.83,1.571,2.72.23,1.533-.479,1.226.019,1.734-1.5.067-.038.594,2.615,2.538.077,2.318,1,.881-.642,1.542.105.776,1.054,1.408-.057,1.14,1.849,5.891-.268,1.734-.814.508.153.948,1.389,1.264V118.3l-1.217,2.462-.565,4.722.326.958.546.029.718.92.642,5.958-.01,2.491-.766.91-.153.862.239,5.431-.278.489-1.322.651-.1,2.577-1.427,1.217.441,2.433-.814,1.226.766,2.538-1.418,2.51-.182,1,.8,1.868-.4,2.778,1.485,1.734,2.155.824-.565,1.14-.891.1-.929,1.169-1.1.115-1.341,1.006-.489.029-1.7-1.255-.738-.134-1.389.814-2.423.469-1,1.331.182.575,1.494,1.035-.565,2.634.374.671,1.753.709,2,2.855.364,1.59-.671,2.347.5.623,2.7,1.628.6,2,.929,1.36,1.561,3.9.038,1.648-1.2,3.008.45,3.3-.7.4-.019.441,3.391,1.763,1.935-.594,1.7.374h0l-.3,1.705-2.989,3.717.278,2.385-1.782.527-1.973,3.7-3.918.939-.45.833-.105,2.385-1.916.556-1.226,1.121-2.807,1.628-.326-.335.048-.776-.46.077-.125-.747-.757-.872-.479.9-.374-.163-.23-1.791-.278.182.144,1.494h0l-.4.01-.23.517-1.207-1.638.115-.967-1.293-2.347-2.194-2.452-1.389-.977-.814-.125,1.006.153.048-.345-.383-.891-.546.038-.345-.718-.613.192-.067-.546-.374-.086-.7,1.015.4.718-2.558-.067-.479.623-2.433-.958-1.14-1-.891-1.217.01-.92-.987-2.682L277,202.951l.182-.144.929,1.293.172-.824-.6-.4.24-.364-.3-1.561-1.657-1.322-.287.345-.4-.393-1.035.92-.987-.651-4.77-1.188-2.922-2.9-1.331-.671.3-.22-.393-.881.077-1.236-.6-.374-.5.354-.671-.508-.144-1.217-.374-.048-.383-.718.24-1.973-.594-1.13.22-.374-.776-.757.105-.738,1.312-.939-.718-.115-.23.757.163-1.475.661-.335-.661-.115-.278-1.063.46-.354-.144-.718.393-.738.824.172.278-.5-.948-.757-.785-.182-.517.46-.6-.508-3.707-.872-.316-.8-1.571.833.536.872-.182,1.006-1.169-.421-.929-.967.383-.536-.556-.144-.019-.68-1.284-.259-1.264.718-1.427-1.3-.939-.316-.843-1.226-.929-.287-.661-.8-.182-.833-1.015-1.188-.077-.833-.661-.862-.374.077-.441-1.513h-.383l-1.111-1.082-.1-1.312-.833-.718-5.221-1.925-.153-1.092-1.111-2.51-3.841-3.506-.46-1.2.546-.067.489-.556.508,1.121.613-.393-.019-.814-.6-.469-.508.364-.46-.709-.259.2-1.226-.977-1.159-.278-.422-1.082-.929-.738-.259-.709-1.83-.527.536-.7-.4-.8-.048-1.264-.326-.182.441-.939-.45-1.073-.383-.134-.048-.594-.661-.067.508-1.734-.594-1.734-.613-.565-1.264.01-.785.383-1.59-.833-.2-1.054.958-2.682-.287-.546-.948-.517-.153-1.13-.958-.8-1.6-2.72-.891-.623-.623-1.858-1.092-1.542-.316-1.533-1.14-.546-.613.172-.23-.316.335-2.356-.594-1.408.125-3.314-.489-1.351-1.868-1.81-.105-2.922-.929-1.916-2.309-1.964-2.433-4.464-.872-3.3.7-2.184.057-2.9.287.077.642-.967.077-1.073-.479-2.634-.3.105-.182-.757-.45.029-2.213-1.3-.642-1.054-.785.7-3.717-.9-.23-.335-.3.23-2.471-.469-.1-.46-.536-.259-1.236-.153.22-1.408-.546-1.523.057-.8-.517-.881-1.14-.929-4.148-1.772-.125-.565-.364.134-.211-.5-.364.556-.757-.527-.556.661-.172.594.335-.067.2.5-.68.91-.68-.077-.393,1.14-2.73-.1-3-1.37-1.379-1.485-2.787-1.533-.421-.565-.718-.153-2.309-2.232-2.059-.134-1.226-.556-.939-1.121h-.785l-3.659-2.644-.881-.125-.92-.91.575-.939-.23-.939.4-2.558-.278-1.092.594-1.753-1.341-1.906,1.734-2.328.048-1.284.45-.46.575-1.964,1.284-.01.45.383.814-.489h0l89.707,32.779Z"
                transform="translate(-18.439 195.864)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="Path_271"
                data-name="Path 271"
                d="M718.13,435.148l1.092,3.563.6.872,1.379.958.948.048.661,1.015.508-.125.077-.4.345.153,1.063,1.36-.172,1.887.431.307,1.035,2.481,1.169,1.753,3.324,2.232.834,1.37,1.255.632,1.437-.144,2.117,1.073,1.236-.613,1.609-.105.287-.69-.326-1.743.287-1.245L742,449.66l3.5.766,1.571,1.485,1.494.632.738.862.651-.4,3.813,1.514-.019,1.571h0l-.2,13.89-10.116.172h0l.508-.987.728-.268-1.054-1.715-1.255-.354-.182-.8-4.157-1.149.718-2.548-1.217-3.161-2.452-1.207-.048-1.925-1.015-1.3-1.159.268-1.14-.172-.565.421-.536-.287.115-.565-1.389-.354-.68,1.762-1.868.029-1.7-.46.038.422-.412-.077-.115.354.393,1.676-.374.747-3.152,1.14-.546.8-5.584,4.09-2.318,2.155-1.83.8-.776-.3-.632-.833-.038-.632-1.159-1.858-.517-.192-.422.335-1.2.057-.345-.316.287-1.791-1.983-2.088-.364-.891.134-.556,1.36-2.031.335-1.619-2.672.086-.738-.335-1.331-1.619-2.05-.814-.517.584-1.59.268-.718,6.782-.565,1.111-1.15.757-1.331,1.791-.057,1.542-2.424,3.037h0l-1.849-.728-.364-2.107.364-.958-.163-1.2-1.063-2.95-.422-.46-2.4-.929-.4-.556-1.111-.038-1.379-1.945-1.331-.374-1.015-.92.019-2.893-.536-1.408-.115-2.711-.488-.383.824-.833.881.489.23-.278-2.174-.374,1.811-.853"
                transform="translate(-40.472 179.722)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="Path_272"
                data-name="Path 272"
                d="M680.114,445.56h0l4.272-1.446.134,1.188,2.548-.45.556-.393-.278-.929.68-.393,1-.134.22.422.718.057,1.255-.23.575-.6-.115-1.14,3.142-.766.24.651.814-.833,5.153-.22.843.393-.642.536.067.853.766.038.766-.422.067.268h.642l.364-.536-1.753-.718,1.035-.441,2.768.268,2.126-.287,2.883-1.561,2.194-2.634,5.058-.948"
                transform="translate(-40.552 179.722)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-TAM"
                d="M585.99,280.655l-.23,4.272-.268.086Zm6.485-25.289.441.153-2.721,6.677-.316-1.312.24-.862.556-.479.134-.967,1.667-3.209Zm-8.334,77.9-1.916,1.341-1.456-1.571-.4-1.092-1.149-1.006-1.293-.163-1.5-1.149-1.1-.374-.69-1.245-2.433.278-.383.671.2.661-1.9.421-.144-.575-.393.172-.7-.556-1.063-.211-3.985.24-1.7.967h0l-.92-.163-2.05.421-1.81-1.13-.68.757-2.222.077-1.916-.374-1.715.211-2.059-2.73-.881-2.414-.8-1.121-1.322-1.035-1.619-.536-.776-1.236-.594-.316-.929.536.048.987-.623.412-2.107-.7-3.123-.249L532,320.159l-1.044-.891-.182-.661-.22-2.855.326-3.477-.46-1.1-.987-.747-.144-1.964-.853-1.015h0l.4-.738-.517-1.293.766-.651,2.969.364.2-1.7-.374-2.72.316-.92,1.408-1.7.642-2.692,2.213-1.178,2.443.651.68-.134,1.676-.929.326-1.446.345-.1,2.155.431-.728-2.241-1.312-.747-1.341-3.477-1.686-2,.824-6.485-1.207-2.519-1.763.086-.211-.268.661-2.012,1.916-1.293,2.749-1.217,1.255-2.04,1.676.105L546,267.062l1.1-.46.345-.987.364-.125.374,1.456,1.063-.431.45-1.533.881-.661.872-.086.364-1.6-.642-.345-.575-2.146.24-.383,1.207-.393,1.4-1.887.489.038.575,1.121,2.682.22.719-1.945.948-1.092,4.2-.814.948-1.082.737-.077,1.312-1.379,1.2-.6.565-1.312,1.475-1.5-.019-.5-.383-.259-1.916-.6-.948-.8.393-.766-.661-1.5.2-1.983.45-.738-.565-1.456.287-3.2-.766-.977-.125-1.408-.661-.421-3.247.651-1.015-.144-2.548,1.456-.92-.517-1.14-2.242-.623-.565-3.142-.441-.364-.632-.785-.441.862-.316-.728-1.284.345-.69-.172-.575-.6-.048-.718.709-.661-.086-1.121-1.552.057-.948-1.36.115-1.734-2.376-1.2-3.966-1.8-.22-.814-1.82-.643-.383-.843.364-.765-.786.22-3.084.45-1.236-.5-.747-1.207-.632-1.4.048-.125-1.073.776-2.289,1.571-1.332-.4-3.056-1.1-1.092-.23-1.322.211-2.356-1.217-.833,1.121-1.36-.048-.843-3.832-.182-.575-.431.335-1.025-.258-.757.594-.661,2.663-.766,1.226-1.648.623-.326h0l.172.278h1.207l.105.68.671.172.172-.374h.718l.671,1.006.326-.259.345.192.68,1.169-.364,1.648h.718l.422.508-.326,2.778-.948,1.906,1,.134-.125.891.259.268.709-.182.345.527.374,1.849-.335,1.925.3.441-.5.632.153,1.169.728.153.795,1.159-.278.374.441.7,1.015.3.4,1.437.738.259.977,1.992-.057.747.843,1.006.45,2.261.4.211.1,2.27,1.169.527.68,1.121-.45,1.207.929.977.776-.4.766.527.833-.2.46.124.048.575.718-.105.709.556.344-.268,1.849-.01.316.853,1.236.249,1.456,1.945,2.28-.105v.556l.3-.105.172.421.479-.46.9.441-.048.4,1.533-.335.009.632,1.294,1.159,1.264-.115.038.728.718.766,1.427.824.757.067.345.421.517-.5.67.575,1.657-.489.354.69.642.029.1-.489,1.207-.163.335.345,2.51-.259.671.4.709-.335.374.22-.144.46.795.23,2.768-.01,1.063,1.83.431-.125.479.939.374-.259.7,1.293,1.82.546-.182.3.613.412,1.446-.259-.105-1.6.671-.565.46.307.172-.335.891-.038.067-.489L598,236.3l1.312-.048.335.268-.316,4.742-.939,3.64-5.412,10.6-.757-.239,1.734-2.979-.68-.546.44-.584-.393-.565-1.073.048.153-.527-1.475-.153-.594-.757-.958,1.035.7-.125.661,1.073.211-.182.316,1.054-.3.24-.23-.508-.6.192-.364.738.335.182.3,1.188-.738.268-.287-1.312-.8.584-.345-.565-.01-1.073-1.715-.23.057.422-.833.776.489.776-.3.9-.5.077-.852-.575-.2.536.536.086.623.853.3-.2.556.307-.182.747.852.632-.737,1.015.057,1.255-1.284,2.8.383.431.709-.029.383.709.824.192-1.446,1.082-.22.948-.4.067-.048.661.575.345.125.747-.517,2.845.393.105.316.8-.833,4.052-.489.038-.268-.517.125-1.37-.929.268-.833-.623.22.891-.661.422.393.536-.287.987.881,2.117.364.172-.211.671.575-.144.115-1.571.728-.671.23,3.084-.651,6.14-.335.45-.22-.077.249-.536-.565-.077-.048.479.853.412.038,6.763.843.441.038.393-.422.307-.335,1.983.172.632.335.086.1-1.9-.24.038-.077-.68.508-.3-.9,9.225.287,10.546-.2,3.439-.449.623-.364,1.839-1.2,1.446-.939,1.992-.163,1.159.939.7.048,2.7-.374-.029-.009.948.536,1.868.431-.125,1.12,2.615-.152.345Z"
                transform="translate(-34.161 190.012)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-TLA"
                d="M576.747,407.427l1.973.364.584-.23,1.485-1.753.469.067.68,1.015,3.927,3.266-.125,1.657.8.163,1.466-.479.565.364,1.159,1.188.3,1.121.642.575.9.192-.268,1.1-.786.527-2.567.489-.8-.307-1.341,2.232-1.188.8-.738.048-1.4-.948-.546.038L579,421.489l-.824.134-3.889-2.146-1.13-1.255-.517-1.322-1.264-1.322-2.9-2.213-1.111-.354-.671.278h0l-.508-.575-.728.239-.92-1.753h0l4.8-1.638.958.22,2-.268,1.389-1.159.785-1.408Z"
                transform="translate(-35.686 180.957)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-VER"
                d="M589.87,349.958m-26.256-13.851,1.705-.967,3.985-.239,1.063.211.7.556.393-.172.144.575,1.9-.421-.2-.661.383-.671,2.433-.278.69,1.245,1.1.374,1.5,1.149,1.293.163,1.15,1.006.4,1.092,1.456,1.571,1.916-1.341h0l.6,5.144,1.59,4.349-.115.977-.383.211.594,1.293.412,3.123.795.891.077,1.063,1.772,3.123,1.446-.326.5.718-.364,1.245.479,1.667.948.766.067,1.073.489.163.709,2.778,4.493,9.522.613,2.816.757,1.3,4,3.947.68,1.169,5.661,6.111,3.314,4.176.613,1.322,2.5,2.768,1.1,3.142.105,1.916.613,1.015-.1,1.063,1.456,2.04-.172,1.724.642,2.529,1.648,1.264,1.169.441.163.671.948.431.92,1.3-.3.623.2.862.728.632,2.308.326.287,2.414.642,1.7.824.891,3.372,1.619.01.69-1.954-1.293-.929-.077-.057.287.45.144v.623l.718.306.3.584-.115.23-.642-.316-.326.278.163.479,1.121-.124-.067-.632.872-.259.6.287.211-1.14,1.724,1.121,2.6.709,9.129.23,2.874,2.251.556,1.121,2.117.584,3.19.287.489.412.546,1.763.728.738.125.642,2.682,2.673.536,2.289,2.558,1.226,3.631-.374,6.964-1.724h0l-1.81.853,2.174.374-.23.278-.881-.489-.824.833.488.383.115,2.711.536,1.408-.019,2.893,1.015.92,1.331.374,1.379,1.945,1.111.038.4.556,2.4.929.422.46,1.063,2.95.163,1.2-.364.958.364,2.107,1.849.728h0l-3.563,2.768-1.868.824-.9,1.082h0l-27.367-1.676.316-1.312-.613-.546.01-.948-3.3-2.663-3.219-4.224.22-1.082,1.427-.632.172-.651-1.312-.8-.4-.057-1.159,1.035-3.506.881-2.883,2.021-2.749.153-1.207.632-1.878-.556-2.088-4.243.163-1.121-.326-.69.7-.651.948-2.471.776-1.063.211-1.2-.958-3.631-.5-.421-1.944-.46-1.1.182-1.37-.5-2.366.757-.671-.335.508-.862-1.322-.086-.977-1.81-1.331-.776-1.255.364-1.878-1.494-.613-.862-.575-2.625.556-1.217-.958-.335-1.1-1.1-.623.584-.048.824.479.709-.287.92-1.121,2.088h0l-1.772-2.079-2.471-.785-1.332.766-.92,1.006-1.37-.1-1.782-3,.163-.718-3.65-1.073-.814-.69-.345-1.073v-2.855l1.782-2.309.067-.527-.5-5.862.172-.862,4.617.211.508-.115.153-.671-.105-1.236-.45-.565-2.807-1.217-3.477-.786-1.379-.948-.326-.4.536-1.322-1.322-2.855.23-.671.546-.23.939-2.519.642-.833.326-.929-.3-.939.182-.776,1.59-4.1,2.06-2.855-.067-.489-6.763-2.385-.786.642-1.169,1.868-1.542,1.341-.565-.182-.824-1.705-1.954.211-.651-.527-.441-2.27-.057-3.841,1.121-.728,1.542.881,1.437-2.376-.46-1.073-2.127-1.169-.776-.8-.153-.814.345-.8-.776-.575-.833.182-.153-.287,1.178-.728.01-1.715-1.945.307-1.14-.374-.259.977-.843.9-.077.8-.929.718-.671,2.452h0l-1.389-.91-.8.345-2.05,2.232-1.533.7-1.81,2.136-2.376,1.1-1.274,1.485-.479.211-.843-1.035-1.121-2.481.24-1.992.556-1.245.316-3.506.623-.517.3-.824,2.289-2.069h.441l1.284,1.332,1.389-.22.239-.6-.536-1.006.6-2.117,1.782-2.356-2.424-3.276-1.945-.594-.824.853-1.264-.057-1.207-.632-1.753-1.638.287-2.548-.287-.718-.814-.163h0l-.125-3.209-1.274-.211-.853-3.171.2-.814h1.207l-.048-.671,1.006.048.278-.785,1.638-.757-1.14-1.063-.038-.46-.431.115-.565-.7.125-1.178-.335-.249.556-.508h-1.092l-.364-.364.259-.4,2.088.249.393-.259.24-.642-.3-2.443,1.015-.68,2.424-.048-1.15-3.41-.623-.7-4.253-1.322-2.261.038-.508-.249-.22-.91Z"
                transform="translate(-35.647 183.978)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-YUC"
                d="M840.234,359.842l.316,5.077-.134.728-.843.833-.45,3.027,1.274,3.065-.1.929-1.092,1.964-.469,2.347-.824,1.035-.249,1.514-.824,1.121-1.648.785-3,2.586-2.979,1.763-1.446-.105-1.466.421-1.092-.958-1.121.536-1.437,2.644.24,2.1-.345.048-.891-1.609-.967-.01-.479.4-.757,2.27-.441.46-5.278,1.9-1.705,1-1.8,1.533.259.661-.287.307-1.035-.881-1.839,1.705-1.14.393-2.328.029-2.328,1.006-1.331,1.331-3.391.163-.536.469.163.833-.307,1.389-.891,1.925h0l-.422-.441-.757-3.3-3.027-3.429-1.13-2.385-3.688-2.012-1.906-4.272-1.743-1.111-.546-1,.105-.872-.987-1.686-.4-.268-.757.7-.383.967-.46.086-.9-.939-1.073.326-.422-.172-.929-1.925-3.956-.5.153-6.983h0l.067-1.322.584-.565.69-1.638-.143-.5-.316.2-1.063,3.286-.536.517.287-1.485.977-2.835,1.006-1.322,5.364-3.391,2.395-.546,1.992-1.226.881-.01.249-.431.939-.3,2.778-.642.057.383.546-.01-.115-.278.623-.345,3.832-.7,15.9-2.107,4.818-1.83.536-1.341,5.269-1.121,3.094.249,1.274-.354,2.165-1.408,2,.45-.163.431,1.657.6,3.439.239.479.776.967.546,2.079.086,1.82.517.4-.393-3.621-.891Z"
                transform="translate(-44.39 183.041)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <path
                id="MX-ZAC"
                d="M460.872,263.922l.019-1.561.6-.508,5.173-1.025,9,.584,2.433,1.638,2.395.929,1.877,1.59,2.069,2.941,1.408-.393,1.111.307.594,3.41,2.711.019.881-.843,2.174-.488,2.835.354,1.2.785,1.839,3.6,1.312,1.245,2,.776,1.379-.354.172-.747.718-.307,2.031.45h0l-.527.4h0l-.776.422-.345,1.705-1.14.958-.661,1.648-.939.479-1.514-.067-.642.5-.2,1.427-.881,1.705-1.3.843-1.791.46-.613.594-.8,3.3.967,1.13-1.13,2.835-1.054,1.475-4.54,1.523-3.334,4.924-3.41,2.04-2.184.8-.814.987-.5,1.379-2.816.556-2.222-.891-.632.192-.833,1.6.594.776-.489,1.619.987,2.893-1.207,3.822-.192,1.207.24,1.446.9.958,4,2.423,1.063,1.264,2.213.326,1.025.556.881,1.916,1.418,1.935,1.992.048,2.328-.7,4.263-4.416,1.4-.671.68-.048,1.887,2.471-.182,1.581-3.161,6.993,1.322,3.7-.345,1.571-.374-.029-.125.527.249.172-.335.92-1.849,3.8h0l-.776.9-.929-.6h0l-.268-.374-2-.718-2.558-2.251-2.04-1.092-3.391-.872h0l-1.092-1.954-.565-2.337L473.5,334.5l-1.274-1.284-.738-1.331-.7.163-.374,1.207-1.159.881-3.324,1.887-1.715.364-1.025.7-.891,3.1-3.19,4.4-1.188,2.749.067,1.159,2.241,2.165h0l-.086,2.117.623,1.772,2.663,1.705-.46,2.577-.92,1.255-2.194,1.341-3.429,2.788-1.456-.479-1.312.469-.6.517-.316,1.935-1.408.5-.105.354-1.638-.412-1.284.249-.211.536-.843-.067-.948.46-2.778-1.542-2.165.182-.9-.469-.718-1.628-.718-.776-2.395-.594-.163-.479,3.87-1.734,1.121-1.073.1-.977-.326-.556-.642-.134-.977.469-.891-.517-.814-2.376,1.715-.441,1.552-1.8.316-2.366-.172-1.715,2.8-1.983,1.7-1.657,3.142-1.609,1.207-.192.709-.661,1.14-2.788.671-3.324-.488-.364-2.06-.134-.479-.421.728-1.446-.393-.824-2.079-.163-1.8.7-.3,1.791,1.188.987-2.289,1.81-.891,1.657-.862.421-.68-.5-1.217-.192-.661.766-1.849-.019-.556-.632.086-.431,3.621-5.91.038-1.111-.575-1.849-1.341-1.264-1.36-.489-3.286.642-.6.546v6.744l-.364,1.111-1.111-.163-1.159-1.705.038-3.18,1.619-4.042,1.389-1.791,1.054-.785.163-.872-1.734-.967-2.548.364-2.069,1.466-.105.757,1.667,3.678.153,1.37-.987,1.13-1.724,1.092-.259-.469,1.006-3.1-.508-.354-2.97,1.121-.345,2.9-1.293-.326-1.035,1.159h0l-.69-.412-.412-.92-.192-2.653h0l1.111-2.922.968-1.446,3.075-1.36.239-.6.153-5.316-.814-2.96.077-1.226,1.312-2.615.584-.354,1.236-5.527.086-1.8,2.222-2.768,2.682-2.318-.853-4.09.431-1.149-.354-1.81.3-1.609-.354-1.581-.747-.489.22-1.264,2.3-.69,2.165-2.05,1.983-2.711,2.3-1.743.45-1.284,1.638-1.312,3.736.259,2.136.671,2.155-.766,1.073.1.958.671,2.548.833.728-.872,1.609.22,5.814-.556.115-.651-.479-.354.019-.393.929-.5-.977-3.832-1.159-.431.776-4.694-.766-.9-1.466-.68-.345-1.054-.718-.326-.038-1.581Z"
                transform="translate(-29.692 187.06)"
                fill="#c4c4c4"
                stroke="#fff"
                strokeMiterlimit="10"
                strokeWidth="0.5"
              ></path>
              <g
                id="Group_277"
                data-name="Group 277"
                transform="translate(236.62 314.126)"
              >
                <g id="Group_276" data-name="Group 276">
                  <path
                    id="Path_273"
                    data-name="Path 273"
                    d="M259.462,123.984h.5a1.106,1.106,0,0,0,1.27,1.057c.568,0,.976-.259.976-.769,0-.563-.494-.7-1.229-.856s-1.345-.368-1.345-1.143c0-.678.563-1.085,1.4-1.085a1.368,1.368,0,0,1,1.557,1.281H262.1a.947.947,0,0,0-1.058-.879c-.558,0-.925.236-.925.661,0,.466.461.6,1.075.723.8.161,1.528.356,1.528,1.282,0,.769-.626,1.2-1.511,1.2A1.539,1.539,0,0,1,259.462,123.984Z"
                    transform="translate(-259.462 -121.187)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_274"
                    data-name="Path 274"
                    d="M263.242,123.942a1.438,1.438,0,1,1,1.436,1.562A1.447,1.447,0,0,1,263.242,123.942Zm2.389,0c0-.655-.322-1.177-.953-1.177s-.96.522-.96,1.177.328,1.172.96,1.172S265.631,124.6,265.631,123.942Z"
                    transform="translate(-259.621 -121.237)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_275"
                    data-name="Path 275"
                    d="M267.187,122.846h.012a1.113,1.113,0,0,1,.942-.46.892.892,0,0,1,1,.953v2.085h-.465v-2.045c0-.384-.254-.6-.672-.6a.786.786,0,0,0-.816.821v1.827h-.465v-2.969h.465Z"
                    transform="translate(-259.768 -121.237)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_276"
                    data-name="Path 276"
                    d="M269.716,123.942a1.438,1.438,0,1,1,1.436,1.562A1.447,1.447,0,0,1,269.716,123.942Zm2.389,0c0-.655-.322-1.177-.953-1.177s-.96.522-.96,1.177.328,1.172.96,1.172S272.1,124.6,272.1,123.942Z"
                    transform="translate(-259.894 -121.237)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_277"
                    data-name="Path 277"
                    d="M273.662,122.951h.011a1.056,1.056,0,0,1,.885-.529.448.448,0,0,1,.19.034v.448h-.017a.541.541,0,0,0-.207-.029.856.856,0,0,0-.861.9v1.649H273.2v-2.969h.466Z"
                    transform="translate(-260.04 -121.239)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_278"
                    data-name="Path 278"
                    d="M276.946,125.012h-.006a1.194,1.194,0,0,1-.988.47c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.419-.063.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.654H275.1c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.942v1.413c0,.241.057.374.316.317h.017v.338a.637.637,0,0,1-.253.046A.456.456,0,0,1,276.946,125.012Zm.012-.684v-.506a2.146,2.146,0,0,1-.678.218c-.466.086-.839.167-.839.6,0,.4.31.482.615.482A.837.837,0,0,0,276.959,124.328Z"
                    transform="translate(-260.114 -121.238)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_279"
                data-name="Group 279"
                transform="translate(109.893 269.604)"
              >
                <g id="Group_278" data-name="Group 278">
                  <path
                    id="Path_279"
                    data-name="Path 279"
                    d="M133.79,74.708h1.741a1.294,1.294,0,0,1,.988.355.978.978,0,0,1,.265.7.849.849,0,0,1-.661.861v.018a.988.988,0,0,1,.827.993,1.121,1.121,0,0,1-.3.786,1.364,1.364,0,0,1-1.028.391H133.79Zm1.676,1.758c.5,0,.8-.247.8-.672,0-.4-.27-.638-.729-.638h-1.229v1.31Zm.133,1.883a.9.9,0,0,0,.643-.206.674.674,0,0,0,.184-.477c0-.512-.361-.764-.861-.764h-1.258v1.447Z"
                    transform="translate(-127.445 -74.708)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_280"
                    data-name="Path 280"
                    d="M139.4,78.449h-.006a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.419-.063.712-.1.712-.424s-.24-.523-.667-.523c-.482,0-.723.207-.74.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.942V78.19c0,.24.057.373.316.316h.017v.338a.637.637,0,0,1-.253.046A.456.456,0,0,1,139.4,78.449Zm.012-.684v-.506a2.155,2.155,0,0,1-.678.218c-.466.086-.839.167-.839.6,0,.4.31.482.615.482A.837.837,0,0,0,139.408,77.765Z"
                    transform="translate(-127.597 -74.755)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_281"
                    data-name="Path 281"
                    d="M140.144,79.636v-.379h.012a.807.807,0,0,0,.161.017c.2,0,.3-.086.3-.287V75.845h.465V79.01c0,.552-.3.649-.683.649A1.6,1.6,0,0,1,140.144,79.636Zm.477-4.928h.465v.574h-.465Z"
                    transform="translate(-127.712 -74.708)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_282"
                    data-name="Path 282"
                    d="M143.6,78.449H143.6a1.194,1.194,0,0,1-.988.47c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.419-.063.712-.1.712-.424s-.24-.523-.667-.523c-.482,0-.723.207-.74.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.942V78.19c0,.24.057.373.316.316h.017v.338a.637.637,0,0,1-.253.046A.456.456,0,0,1,143.6,78.449Zm.012-.684v-.506a2.146,2.146,0,0,1-.678.218c-.466.086-.839.167-.839.6,0,.4.31.482.615.482A.837.837,0,0,0,143.614,77.765Z"
                    transform="translate(-127.774 -74.755)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_283"
                    data-name="Path 283"
                    d="M127.166,83.754a1.949,1.949,0,0,1,1.947-2.143,1.522,1.522,0,0,1,1.649,1.31h-.506a1.07,1.07,0,0,0-1.154-.862c-.9,0-1.407.73-1.407,1.695,0,1.006.557,1.684,1.413,1.684a1.139,1.139,0,0,0,1.195-1.132h.512a1.743,1.743,0,0,1-.494,1.154,1.7,1.7,0,0,1-1.229.431A1.938,1.938,0,0,1,127.166,83.754Z"
                    transform="translate(-127.166 -74.999)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_284"
                    data-name="Path 284"
                    d="M133.323,85.449h-.006a1.2,1.2,0,0,1-.989.47c-.562,0-1-.264-1-.828,0-.7.552-.844,1.292-.959.42-.063.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.74.654h-.448c.017-.574.4-1,1.183-1,.631,0,1.1.236,1.1.942V85.19c0,.24.057.373.316.316h.016v.338a.628.628,0,0,1-.252.046A.455.455,0,0,1,133.323,85.449Zm.012-.684v-.506a2.137,2.137,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.311.482.616.482A.837.837,0,0,0,133.335,84.765Z"
                    transform="translate(-127.341 -75.05)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_285"
                    data-name="Path 285"
                    d="M134.587,81.708h.467v4.107h-.467Z"
                    transform="translate(-127.478 -75.003)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_286"
                    data-name="Path 286"
                    d="M135.752,81.708h.466v.574h-.466Zm0,1.137h.466v2.969h-.466Z"
                    transform="translate(-127.527 -75.003)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_287"
                    data-name="Path 287"
                    d="M136.7,82.843h.407v-.477c0-.512.293-.7.787-.7a2.157,2.157,0,0,1,.236.012v.4c-.08-.006-.1-.006-.149-.006-.3,0-.408.075-.408.316v.454h.552v.379h-.552v2.59h-.466v-2.59H136.7Z"
                    transform="translate(-127.567 -75.001)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_288"
                    data-name="Path 288"
                    d="M138.4,84.379a1.438,1.438,0,1,1,1.436,1.562A1.447,1.447,0,0,1,138.4,84.379Zm2.389,0c0-.655-.322-1.177-.953-1.177s-.96.522-.96,1.177.328,1.172.96,1.172S140.787,85.034,140.787,84.379Z"
                    transform="translate(-127.639 -75.049)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_289"
                    data-name="Path 289"
                    d="M142.344,83.388h.011a1.056,1.056,0,0,1,.885-.529.455.455,0,0,1,.191.034v.448h-.018a.54.54,0,0,0-.207-.029.856.856,0,0,0-.861.9v1.649h-.466V82.893h.466Z"
                    transform="translate(-127.785 -75.051)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_290"
                    data-name="Path 290"
                    d="M144.4,83.283h.011a1.115,1.115,0,0,1,.942-.46.892.892,0,0,1,1,.953v2.085h-.465V83.816c0-.384-.254-.6-.672-.6a.786.786,0,0,0-.816.821v1.827h-.465V82.892h.465Z"
                    transform="translate(-127.872 -75.05)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_291"
                    data-name="Path 291"
                    d="M147.1,81.708h.465v.574H147.1Zm0,1.137h.465v2.969H147.1Z"
                    transform="translate(-128.005 -75.003)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_292"
                    data-name="Path 292"
                    d="M150.063,85.449h-.006a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.419-.063.712-.1.712-.424s-.24-.523-.667-.523c-.483,0-.723.207-.74.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.942V85.19c0,.24.057.373.316.316h.017v.338a.637.637,0,0,1-.253.046A.456.456,0,0,1,150.063,85.449Zm.012-.684v-.506a2.154,2.154,0,0,1-.678.218c-.466.086-.839.167-.839.6,0,.4.31.482.615.482A.837.837,0,0,0,150.076,84.765Z"
                    transform="translate(-128.046 -75.05)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_281"
                data-name="Group 281"
                transform="translate(210.871 441.934)"
              >
                <g id="Group_280" data-name="Group 280">
                  <path
                    id="Path_293"
                    data-name="Path 293"
                    d="M239.2,254.612h1.741a1.294,1.294,0,0,1,.989.356.981.981,0,0,1,.264.7.85.85,0,0,1-.66.861v.017a.99.99,0,0,1,.827.993,1.118,1.118,0,0,1-.3.787,1.364,1.364,0,0,1-1.028.391H239.2Zm1.677,1.758c.5,0,.8-.246.8-.672,0-.4-.27-.638-.73-.638h-1.229v1.309Zm.133,1.884a.9.9,0,0,0,.643-.207.673.673,0,0,0,.184-.476c0-.512-.362-.764-.861-.764h-1.259v1.447Z"
                    transform="translate(-232.861 -254.612)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_294"
                    data-name="Path 294"
                    d="M244.81,258.353h0a1.194,1.194,0,0,1-.989.471c-.563,0-1.006-.264-1.006-.828,0-.7.553-.844,1.293-.959.42-.063.712-.1.712-.425s-.24-.522-.666-.522c-.483,0-.724.206-.74.654h-.448c.016-.574.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.241.057.374.316.316h.016v.339a.628.628,0,0,1-.252.046A.457.457,0,0,1,244.81,258.353Zm.012-.684v-.5a2.138,2.138,0,0,1-.678.218c-.465.085-.839.166-.839.6,0,.4.31.482.616.482A.837.837,0,0,0,244.823,257.669Z"
                    transform="translate(-233.013 -254.659)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_295"
                    data-name="Path 295"
                    d="M245.56,259.54v-.378h.011a.825.825,0,0,0,.161.017c.2,0,.306-.086.306-.287V255.75h.465v3.165c0,.552-.3.648-.684.648A1.493,1.493,0,0,1,245.56,259.54Zm.477-4.928h.465v.575h-.465Z"
                    transform="translate(-233.128 -254.612)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_296"
                    data-name="Path 296"
                    d="M249.016,258.353h0a1.194,1.194,0,0,1-.989.471c-.563,0-1-.264-1-.828,0-.7.552-.844,1.292-.959.419-.063.712-.1.712-.425s-.24-.522-.666-.522c-.483,0-.724.206-.74.654h-.448c.016-.574.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.241.057.374.316.316h.016v.339a.628.628,0,0,1-.252.046A.457.457,0,0,1,249.016,258.353Zm.012-.684v-.5a2.138,2.138,0,0,1-.678.218c-.465.085-.839.166-.839.6,0,.4.31.482.616.482A.837.837,0,0,0,249.029,257.669Z"
                    transform="translate(-233.19 -254.659)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_297"
                    data-name="Path 297"
                    d="M232.582,263.659a1.949,1.949,0,0,1,1.946-2.143,1.523,1.523,0,0,1,1.649,1.31h-.506a1.071,1.071,0,0,0-1.155-.862c-.9,0-1.406.73-1.406,1.694,0,1.006.557,1.683,1.412,1.683a1.137,1.137,0,0,0,1.195-1.131h.512a1.746,1.746,0,0,1-.494,1.154,1.7,1.7,0,0,1-1.229.431A1.938,1.938,0,0,1,232.582,263.659Z"
                    transform="translate(-232.582 -254.903)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_298"
                    data-name="Path 298"
                    d="M238.738,265.353h-.006a1.192,1.192,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.419-.063.712-.1.712-.425s-.24-.522-.666-.522c-.483,0-.724.206-.741.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.235,1.1.942v1.413c0,.241.057.374.316.316h.017v.339a.636.636,0,0,1-.253.046A.457.457,0,0,1,238.738,265.353Zm.012-.684v-.5a2.146,2.146,0,0,1-.678.218c-.466.085-.839.166-.839.6,0,.4.31.482.615.482A.838.838,0,0,0,238.751,264.669Z"
                    transform="translate(-232.757 -254.954)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_299"
                    data-name="Path 299"
                    d="M240,261.612h.466v4.107H240Z"
                    transform="translate(-232.894 -254.907)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_300"
                    data-name="Path 300"
                    d="M241.168,261.612h.465v.575h-.465Zm0,1.138h.465v2.969h-.465Z"
                    transform="translate(-232.943 -254.907)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_301"
                    data-name="Path 301"
                    d="M242.115,262.748h.408v-.477c0-.512.293-.7.786-.7.1,0,.173.006.236.012v.4c-.08-.006-.1-.006-.149-.006-.3,0-.408.075-.408.316v.454h.552v.378h-.552v2.591h-.465v-2.591h-.408Z"
                    transform="translate(-232.983 -254.905)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_302"
                    data-name="Path 302"
                    d="M243.813,264.284a1.439,1.439,0,1,1,1.436,1.562A1.447,1.447,0,0,1,243.813,264.284Zm2.39,0c0-.655-.322-1.177-.954-1.177s-.959.522-.959,1.177.327,1.171.959,1.171S246.2,264.939,246.2,264.284Z"
                    transform="translate(-233.055 -254.953)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_303"
                    data-name="Path 303"
                    d="M247.759,263.292h.012a1.055,1.055,0,0,1,.885-.528.436.436,0,0,1,.19.034v.448h-.018a.538.538,0,0,0-.206-.03.857.857,0,0,0-.862.9v1.649h-.465V262.8h.465Z"
                    transform="translate(-233.201 -254.955)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_304"
                    data-name="Path 304"
                    d="M249.817,263.188h.011a1.113,1.113,0,0,1,.942-.46.892.892,0,0,1,1,.953v2.085h-.465v-2.045c0-.385-.254-.6-.673-.6a.786.786,0,0,0-.815.822v1.827h-.465V262.8h.465Z"
                    transform="translate(-233.288 -254.954)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_305"
                    data-name="Path 305"
                    d="M252.519,261.612h.466v.575h-.466Zm0,1.138h.466v2.969h-.466Z"
                    transform="translate(-233.421 -254.907)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_306"
                    data-name="Path 306"
                    d="M255.478,265.353h0a1.194,1.194,0,0,1-.989.471c-.563,0-1.006-.264-1.006-.828,0-.7.553-.844,1.293-.959.42-.063.712-.1.712-.425s-.24-.522-.666-.522c-.483,0-.724.206-.741.654h-.447c.016-.574.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.241.056.374.316.316h.016v.339a.627.627,0,0,1-.252.046A.457.457,0,0,1,255.478,265.353Zm.012-.684v-.5a2.138,2.138,0,0,1-.678.218c-.465.085-.839.166-.839.6,0,.4.31.482.616.482A.837.837,0,0,0,255.491,264.669Z"
                    transform="translate(-233.462 -254.954)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_307"
                    data-name="Path 307"
                    d="M240.033,271.326h.506a1.106,1.106,0,0,0,1.27,1.057c.567,0,.975-.259.975-.769,0-.563-.493-.7-1.229-.856s-1.344-.368-1.344-1.143c0-.678.563-1.086,1.4-1.086a1.37,1.37,0,0,1,1.557,1.282h-.489a.947.947,0,0,0-1.058-.879c-.557,0-.924.236-.924.661,0,.466.46.6,1.075.723.8.161,1.527.356,1.527,1.282,0,.769-.626,1.2-1.511,1.2A1.539,1.539,0,0,1,240.033,271.326Z"
                    transform="translate(-232.896 -255.198)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_308"
                    data-name="Path 308"
                    d="M245.876,272.769v-.408h-.012a1.029,1.029,0,0,1-.9.477.919.919,0,0,1-1-.983V269.8h.466v2.045a.585.585,0,0,0,.666.608.768.768,0,0,0,.776-.827V269.8h.465v2.969Z"
                    transform="translate(-233.061 -255.251)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_309"
                    data-name="Path 309"
                    d="M247.573,270.292h.011a1.057,1.057,0,0,1,.885-.528.441.441,0,0,1,.191.034v.448h-.018a.539.539,0,0,0-.207-.03.857.857,0,0,0-.861.9v1.649h-.466V269.8h.466Z"
                    transform="translate(-233.193 -255.25)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_283"
                data-name="Group 283"
                transform="translate(330.374 331.316)"
              >
                <g id="Group_282" data-name="Group 282">
                  <path
                    id="Path_310"
                    data-name="Path 310"
                    d="M357.337,141.275a1.949,1.949,0,0,1,1.947-2.143,1.522,1.522,0,0,1,1.649,1.31h-.506a1.069,1.069,0,0,0-1.154-.862c-.9,0-1.407.729-1.407,1.695,0,1.006.556,1.683,1.413,1.683a1.139,1.139,0,0,0,1.194-1.132h.512a1.747,1.747,0,0,1-.494,1.155,1.7,1.7,0,0,1-1.229.431A1.938,1.938,0,0,1,357.337,141.275Z"
                    transform="translate(-357.337 -139.132)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_311"
                    data-name="Path 311"
                    d="M362.146,140.756h.012a1.115,1.115,0,0,1,.943-.46.893.893,0,0,1,1,.954v2.085h-.466V141.29c0-.385-.253-.6-.672-.6a.787.787,0,0,0-.816.822v1.827h-.465v-4.107h.465Z"
                    transform="translate(-357.52 -139.136)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_312"
                    data-name="Path 312"
                    d="M364.849,139.228h.465v.575h-.465Zm0,1.138h.465v2.969h-.465Z"
                    transform="translate(-357.653 -139.136)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_313"
                    data-name="Path 313"
                    d="M366.461,140.756h.012a1.113,1.113,0,0,1,.942-.46.893.893,0,0,1,1,.954v2.085h-.465V141.29c0-.385-.254-.6-.673-.6a.786.786,0,0,0-.815.822v1.827H366v-4.107h.465Z"
                    transform="translate(-357.702 -139.136)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_314"
                    data-name="Path 314"
                    d="M371.052,143.386v-.408h-.011a1.028,1.028,0,0,1-.9.476.919.919,0,0,1-1-.982v-2.056h.466v2.044a.585.585,0,0,0,.667.609.768.768,0,0,0,.775-.828v-1.826h.466v2.969Z"
                    transform="translate(-357.834 -139.186)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_315"
                    data-name="Path 315"
                    d="M374.1,142.969h-.006a1.192,1.192,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.96.418-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.655h-.448c.017-.575.4-1,1.184-1,.631,0,1.1.235,1.1.942v1.413c0,.241.057.374.316.316h.017v.339a.64.64,0,0,1-.253.045A.456.456,0,0,1,374.1,142.969Zm.012-.684v-.5a2.148,2.148,0,0,1-.678.217c-.466.086-.839.167-.839.6,0,.4.31.483.615.483A.838.838,0,0,0,374.108,142.285Z"
                    transform="translate(-357.958 -139.184)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_316"
                    data-name="Path 316"
                    d="M375.809,140.756h.01a1.115,1.115,0,0,1,.943-.46.893.893,0,0,1,1,.954v2.085H377.3V141.29c0-.385-.253-.6-.672-.6a.786.786,0,0,0-.815.822v1.827h-.466v-4.107h.466Z"
                    transform="translate(-358.095 -139.136)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_317"
                    data-name="Path 317"
                    d="M380.4,143.386v-.408h-.012a1.029,1.029,0,0,1-.9.476.919.919,0,0,1-1-.982v-2.056h.466v2.044a.585.585,0,0,0,.666.609.769.769,0,0,0,.776-.828v-1.826h.465v2.969Z"
                    transform="translate(-358.228 -139.186)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_318"
                    data-name="Path 318"
                    d="M383.444,142.969h-.006a1.193,1.193,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.292-.96.42-.062.713-.1.713-.424s-.24-.523-.667-.523c-.483,0-.724.207-.74.655h-.448c.017-.575.4-1,1.183-1,.632,0,1.1.235,1.1.942v1.413c0,.241.057.374.316.316h.017v.339a.64.64,0,0,1-.253.045A.456.456,0,0,1,383.444,142.969Zm.011-.684v-.5a2.14,2.14,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.311.483.616.483A.837.837,0,0,0,383.456,142.285Z"
                    transform="translate(-358.352 -139.184)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_285"
                data-name="Group 285"
                transform="translate(434.856 391.839)"
              >
                <g
                  id="Group_284"
                  data-name="Group 284"
                  transform="translate(0 0)"
                >
                  <path
                    id="Path_319"
                    data-name="Path 319"
                    d="M466.411,204.457a1.948,1.948,0,0,1,1.946-2.142,1.523,1.523,0,0,1,1.65,1.309H469.5a1.071,1.071,0,0,0-1.154-.862c-.9,0-1.407.73-1.407,1.695,0,1.006.557,1.684,1.412,1.684a1.139,1.139,0,0,0,1.2-1.132h.512a1.747,1.747,0,0,1-.494,1.155,1.7,1.7,0,0,1-1.229.43A1.938,1.938,0,0,1,466.411,204.457Z"
                    transform="translate(-466.411 -202.315)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_320"
                    data-name="Path 320"
                    d="M470.581,205.082a1.438,1.438,0,1,1,1.436,1.562A1.447,1.447,0,0,1,470.581,205.082Zm2.389,0c0-.655-.322-1.177-.953-1.177s-.96.522-.96,1.177.328,1.172.96,1.172S472.97,205.738,472.97,205.082Z"
                    transform="translate(-466.587 -202.366)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_321"
                    data-name="Path 321"
                    d="M475.873,206.152h-.006a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.264-1.006-.827,0-.7.552-.845,1.293-.96.419-.063.712-.1.712-.424s-.24-.523-.667-.523c-.482,0-.723.207-.74.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.943v1.413c0,.241.057.373.316.316h.017v.338a.638.638,0,0,1-.253.046A.456.456,0,0,1,475.873,206.152Zm.012-.684v-.506a2.153,2.153,0,0,1-.678.218c-.466.086-.839.167-.839.6,0,.4.31.482.615.482A.837.837,0,0,0,475.886,205.468Z"
                    transform="translate(-466.725 -202.366)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_322"
                    data-name="Path 322"
                    d="M477.586,203.939h.012a1.115,1.115,0,0,1,.943-.46.892.892,0,0,1,1,.954v2.084h-.465v-2.044c0-.385-.254-.6-.672-.6a.787.787,0,0,0-.816.822v1.826h-.465v-4.107h.465Z"
                    transform="translate(-466.862 -202.319)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_323"
                    data-name="Path 323"
                    d="M482.178,206.567v-.407h-.011a1.032,1.032,0,0,1-.9.476.92.92,0,0,1-1-.982V203.6h.467v2.045a.585.585,0,0,0,.666.609.769.769,0,0,0,.776-.828V203.6h.465v2.969Z"
                    transform="translate(-466.994 -202.369)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_324"
                    data-name="Path 324"
                    d="M483.409,202.411h.465v.574h-.465Zm0,1.137h.465v2.969h-.465Z"
                    transform="translate(-467.127 -202.319)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_325"
                    data-name="Path 325"
                    d="M484.572,202.411h.466v4.107h-.466Z"
                    transform="translate(-467.176 -202.319)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_326"
                    data-name="Path 326"
                    d="M487.549,206.152h-.006a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.264-1.006-.827,0-.7.552-.845,1.292-.96.42-.063.713-.1.713-.424s-.24-.523-.667-.523c-.483,0-.724.207-.74.654H485.7c.017-.574.4-1,1.183-1,.632,0,1.1.236,1.1.943v1.413c0,.241.057.373.316.316h.017v.338a.637.637,0,0,1-.253.046A.455.455,0,0,1,487.549,206.152Zm.011-.684v-.506a2.138,2.138,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.311.482.616.482A.837.837,0,0,0,487.561,205.468Z"
                    transform="translate(-467.217 -202.366)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_287"
                data-name="Group 287"
                transform="translate(484.925 452.774)"
              >
                <g id="Group_286" data-name="Group 286">
                  <path
                    id="Path_327"
                    data-name="Path 327"
                    d="M518.68,265.928h.546l1.873,2.854c.138.213.3.529.3.529h.01v-3.383h.483v4.107h-.529l-1.883-2.86c-.132-.2-.3-.511-.3-.511h-.011v3.372h-.483Z"
                    transform="translate(-518.68 -265.928)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_328"
                    data-name="Path 328"
                    d="M524.655,270.085v-.407h-.011a1.03,1.03,0,0,1-.9.476.919.919,0,0,1-1-.982v-2.057h.466v2.045a.585.585,0,0,0,.667.609.768.768,0,0,0,.775-.828v-1.827h.466v2.97Z"
                    transform="translate(-518.851 -265.978)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_329"
                    data-name="Path 329"
                    d="M525.713,268.6a1.449,1.449,0,0,1,1.407-1.562c.89,0,1.367.69,1.367,1.672h-2.3c.034.6.362,1.063.96,1.063a.77.77,0,0,0,.821-.609h.454a1.209,1.209,0,0,1-1.275,1A1.434,1.434,0,0,1,525.713,268.6Zm.489-.24h1.785c-.028-.592-.333-.954-.867-.954C526.568,267.4,526.269,267.819,526.2,268.359Z"
                    transform="translate(-518.976 -265.975)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_330"
                    data-name="Path 330"
                    d="M528.706,267.115h.5l.615,1.884c.086.264.195.625.195.625h.012s.108-.367.194-.625l.627-1.884h.483l-1.069,2.97h-.516Z"
                    transform="translate(-519.102 -265.978)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_331"
                    data-name="Path 331"
                    d="M531.557,268.6a1.438,1.438,0,1,1,1.436,1.562A1.447,1.447,0,0,1,531.557,268.6Zm2.389,0c0-.655-.322-1.177-.953-1.177s-.96.522-.96,1.177.327,1.172.96,1.172S533.946,269.255,533.946,268.6Z"
                    transform="translate(-519.222 -265.975)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_332"
                    data-name="Path 332"
                    d="M520.456,273.128h.517v3.63h2.154v.477h-2.672Z"
                    transform="translate(-518.755 -266.231)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_333"
                    data-name="Path 333"
                    d="M523.415,275.8a1.449,1.449,0,0,1,1.407-1.562c.89,0,1.367.69,1.367,1.672h-2.3c.034.6.362,1.063.96,1.063a.77.77,0,0,0,.821-.609h.454a1.208,1.208,0,0,1-1.275,1A1.435,1.435,0,0,1,523.415,275.8Zm.489-.24h1.786c-.028-.592-.333-.954-.867-.954C524.271,274.6,523.972,275.019,523.9,275.559Z"
                    transform="translate(-518.879 -266.278)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_334"
                    data-name="Path 334"
                    d="M526.613,275.75a1.438,1.438,0,1,1,1.436,1.563A1.447,1.447,0,0,1,526.613,275.75Zm2.389,0c0-.654-.322-1.177-.953-1.177s-.96.523-.96,1.177.328,1.172.96,1.172S529,276.405,529,275.75Zm-.85-2.682h.574l-.47.73h-.4Z"
                    transform="translate(-519.014 -266.229)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_335"
                    data-name="Path 335"
                    d="M530.558,274.7h.012a1.113,1.113,0,0,1,.942-.46.892.892,0,0,1,1,.954v2.085h-.465v-2.045c0-.385-.254-.6-.672-.6a.786.786,0,0,0-.816.822v1.827h-.465v-2.97h.465Z"
                    transform="translate(-519.161 -266.278)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_289"
                data-name="Group 289"
                transform="translate(515.594 479.725)"
              >
                <g
                  id="Group_288"
                  data-name="Group 288"
                  transform="translate(0)"
                >
                  <path
                    id="Path_336"
                    data-name="Path 336"
                    d="M550.7,294.064h3.252v.454h-1.368v3.653h-.516v-3.653H550.7Z"
                    transform="translate(-550.697 -294.064)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_337"
                    data-name="Path 337"
                    d="M555.678,297.805h-.006a1.2,1.2,0,0,1-.988.47c-.562,0-1-.263-1-.827,0-.7.552-.845,1.292-.96.42-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.74.654h-.448c.016-.574.4-1,1.183-1,.631,0,1.1.236,1.1.943v1.412c0,.241.057.374.316.317h.016v.338a.628.628,0,0,1-.252.046A.456.456,0,0,1,555.678,297.805Zm.012-.684v-.5a2.163,2.163,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.311.482.616.482A.837.837,0,0,0,555.69,297.121Z"
                    transform="translate(-550.823 -294.111)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_338"
                    data-name="Path 338"
                    d="M556.926,295.248h.465v.424h.012a1.043,1.043,0,0,1,.874-.493.78.78,0,0,1,.775.529h.012a1.04,1.04,0,0,1,.907-.529.863.863,0,0,1,.9.954v2.085h-.465v-2.063a.542.542,0,0,0-.546-.585.763.763,0,0,0-.73.8v1.851h-.466v-2.063a.514.514,0,0,0-.511-.585.806.806,0,0,0-.764.8v1.851h-.465Z"
                    transform="translate(-550.959 -294.111)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_339"
                    data-name="Path 339"
                    d="M563.5,297.805H563.5a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.845,1.292-.96.42-.062.713-.1.713-.424s-.241-.523-.667-.523c-.483,0-.724.207-.741.654h-.448c.017-.574.4-1,1.183-1,.632,0,1.1.236,1.1.943v1.412c0,.241.057.374.316.317h.017v.338a.637.637,0,0,1-.253.046A.457.457,0,0,1,563.5,297.805Zm.011-.684v-.5a2.162,2.162,0,0,1-.678.217c-.465.086-.838.167-.838.6,0,.4.31.482.615.482A.837.837,0,0,0,563.514,297.121Z"
                    transform="translate(-551.152 -294.111)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_340"
                    data-name="Path 340"
                    d="M566.638,298.221v-.408h-.011a1.03,1.03,0,0,1-.9.476.92.92,0,0,1-1-.982v-2.057h.466V297.3a.585.585,0,0,0,.666.609.769.769,0,0,0,.776-.828v-1.827h.465v2.97Z"
                    transform="translate(-551.288 -294.114)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_341"
                    data-name="Path 341"
                    d="M567.887,294.064h.466v4.107h-.466Z"
                    transform="translate(-551.421 -294.064)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_342"
                    data-name="Path 342"
                    d="M569.051,294.064h.465v.575h-.465Zm0,1.137h.465v2.97h-.465Z"
                    transform="translate(-551.47 -294.064)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_343"
                    data-name="Path 343"
                    d="M570.2,295.248h.466v.42h.01a1.117,1.117,0,0,1,.954-.489c.753,0,1.275.574,1.275,1.563,0,.953-.487,1.561-1.292,1.561a1.078,1.078,0,0,1-.937-.437h-.01v1.3H570.2Zm2.228,1.5c0-.667-.282-1.172-.843-1.172-.649,0-.949.529-.949,1.172s.26,1.166.943,1.166C572.143,297.913,572.425,297.42,572.425,296.747Z"
                    transform="translate(-551.518 -294.111)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_344"
                    data-name="Path 344"
                    d="M575.321,297.805h-.006a1.194,1.194,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.845,1.293-.96.419-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.943v1.412c0,.241.057.374.316.317h.017v.338a.638.638,0,0,1-.253.046A.457.457,0,0,1,575.321,297.805Zm.013-.684v-.5a2.173,2.173,0,0,1-.678.217c-.466.086-.839.167-.839.6,0,.4.31.482.615.482A.838.838,0,0,0,575.334,297.121Z"
                    transform="translate(-551.649 -294.111)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_345"
                    data-name="Path 345"
                    d="M576.371,297.253h.477c.063.517.408.66.851.66.489,0,.689-.212.689-.489,0-.332-.23-.419-.776-.534-.585-.121-1.108-.241-1.108-.891,0-.493.379-.815,1.068-.815a1.014,1.014,0,0,1,1.167.93h-.477c-.046-.39-.259-.563-.7-.563-.424,0-.6.191-.6.426,0,.322.287.384.8.489.6.12,1.1.252,1.1.936,0,.6-.489.9-1.172.9C576.871,298.3,576.417,297.919,576.371,297.253Z"
                    transform="translate(-551.778 -294.111)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_291"
                data-name="Group 291"
                transform="translate(476.36 515.626)"
              >
                <g id="Group_290" data-name="Group 290">
                  <path
                    id="Path_346"
                    data-name="Path 346"
                    d="M509.739,334.34h.506a1.106,1.106,0,0,0,1.27,1.057c.568,0,.975-.258.975-.769,0-.563-.493-.7-1.229-.856s-1.344-.368-1.344-1.143c0-.677.563-1.085,1.4-1.085a1.37,1.37,0,0,1,1.557,1.281h-.489a.947.947,0,0,0-1.057-.878c-.558,0-.924.235-.924.66,0,.466.46.6,1.075.723.8.161,1.527.356,1.527,1.282,0,.77-.626,1.2-1.511,1.2A1.539,1.539,0,0,1,509.739,334.34Z"
                    transform="translate(-509.739 -331.543)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_347"
                    data-name="Path 347"
                    d="M515.506,335.367H515.5a1.191,1.191,0,0,1-.988.472c-.563,0-1.006-.265-1.006-.829,0-.7.552-.843,1.292-.959.42-.063.713-.1.713-.424s-.24-.523-.667-.523c-.483,0-.724.207-.741.655h-.448c.017-.575.4-1.006,1.183-1.006.632,0,1.1.236,1.1.942v1.414c0,.241.057.374.316.316h.017v.339a.659.659,0,0,1-.253.045A.456.456,0,0,1,515.506,335.367Zm.012-.683v-.506a2.14,2.14,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.311.483.616.483A.837.837,0,0,0,515.518,334.684Z"
                    transform="translate(-509.898 -331.594)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_348"
                    data-name="Path 348"
                    d="M517.218,333.2h.011a1.115,1.115,0,0,1,.942-.46.892.892,0,0,1,1,.953v2.085h-.465v-2.045c0-.384-.254-.6-.672-.6a.787.787,0,0,0-.816.823v1.826h-.465v-2.969h.465Z"
                    transform="translate(-510.034 -331.593)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_349"
                    data-name="Path 349"
                    d="M520.946,331.627h.517v3.63h2.154v.476h-2.672Z"
                    transform="translate(-510.211 -331.547)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_350"
                    data-name="Path 350"
                    d="M526,335.784v-.408h-.011a1.029,1.029,0,0,1-.9.477.919.919,0,0,1-1-.982v-2.056h.466v2.044a.585.585,0,0,0,.666.609.768.768,0,0,0,.776-.828v-1.826h.465v2.969Z"
                    transform="translate(-510.343 -331.597)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_351"
                    data-name="Path 351"
                    d="M527.229,331.627h.466v.575h-.466Zm0,1.138h.466v2.969h-.466Z"
                    transform="translate(-510.475 -331.547)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_352"
                    data-name="Path 352"
                    d="M528.177,334.816h.477c.062.516.408.661.85.661.489,0,.689-.213.689-.489,0-.333-.229-.42-.775-.534-.586-.121-1.109-.241-1.109-.89,0-.494.38-.816,1.069-.816a1.013,1.013,0,0,1,1.166.931h-.477c-.045-.391-.258-.563-.7-.563-.424,0-.6.19-.6.424,0,.322.287.386.8.488.6.121,1.1.254,1.1.937,0,.6-.488.9-1.171.9C528.676,335.867,528.223,335.483,528.177,334.816Z"
                    transform="translate(-510.515 -331.594)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_353"
                    data-name="Path 353"
                    d="M512.709,338.827h1.712a1.227,1.227,0,0,1,.977.4,1.193,1.193,0,0,1,.276.783c0,.752-.432,1.2-1.327,1.2h-1.121v1.723h-.517Zm.517,1.965h1.081c.579,0,.854-.293.854-.764,0-.493-.3-.752-.827-.752h-1.108Z"
                    transform="translate(-509.864 -331.85)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_354"
                    data-name="Path 354"
                    d="M515.992,341.5a1.438,1.438,0,1,1,1.436,1.562A1.446,1.446,0,0,1,515.992,341.5Zm2.389,0c0-.654-.322-1.177-.953-1.177s-.96.523-.96,1.177.328,1.173.96,1.173S518.381,342.154,518.381,341.5Z"
                    transform="translate(-510.002 -331.896)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_355"
                    data-name="Path 355"
                    d="M519.214,339.974h.431v-.937h.465v.937h.575v.378h-.575v1.93c0,.207.11.276.3.276a.878.878,0,0,0,.247-.04h.017v.4a1.881,1.881,0,0,1-.373.04c-.38,0-.655-.155-.655-.575v-2.034h-.431Z"
                    transform="translate(-510.138 -331.858)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_356"
                    data-name="Path 356"
                    d="M521.05,341.5a1.438,1.438,0,1,1,1.436,1.562A1.446,1.446,0,0,1,521.05,341.5Zm2.389,0c0-.654-.322-1.177-.953-1.177s-.96.523-.96,1.177.328,1.173.96,1.173S523.439,342.154,523.439,341.5Z"
                    transform="translate(-510.215 -331.896)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_357"
                    data-name="Path 357"
                    d="M524.331,342.016h.477c.062.517.408.661.851.661.489,0,.689-.213.689-.488,0-.332-.23-.419-.776-.534-.585-.121-1.108-.241-1.108-.891,0-.494.379-.816,1.068-.816a1.015,1.015,0,0,1,1.167.931h-.477c-.046-.391-.259-.563-.7-.563-.424,0-.6.19-.6.424,0,.322.287.386.8.489.6.121,1.1.254,1.1.937,0,.6-.489.9-1.172.9C524.83,343.067,524.377,342.683,524.331,342.016Z"
                    transform="translate(-510.353 -331.897)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_358"
                    data-name="Path 358"
                    d="M527.4,339.962h.466v2.969H527.4Zm.31-1.2h.568l-.443.729h-.39Z"
                    transform="translate(-510.482 -331.847)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_293"
                data-name="Group 293"
                transform="translate(460.189 548.724)"
              >
                <g id="Group_292" data-name="Group 292">
                  <path
                    id="Path_359"
                    data-name="Path 359"
                    d="M496.08,369.747h-.012a1.493,1.493,0,0,1-1.281.626,2.148,2.148,0,0,1,.046-4.279,1.538,1.538,0,0,1,1.672,1.321h-.535a1.055,1.055,0,0,0-1.148-.873c-.988,0-1.436.74-1.436,1.705a1.487,1.487,0,0,0,1.419,1.677,1.167,1.167,0,0,0,1.241-1.264v-.1h-1.237v-.448h1.753v2.171h-.333Z"
                    transform="translate(-492.857 -366.095)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_360"
                    data-name="Path 360"
                    d="M499.288,370.341v-.408h-.012a1.029,1.029,0,0,1-.9.477.919.919,0,0,1-1-.982v-2.057h.465v2.045a.585.585,0,0,0,.666.609.769.769,0,0,0,.776-.829v-1.826h.465v2.969Z"
                    transform="translate(-493.047 -366.149)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_361"
                    data-name="Path 361"
                    d="M502.332,369.925h-.006a1.193,1.193,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.419-.063.712-.1.712-.424s-.24-.523-.667-.523c-.482,0-.723.207-.74.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.942v1.414c0,.241.057.373.316.316h.017v.338a.638.638,0,0,1-.253.046A.457.457,0,0,1,502.332,369.925Zm.012-.684v-.5a2.156,2.156,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.31.483.615.483A.837.837,0,0,0,502.345,369.241Z"
                    transform="translate(-493.172 -366.146)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_362"
                    data-name="Path 362"
                    d="M504.044,367.759h.012A1.114,1.114,0,0,1,505,367.3a.891.891,0,0,1,1,.953v2.085h-.465v-2.045c0-.384-.254-.6-.672-.6a.787.787,0,0,0-.816.822v1.827h-.465v-2.969h.465Z"
                    transform="translate(-493.308 -366.146)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_363"
                    data-name="Path 363"
                    d="M508.559,369.925h0a1.194,1.194,0,0,1-.989.471c-.563,0-1-.264-1-.828,0-.7.552-.844,1.292-.959.42-.063.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.74.654h-.448c.016-.574.4-1,1.183-1,.631,0,1.1.236,1.1.942v1.414c0,.241.056.373.316.316h.016v.338a.629.629,0,0,1-.252.046A.457.457,0,0,1,508.559,369.925Zm.012-.684v-.5a2.14,2.14,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.31.483.616.483A.837.837,0,0,0,508.572,369.241Z"
                    transform="translate(-493.434 -366.146)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_364"
                    data-name="Path 364"
                    d="M509.309,371.112v-.378h.011a.9.9,0,0,0,.161.017c.2,0,.306-.087.306-.288v-3.141h.465v3.165c0,.552-.3.649-.684.649A1.481,1.481,0,0,1,509.309,371.112Zm.477-4.927h.465v.574h-.465Z"
                    transform="translate(-493.549 -366.099)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_365"
                    data-name="Path 365"
                    d="M512.842,370.341v-.408h-.011a1.031,1.031,0,0,1-.9.477.919.919,0,0,1-1-.982v-2.057h.466v2.045a.585.585,0,0,0,.666.609.769.769,0,0,0,.776-.829v-1.826h.465v2.969Z"
                    transform="translate(-493.618 -366.149)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_366"
                    data-name="Path 366"
                    d="M515.886,369.925h-.006a1.194,1.194,0,0,1-.989.471c-.562,0-1-.264-1-.828,0-.7.552-.844,1.292-.959.42-.063.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.654h-.448c.017-.574.4-1,1.183-1,.631,0,1.1.236,1.1.942v1.414c0,.241.056.373.316.316h.016v.338a.629.629,0,0,1-.252.046A.456.456,0,0,1,515.886,369.925Zm.012-.684v-.5a2.141,2.141,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.311.483.616.483A.837.837,0,0,0,515.9,369.241Z"
                    transform="translate(-493.742 -366.146)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_367"
                    data-name="Path 367"
                    d="M516.875,367.331h.431V366.4h.465v.936h.574v.379h-.574v1.929c0,.207.109.276.3.276a.879.879,0,0,0,.247-.04h.017v.4a1.815,1.815,0,0,1-.373.04c-.38,0-.655-.155-.655-.574V367.71h-.431Z"
                    transform="translate(-493.868 -366.108)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_368"
                    data-name="Path 368"
                    d="M518.711,368.856a1.438,1.438,0,1,1,1.436,1.562A1.447,1.447,0,0,1,518.711,368.856Zm2.389,0c0-.655-.322-1.178-.953-1.178s-.96.523-.96,1.178.328,1.171.96,1.171S521.1,369.511,521.1,368.856Z"
                    transform="translate(-493.945 -366.145)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_295"
                data-name="Group 295"
                transform="translate(500.607 552.26)"
              >
                <g id="Group_294" data-name="Group 294">
                  <path
                    id="Path_369"
                    data-name="Path 369"
                    d="M538.079,373.825a2.066,2.066,0,0,1-1.017.247,2.147,2.147,0,1,1,2.011-2.144,2.2,2.2,0,0,1-.638,1.632l.546.437-.264.332Zm-.4-.322-.414-.333.264-.332.523.419a1.789,1.789,0,0,0,.493-1.328,1.495,1.495,0,1,0-1.482,1.69A1.585,1.585,0,0,0,537.677,373.5Z"
                    transform="translate(-535.052 -369.787)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_370"
                    data-name="Path 370"
                    d="M540.286,371.563h.011a1.056,1.056,0,0,1,.885-.529.448.448,0,0,1,.19.034v.448h-.017a.56.56,0,0,0-.207-.029.856.856,0,0,0-.861.9v1.65h-.466v-2.97h.466Z"
                    transform="translate(-535.253 -369.84)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_371"
                    data-name="Path 371"
                    d="M541.586,372.554a1.438,1.438,0,1,1,1.436,1.563A1.447,1.447,0,0,1,541.586,372.554Zm2.389,0c0-.654-.322-1.176-.953-1.176s-.96.522-.96,1.176.328,1.173.96,1.173S543.975,373.21,543.975,372.554Z"
                    transform="translate(-535.327 -369.838)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_297"
                data-name="Group 297"
                transform="translate(512.061 564.641)"
              >
                <g
                  id="Group_296"
                  data-name="Group 296"
                  transform="translate(0)"
                >
                  <path
                    id="Path_372"
                    data-name="Path 372"
                    d="M547.009,382.712h.517v1.746h2.195v-1.746h.516v4.107h-.516v-1.942h-2.195v1.942h-.517Z"
                    transform="translate(-547.009 -382.712)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_373"
                    data-name="Path 373"
                    d="M551.113,382.712h.466v.575h-.466Zm0,1.137h.466v2.97h-.466Z"
                    transform="translate(-547.182 -382.712)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_374"
                    data-name="Path 374"
                    d="M552.086,385.343c0-.989.569-1.563,1.3-1.563a1.1,1.1,0,0,1,.93.489h.013v-1.557h.465v4.107h-.465v-.362h-.013a1.076,1.076,0,0,1-.919.448C552.591,386.906,552.086,386.3,552.086,385.343Zm2.274.006c0-.69-.333-1.172-.947-1.172-.563,0-.851.506-.851,1.172s.287,1.166.856,1.166C554.078,386.515,554.36,386.021,554.36,385.349Z"
                    transform="translate(-547.223 -382.712)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_375"
                    data-name="Path 375"
                    d="M557.389,386.453h0a1.194,1.194,0,0,1-.989.471c-.563,0-1-.264-1-.828,0-.7.552-.844,1.292-.96.42-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.655h-.448c.016-.575.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.241.057.374.316.316h.016v.338a.611.611,0,0,1-.252.046A.456.456,0,0,1,557.389,386.453Zm.012-.683v-.506a2.139,2.139,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.31.482.616.482A.837.837,0,0,0,557.4,385.77Z"
                    transform="translate(-547.362 -382.759)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_376"
                    data-name="Path 376"
                    d="M558.655,382.712h.466v4.107h-.466Z"
                    transform="translate(-547.499 -382.712)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_377"
                    data-name="Path 377"
                    d="M559.737,387.033h.43c.047.328.356.454.816.454.546,0,.851-.247.851-.764v-.42h-.011a1.014,1.014,0,0,1-.879.46,1.335,1.335,0,0,1-1.3-1.471,1.317,1.317,0,0,1,1.27-1.465,1.028,1.028,0,0,1,.907.448h.011V383.9h.466v2.809c0,.782-.517,1.149-1.3,1.149C560.225,387.854,559.789,387.544,559.737,387.033Zm2.131-1.763c0-.65-.276-1.052-.907-1.052-.558,0-.839.437-.839,1.08s.293,1.075.844,1.075C561.65,386.372,561.868,385.9,561.868,385.27Z"
                    transform="translate(-547.541 -382.759)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_378"
                    data-name="Path 378"
                    d="M562.9,385.383a1.438,1.438,0,1,1,1.436,1.563A1.447,1.447,0,0,1,562.9,385.383Zm2.389,0c0-.654-.322-1.176-.953-1.176s-.96.522-.96,1.176.328,1.172.96,1.172S565.292,386.039,565.292,385.383Z"
                    transform="translate(-547.678 -382.759)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_299"
                data-name="Group 299"
                transform="translate(409.385 497.956)"
              >
                <g id="Group_298" data-name="Group 298">
                  <path
                    id="Path_379"
                    data-name="Path 379"
                    d="M439.82,316.709l2.458-3.16v-.016h-2.314V313.1h2.952v.4l-2.492,3.222v.017h2.59v.466H439.82Z"
                    transform="translate(-439.82 -313.096)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_380"
                    data-name="Path 380"
                    d="M445.454,316.837h0a1.2,1.2,0,0,1-.989.47c-.563,0-1-.263-1-.827,0-.7.552-.845,1.292-.96.42-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.74.655h-.448c.016-.575.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.241.057.373.316.316h.016v.338a.611.611,0,0,1-.252.046A.456.456,0,0,1,445.454,316.837Zm.012-.684v-.5a2.136,2.136,0,0,1-.678.218c-.465.085-.839.166-.839.6,0,.4.31.482.616.482A.837.837,0,0,0,445.467,316.153Z"
                    transform="translate(-439.973 -313.143)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_381"
                    data-name="Path 381"
                    d="M446.529,315.767a1.447,1.447,0,0,1,1.436-1.562,1.193,1.193,0,0,1,1.269,1.035h-.471a.742.742,0,0,0-.8-.649c-.632,0-.96.522-.96,1.177s.328,1.173.96,1.173a.745.745,0,0,0,.809-.736h.46a1.161,1.161,0,0,1-1.269,1.126A1.447,1.447,0,0,1,446.529,315.767Z"
                    transform="translate(-440.102 -313.143)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_382"
                    data-name="Path 382"
                    d="M451.689,316.837h-.006a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.845,1.293-.96.419-.062.712-.1.712-.424s-.24-.523-.667-.523c-.483,0-.724.207-.74.655h-.448c.017-.575.4-1,1.183-1,.632,0,1.1.235,1.1.942v1.413c0,.241.057.373.316.316h.017v.338a.62.62,0,0,1-.253.046A.456.456,0,0,1,451.689,316.837Zm.012-.684v-.5a2.151,2.151,0,0,1-.679.218c-.465.085-.838.166-.838.6,0,.4.31.482.615.482A.837.837,0,0,0,451.7,316.153Z"
                    transform="translate(-440.236 -313.143)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_383"
                    data-name="Path 383"
                    d="M452.678,314.242h.431v-.936h.466v.936h.574v.379h-.574v1.93c0,.207.109.275.3.275a.883.883,0,0,0,.247-.039h.018v.4a1.758,1.758,0,0,1-.374.04c-.38,0-.655-.155-.655-.574v-2.034h-.431Z"
                    transform="translate(-440.361 -313.105)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_384"
                    data-name="Path 384"
                    d="M454.514,315.767a1.449,1.449,0,0,1,1.407-1.562c.891,0,1.368.69,1.368,1.672h-2.3c.034.6.362,1.063.96,1.063a.771.771,0,0,0,.822-.609h.453a1.208,1.208,0,0,1-1.275,1A1.435,1.435,0,0,1,454.514,315.767Zm.489-.24h1.786c-.028-.592-.333-.954-.867-.954C455.37,314.573,455.071,314.987,455,315.527Z"
                    transform="translate(-440.439 -313.143)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_385"
                    data-name="Path 385"
                    d="M457.712,315.767a1.448,1.448,0,0,1,1.436-1.562,1.194,1.194,0,0,1,1.27,1.035h-.472a.742.742,0,0,0-.8-.649c-.632,0-.96.522-.96,1.177s.328,1.173.96,1.173a.745.745,0,0,0,.81-.736h.46a1.162,1.162,0,0,1-1.27,1.126A1.448,1.448,0,0,1,457.712,315.767Z"
                    transform="translate(-440.573 -313.143)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_386"
                    data-name="Path 386"
                    d="M462.872,316.837h0a1.2,1.2,0,0,1-.989.47c-.563,0-1.006-.263-1.006-.827,0-.7.553-.845,1.293-.96.42-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.655h-.448c.016-.575.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.241.056.373.316.316h.016v.338a.611.611,0,0,1-.252.046A.456.456,0,0,1,462.872,316.837Zm.012-.684v-.5a2.136,2.136,0,0,1-.678.218c-.465.085-.839.166-.839.6,0,.4.31.482.616.482A.837.837,0,0,0,462.885,316.153Z"
                    transform="translate(-440.706 -313.143)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_387"
                    data-name="Path 387"
                    d="M463.921,316.285h.477c.063.517.408.66.851.66.488,0,.689-.212.689-.488,0-.333-.229-.42-.776-.534-.585-.121-1.108-.241-1.108-.891,0-.493.38-.815,1.068-.815a1.013,1.013,0,0,1,1.167.931h-.477c-.046-.391-.259-.563-.7-.563-.425,0-.6.19-.6.425,0,.322.286.384.8.489.6.121,1.1.252,1.1.936,0,.6-.489.9-1.172.9C464.421,317.337,463.967,316.951,463.921,316.285Z"
                    transform="translate(-440.835 -313.143)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_301"
                data-name="Group 301"
                transform="translate(371.166 530.098)"
              >
                <g id="Group_300" data-name="Group 300">
                  <path
                    id="Path_388"
                    data-name="Path 388"
                    d="M399.922,346.651h.546l1.873,2.855c.138.214.3.53.3.53h.011v-3.385h.483v4.108h-.528l-1.884-2.861c-.132-.2-.3-.512-.3-.512H400.4v3.373h-.483Z"
                    transform="translate(-399.922 -346.651)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_389"
                    data-name="Path 389"
                    d="M405.821,350.393h-.006a1.194,1.194,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.844,1.293-.96.419-.063.712-.1.712-.425s-.24-.521-.666-.521c-.483,0-.724.2-.741.654h-.448c.017-.574.4-1.006,1.184-1.006.631,0,1.1.235,1.1.943v1.412c0,.241.057.375.316.316h.017v.339a.634.634,0,0,1-.253.047A.457.457,0,0,1,405.821,350.393Zm.012-.684v-.5a2.171,2.171,0,0,1-.678.217c-.466.086-.839.167-.839.6,0,.4.31.483.615.483A.838.838,0,0,0,405.834,349.709Z"
                    transform="translate(-400.086 -346.698)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_390"
                    data-name="Path 390"
                    d="M406.955,351.728v-.413h.022a.67.67,0,0,0,.316.062c.2,0,.328-.075.443-.362l.115-.292-1.161-2.884h.507l.7,1.851c.092.246.2.58.2.58h.012s.1-.333.2-.58l.677-1.851h.494l-1.27,3.211c-.241.615-.47.741-.832.741A1.022,1.022,0,0,1,406.955,351.728Z"
                    transform="translate(-400.207 -346.701)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_391"
                    data-name="Path 391"
                    d="M411.665,350.393h-.006a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.844,1.293-.96.419-.063.712-.1.712-.425s-.24-.521-.667-.521c-.482,0-.723.2-.74.654h-.448c.017-.574.4-1.006,1.184-1.006.631,0,1.1.235,1.1.943v1.412c0,.241.057.375.316.316h.017v.339a.634.634,0,0,1-.253.047A.457.457,0,0,1,411.665,350.393Zm.012-.684v-.5a2.179,2.179,0,0,1-.678.217c-.466.086-.839.167-.839.6,0,.4.31.483.615.483A.838.838,0,0,0,411.678,349.709Z"
                    transform="translate(-400.332 -346.698)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_392"
                    data-name="Path 392"
                    d="M413.377,348.332h.011a1.057,1.057,0,0,1,.885-.528.449.449,0,0,1,.19.034v.449h-.017a.524.524,0,0,0-.207-.031.857.857,0,0,0-.862.9v1.65h-.465v-2.97h.465Z"
                    transform="translate(-400.469 -346.7)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_393"
                    data-name="Path 393"
                    d="M414.971,346.651h.465v.575h-.465Zm0,1.138h.465v2.97h-.465Z"
                    transform="translate(-400.556 -346.651)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_394"
                    data-name="Path 394"
                    d="M415.918,347.8h.431v-.936h.465v.936h.575v.379h-.575v1.93c0,.207.11.276.3.276a.84.84,0,0,0,.247-.041h.017v.4a1.744,1.744,0,0,1-.373.041c-.38,0-.655-.155-.655-.575v-2.034h-.431Z"
                    transform="translate(-400.595 -346.66)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_303"
                data-name="Group 303"
                transform="translate(419.945 560.362)"
              >
                <g id="Group_302" data-name="Group 302">
                  <path
                    id="Path_395"
                    data-name="Path 395"
                    d="M450.844,381.078v-.057h.512v.04c0,.729.23.947.713.947.413,0,.654-.213.654-.73v-3.033h.517v3.067a1.062,1.062,0,0,1-1.189,1.121C451.309,382.433,450.844,382.031,450.844,381.078Z"
                    transform="translate(-450.844 -378.245)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_396"
                    data-name="Path 396"
                    d="M455.837,381.987h-.006a1.194,1.194,0,0,1-.988.47c-.563,0-1.006-.264-1.006-.827,0-.7.553-.845,1.293-.96.419-.063.712-.1.712-.425s-.241-.523-.666-.523c-.483,0-.724.207-.741.655h-.447c.016-.575.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.241.057.374.317.316h.016v.339a.631.631,0,0,1-.253.046A.456.456,0,0,1,455.837,381.987Zm.012-.684V380.8a2.137,2.137,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.31.482.616.482A.837.837,0,0,0,455.85,381.3Z"
                    transform="translate(-450.97 -378.292)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_397"
                    data-name="Path 397"
                    d="M457.1,378.245h.466v4.108H457.1Z"
                    transform="translate(-451.107 -378.245)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_398"
                    data-name="Path 398"
                    d="M458.266,378.245h.465v.575h-.465Zm0,1.138h.465v2.969h-.465Z"
                    transform="translate(-451.156 -378.245)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_399"
                    data-name="Path 399"
                    d="M459.214,381.434h.477c.063.517.408.661.851.661.488,0,.689-.213.689-.489,0-.333-.229-.419-.776-.534-.585-.121-1.108-.241-1.108-.891,0-.494.379-.816,1.068-.816a1.013,1.013,0,0,1,1.167.931H461.1c-.046-.39-.259-.563-.7-.563-.425,0-.6.19-.6.425,0,.322.287.385.8.488.6.121,1.1.253,1.1.937,0,.6-.489.9-1.172.9C459.714,382.486,459.26,382.1,459.214,381.434Z"
                    transform="translate(-451.196 -378.292)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_400"
                    data-name="Path 400"
                    d="M462.166,380.916a1.447,1.447,0,0,1,1.436-1.561,1.193,1.193,0,0,1,1.27,1.034H464.4a.742.742,0,0,0-.8-.649c-.632,0-.959.522-.959,1.176s.327,1.172.959,1.172a.746.746,0,0,0,.81-.736h.46a1.161,1.161,0,0,1-1.27,1.126A1.448,1.448,0,0,1,462.166,380.916Z"
                    transform="translate(-451.321 -378.292)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_401"
                    data-name="Path 401"
                    d="M465.281,380.916a1.438,1.438,0,1,1,1.436,1.563A1.446,1.446,0,0,1,465.281,380.916Zm2.389,0c0-.654-.322-1.176-.953-1.176s-.96.522-.96,1.176.328,1.172.96,1.172S467.67,381.572,467.67,380.916Z"
                    transform="translate(-451.452 -378.292)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_305"
                data-name="Group 305"
                transform="translate(393.75 600.051)"
              >
                <g id="Group_304" data-name="Group 304">
                  <path
                    id="Path_402"
                    data-name="Path 402"
                    d="M423.5,421.821a1.949,1.949,0,0,1,1.947-2.143,1.522,1.522,0,0,1,1.649,1.31h-.506a1.07,1.07,0,0,0-1.154-.862c-.9,0-1.407.729-1.407,1.695,0,1.006.557,1.683,1.413,1.683a1.139,1.139,0,0,0,1.195-1.132h.511a1.745,1.745,0,0,1-.494,1.155,1.7,1.7,0,0,1-1.229.431A1.939,1.939,0,0,1,423.5,421.821Z"
                    transform="translate(-423.498 -419.678)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_403"
                    data-name="Path 403"
                    d="M427.668,422.446a1.439,1.439,0,1,1,1.436,1.562A1.447,1.447,0,0,1,427.668,422.446Zm2.39,0c0-.655-.322-1.178-.954-1.178s-.96.523-.96,1.178.328,1.172.96,1.172S430.058,423.1,430.058,422.446Z"
                    transform="translate(-423.673 -419.729)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_404"
                    data-name="Path 404"
                    d="M431.165,419.774h.467v4.107h-.467Z"
                    transform="translate(-423.821 -419.682)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_405"
                    data-name="Path 405"
                    d="M432.331,419.774h.465v.575h-.465Zm0,1.138h.465v2.969h-.465Z"
                    transform="translate(-423.87 -419.682)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_406"
                    data-name="Path 406"
                    d="M433.476,420.959h.465v.424h.012a1.044,1.044,0,0,1,.874-.493.781.781,0,0,1,.776.528h.01a1.042,1.042,0,0,1,.908-.528.861.861,0,0,1,.9.953v2.085h-.465v-2.062a.543.543,0,0,0-.546-.586.763.763,0,0,0-.729.8v1.85h-.467v-2.062c0-.368-.206-.586-.511-.586a.807.807,0,0,0-.764.8v1.85h-.465Z"
                    transform="translate(-423.918 -419.729)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_407"
                    data-name="Path 407"
                    d="M440.052,423.515h-.006a1.192,1.192,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.419-.064.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.206-.741.654H438.2c.017-.574.4-1.006,1.184-1.006.631,0,1.1.237,1.1.943v1.413c0,.241.057.374.316.316h.017v.339a.655.655,0,0,1-.253.046A.457.457,0,0,1,440.052,423.515Zm.012-.684v-.5a2.173,2.173,0,0,1-.678.217c-.466.086-.839.167-.839.6,0,.4.31.483.615.483A.838.838,0,0,0,440.065,422.831Z"
                    transform="translate(-424.111 -419.729)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_307"
                data-name="Group 307"
                transform="translate(444.064 597.207)"
              >
                <g id="Group_306" data-name="Group 306">
                  <path
                    id="Path_408"
                    data-name="Path 408"
                    d="M476.023,416.77h.662l1.331,3.411h.011l1.322-3.411h.672v4.107h-.493V417.5h-.011s-.109.321-.185.516l-1.125,2.861h-.4l-1.126-2.861c-.08-.206-.185-.516-.185-.516h-.011v3.378h-.466Z"
                    transform="translate(-476.023 -416.712)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_409"
                    data-name="Path 409"
                    d="M480.932,416.77h.465v.574h-.465Zm0,1.137h.465v2.97h-.465Z"
                    transform="translate(-476.23 -416.712)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_410"
                    data-name="Path 410"
                    d="M481.9,419.441a1.447,1.447,0,0,1,1.436-1.562,1.192,1.192,0,0,1,1.269,1.034h-.471a.742.742,0,0,0-.8-.649c-.632,0-.96.523-.96,1.178s.328,1.172.96,1.172a.745.745,0,0,0,.81-.735h.459A1.16,1.16,0,0,1,483.34,421,1.447,1.447,0,0,1,481.9,419.441Z"
                    transform="translate(-476.271 -416.758)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_411"
                    data-name="Path 411"
                    d="M485.658,418.3h.01a1.116,1.116,0,0,1,.943-.459.892.892,0,0,1,1,.953v2.085h-.466v-2.045c0-.384-.253-.6-.672-.6a.786.786,0,0,0-.815.822v1.827h-.466V416.77h.466Z"
                    transform="translate(-476.409 -416.712)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_412"
                    data-name="Path 412"
                    d="M488.186,419.441A1.439,1.439,0,1,1,489.623,421,1.447,1.447,0,0,1,488.186,419.441Zm2.39,0c0-.655-.322-1.178-.953-1.178s-.96.523-.96,1.178.327,1.172.96,1.172S490.576,420.1,490.576,419.441Z"
                    transform="translate(-476.535 -416.758)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_413"
                    data-name="Path 413"
                    d="M493.478,420.51h0a1.194,1.194,0,0,1-.989.471c-.563,0-1.006-.264-1.006-.828,0-.7.553-.844,1.293-.959.42-.063.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.654h-.447c.016-.574.4-1,1.183-1,.631,0,1.1.236,1.1.942v1.414c0,.24.056.373.316.316h.016v.338a.628.628,0,0,1-.252.046A.457.457,0,0,1,493.478,420.51Zm.013-.684v-.5a2.139,2.139,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.31.483.616.483A.837.837,0,0,0,493.491,419.826Z"
                    transform="translate(-476.674 -416.759)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_414"
                    data-name="Path 414"
                    d="M494.552,419.441a1.447,1.447,0,0,1,1.436-1.562,1.193,1.193,0,0,1,1.269,1.034h-.471a.743.743,0,0,0-.8-.649c-.632,0-.96.523-.96,1.178s.328,1.172.96,1.172a.745.745,0,0,0,.809-.735h.46A1.161,1.161,0,0,1,495.988,421,1.447,1.447,0,0,1,494.552,419.441Z"
                    transform="translate(-476.803 -416.758)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_415"
                    data-name="Path 415"
                    d="M499.712,420.46h-.006a1.193,1.193,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.292-.959.42-.063.713-.1.713-.424s-.24-.523-.667-.523c-.483,0-.724.207-.74.654h-.448c.017-.574.4-1,1.183-1,.632,0,1.1.236,1.1.942V420.2c0,.24.057.373.316.316h.017v.338a.637.637,0,0,1-.253.046A.457.457,0,0,1,499.712,420.46Zm.012-.684v-.5a2.139,2.139,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.311.483.616.483A.836.836,0,0,0,499.724,419.776Zm-.568-3.067h.574l-.471.73h-.4Z"
                    transform="translate(-476.936 -416.709)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_416"
                    data-name="Path 416"
                    d="M501.425,418.344h.011a1.114,1.114,0,0,1,.942-.459.892.892,0,0,1,1,.953v2.085h-.465v-2.045c0-.384-.254-.6-.672-.6a.787.787,0,0,0-.816.822v1.827h-.465v-2.97h.465Z"
                    transform="translate(-477.073 -416.758)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_309"
                data-name="Group 309"
                transform="translate(485.817 641.63)"
              >
                <g id="Group_308" data-name="Group 308">
                  <path
                    id="Path_417"
                    data-name="Path 417"
                    d="M522.835,466.737h-.013a1.494,1.494,0,0,1-1.281.626,2.148,2.148,0,0,1,.046-4.279,1.538,1.538,0,0,1,1.672,1.321h-.534a1.056,1.056,0,0,0-1.148-.873c-.988,0-1.436.74-1.436,1.705a1.487,1.487,0,0,0,1.418,1.677,1.166,1.166,0,0,0,1.242-1.263v-.1h-1.236v-.448h1.752v2.172h-.332Z"
                    transform="translate(-519.612 -463.085)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_418"
                    data-name="Path 418"
                    d="M526.043,467.331v-.408h-.011a1.029,1.029,0,0,1-.9.477.919.919,0,0,1-1-.982v-2.057h.466v2.045a.585.585,0,0,0,.666.609.768.768,0,0,0,.776-.828v-1.827h.465v2.969Z"
                    transform="translate(-519.802 -463.139)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_419"
                    data-name="Path 419"
                    d="M527.1,465.846a1.448,1.448,0,0,1,1.406-1.562c.891,0,1.368.69,1.368,1.672h-2.3c.034.6.362,1.063.96,1.063a.77.77,0,0,0,.821-.609h.454a1.209,1.209,0,0,1-1.275,1A1.433,1.433,0,0,1,527.1,465.846Zm.488-.241h1.786c-.029-.591-.333-.953-.868-.953C527.956,464.652,527.658,465.066,527.589,465.6Z"
                    transform="translate(-519.927 -463.135)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_420"
                    data-name="Path 420"
                    d="M530.937,464.855h.012a1.053,1.053,0,0,1,.885-.529.454.454,0,0,1,.19.035v.448H532a.56.56,0,0,0-.207-.029.856.856,0,0,0-.861.9v1.648h-.465V464.36h.465Z"
                    transform="translate(-520.069 -463.137)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_421"
                    data-name="Path 421"
                    d="M533,464.855h.011a1.056,1.056,0,0,1,.885-.529.448.448,0,0,1,.19.035v.448h-.017a.56.56,0,0,0-.207-.029.856.856,0,0,0-.861.9v1.648h-.466V464.36H533Z"
                    transform="translate(-520.156 -463.137)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_422"
                    data-name="Path 422"
                    d="M534.294,465.846a1.449,1.449,0,0,1,1.407-1.562c.89,0,1.367.69,1.367,1.672h-2.3c.034.6.362,1.063.96,1.063a.77.77,0,0,0,.821-.609H537a1.209,1.209,0,0,1-1.275,1A1.434,1.434,0,0,1,534.294,465.846Zm.488-.241h1.786c-.028-.591-.333-.953-.867-.953C535.149,464.652,534.851,465.066,534.782,465.6Z"
                    transform="translate(-520.23 -463.135)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_423"
                    data-name="Path 423"
                    d="M538.131,464.855h.012a1.056,1.056,0,0,1,.885-.529.448.448,0,0,1,.19.035v.448H539.2a.567.567,0,0,0-.207-.029.856.856,0,0,0-.862.9v1.648h-.465V464.36h.465Z"
                    transform="translate(-520.372 -463.137)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_424"
                    data-name="Path 424"
                    d="M539.43,465.846a1.438,1.438,0,1,1,1.436,1.562A1.447,1.447,0,0,1,539.43,465.846Zm2.389,0c0-.655-.322-1.177-.953-1.177s-.96.522-.96,1.177.328,1.172.96,1.172S541.819,466.5,541.819,465.846Z"
                    transform="translate(-520.446 -463.135)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_311"
                data-name="Group 311"
                transform="translate(540.626 604.928)"
              >
                <g
                  id="Group_310"
                  data-name="Group 310"
                  transform="translate(0)"
                >
                  <path
                    id="Path_425"
                    data-name="Path 425"
                    d="M576.829,424.77h1.712a1.224,1.224,0,0,1,.977.4,1.193,1.193,0,0,1,.276.782c0,.752-.431,1.207-1.328,1.207h-1.12v1.723h-.517Zm.517,1.964h1.081c.579,0,.855-.292.855-.763,0-.494-.3-.753-.828-.753h-1.108Z"
                    transform="translate(-576.829 -424.77)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_426"
                    data-name="Path 426"
                    d="M582.265,428.927v-.408h-.011a1.031,1.031,0,0,1-.9.476.92.92,0,0,1-1-.982v-2.057h.467V428a.585.585,0,0,0,.666.609.769.769,0,0,0,.776-.828v-1.827h.465v2.97Z"
                    transform="translate(-576.978 -424.82)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_427"
                    data-name="Path 427"
                    d="M583.322,427.441a1.448,1.448,0,0,1,1.407-1.562c.891,0,1.368.69,1.368,1.672h-2.3c.034.6.362,1.062.96,1.062a.771.771,0,0,0,.822-.608h.453a1.209,1.209,0,0,1-1.275,1A1.435,1.435,0,0,1,583.322,427.441Zm.488-.24H585.6c-.028-.592-.332-.954-.867-.954C584.178,426.247,583.879,426.66,583.811,427.2Z"
                    transform="translate(-577.102 -424.817)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_428"
                    data-name="Path 428"
                    d="M587.169,428.486h-.01v.392h-.466V424.77h.466v1.561h.01a1.123,1.123,0,0,1,.954-.493c.753,0,1.265.574,1.265,1.563,0,.952-.477,1.562-1.282,1.562A1.066,1.066,0,0,1,587.169,428.486Zm1.742-1.079c0-.668-.27-1.172-.833-1.172-.649,0-.949.529-.949,1.172,0,.666.311,1.165.943,1.165C588.641,428.572,588.911,428.079,588.911,427.407Z"
                    transform="translate(-577.244 -424.77)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_429"
                    data-name="Path 429"
                    d="M590.011,424.77h.466v4.108h-.466Z"
                    transform="translate(-577.384 -424.77)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_430"
                    data-name="Path 430"
                    d="M592.988,428.511h-.006a1.194,1.194,0,0,1-.987.47c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.418-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.742.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.235,1.1.941v1.414c0,.24.058.373.316.316h.017v.338a.621.621,0,0,1-.253.046A.457.457,0,0,1,592.988,428.511Zm.013-.685v-.5a2.171,2.171,0,0,1-.678.218c-.466.086-.839.167-.839.6,0,.4.31.483.615.483A.837.837,0,0,0,593,427.826Z"
                    transform="translate(-577.425 -424.817)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_313"
                data-name="Group 313"
                transform="translate(532.798 591.678)"
              >
                <g
                  id="Group_312"
                  data-name="Group 312"
                  transform="translate(0)"
                >
                  <path
                    id="Path_431"
                    data-name="Path 431"
                    d="M568.657,410.937h3.252v.453h-1.368v3.654h-.516V411.39h-1.368Z"
                    transform="translate(-568.657 -410.937)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_432"
                    data-name="Path 432"
                    d="M572.533,410.937H573v4.107h-.466Z"
                    transform="translate(-568.82 -410.937)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_433"
                    data-name="Path 433"
                    d="M575.51,414.678H575.5a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.845,1.292-.96.419-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.74.654h-.448c.017-.574.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.24.057.373.316.316h.016v.338a.613.613,0,0,1-.252.046A.455.455,0,0,1,575.51,414.678Zm.012-.684v-.5a2.163,2.163,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.311.482.616.482A.837.837,0,0,0,575.522,413.994Z"
                    transform="translate(-568.861 -410.984)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_434"
                    data-name="Path 434"
                    d="M577.583,413.538l-.988-1.414h.539l.707,1.08h.012l.736-1.08h.511l-.977,1.361,1.161,1.609h-.545l-.879-1.275h-.011l-.861,1.275h-.523Z"
                    transform="translate(-568.986 -410.987)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_435"
                    data-name="Path 435"
                    d="M579.392,413.608a1.447,1.447,0,0,1,1.436-1.562,1.193,1.193,0,0,1,1.269,1.035h-.471a.743.743,0,0,0-.8-.649c-.633,0-.96.522-.96,1.177s.327,1.171.96,1.171a.745.745,0,0,0,.809-.735h.46a1.161,1.161,0,0,1-1.269,1.126A1.447,1.447,0,0,1,579.392,413.608Z"
                    transform="translate(-569.109 -410.984)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_436"
                    data-name="Path 436"
                    d="M584.552,414.678h-.006a1.2,1.2,0,0,1-.988.47c-.562,0-1-.263-1-.827,0-.7.552-.845,1.292-.96.42-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.74.654H582.7c.017-.574.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.24.056.373.316.316h.016v.338a.612.612,0,0,1-.252.046A.455.455,0,0,1,584.552,414.678Zm.011-.684v-.5a2.161,2.161,0,0,1-.678.217c-.464.086-.839.167-.839.6,0,.4.311.482.616.482A.837.837,0,0,0,584.564,413.994Z"
                    transform="translate(-569.242 -410.984)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_437"
                    data-name="Path 437"
                    d="M585.816,410.937h.466v4.107h-.466Z"
                    transform="translate(-569.379 -410.937)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_438"
                    data-name="Path 438"
                    d="M588.794,414.678h-.006a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.845,1.292-.96.42-.062.713-.1.713-.424s-.24-.523-.667-.523c-.483,0-.724.207-.74.654h-.448c.017-.574.4-1,1.183-1,.632,0,1.1.235,1.1.942v1.413c0,.24.058.373.316.316h.017v.338a.621.621,0,0,1-.253.046A.456.456,0,0,1,588.794,414.678Zm.011-.684v-.5a2.16,2.16,0,0,1-.678.217c-.465.086-.838.167-.838.6,0,.4.31.482.615.482A.836.836,0,0,0,588.806,413.994Z"
                    transform="translate(-569.42 -410.984)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_315"
                data-name="Group 315"
                transform="translate(578.716 655.215)"
              >
                <g id="Group_314" data-name="Group 314">
                  <path
                    id="Path_439"
                    data-name="Path 439"
                    d="M616.593,479.41a2.014,2.014,0,1,1,2.01,2.142A1.975,1.975,0,0,1,616.593,479.41Zm3.491,0A1.495,1.495,0,1,0,618.6,481.1,1.5,1.5,0,0,0,620.084,479.41Z"
                    transform="translate(-616.593 -477.267)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_440"
                    data-name="Path 440"
                    d="M623.115,481.1h-.006a1.192,1.192,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.419-.064.712-.1.712-.425s-.24-.523-.666-.523c-.483,0-.724.207-.741.655h-.448c.017-.575.4-1.006,1.184-1.006.631,0,1.1.236,1.1.943v1.413c0,.241.057.374.316.316h.017v.339a.654.654,0,0,1-.253.046A.457.457,0,0,1,623.115,481.1Zm.013-.684v-.5a2.172,2.172,0,0,1-.678.218c-.466.086-.839.167-.839.6,0,.4.31.483.615.483A.838.838,0,0,0,623.128,480.42Z"
                    transform="translate(-616.783 -477.318)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_441"
                    data-name="Path 441"
                    d="M625.188,479.963l-.988-1.412h.539l.708,1.08h.011l.736-1.08h.511l-.976,1.361,1.161,1.607h-.546l-.879-1.275h-.011l-.862,1.275h-.523Z"
                    transform="translate(-616.908 -477.321)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_442"
                    data-name="Path 442"
                    d="M629.1,481.1h0a1.194,1.194,0,0,1-.989.471c-.563,0-1.006-.264-1.006-.828,0-.7.553-.844,1.293-.959.42-.064.712-.1.712-.425s-.24-.523-.666-.523c-.483,0-.724.207-.741.655h-.447c.016-.575.4-1.006,1.183-1.006.631,0,1.1.236,1.1.943v1.413c0,.241.057.374.316.316h.016v.339a.645.645,0,0,1-.252.046A.457.457,0,0,1,629.1,481.1Zm.012-.684v-.5a2.16,2.16,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.31.483.616.483A.837.837,0,0,0,629.116,480.42Z"
                    transform="translate(-617.036 -477.318)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_443"
                    data-name="Path 443"
                    d="M630.177,480.035a1.447,1.447,0,0,1,1.436-1.562,1.193,1.193,0,0,1,1.269,1.034h-.471a.743.743,0,0,0-.8-.649c-.632,0-.96.523-.96,1.178s.328,1.172.96,1.172a.745.745,0,0,0,.809-.735h.46a1.16,1.16,0,0,1-1.269,1.125A1.447,1.447,0,0,1,630.177,480.035Z"
                    transform="translate(-617.165 -477.318)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_444"
                    data-name="Path 444"
                    d="M635.337,481.1h-.006a1.193,1.193,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.292-.959.419-.064.713-.1.713-.425s-.24-.523-.667-.523c-.483,0-.724.207-.74.655h-.448c.017-.575.4-1.006,1.183-1.006.632,0,1.1.236,1.1.943v1.413c0,.241.057.374.316.316h.017v.339a.655.655,0,0,1-.253.046A.457.457,0,0,1,635.337,481.1Zm.012-.684v-.5a2.163,2.163,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.311.483.616.483A.837.837,0,0,0,635.349,480.42Z"
                    transform="translate(-617.298 -477.318)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_317"
                data-name="Group 317"
                transform="translate(604.987 628.886)"
              >
                <g id="Group_316" data-name="Group 316">
                  <path
                    id="Path_445"
                    data-name="Path 445"
                    d="M644.019,449.781h.558l1.011,3.057c.063.189.161.551.161.551h.011s.1-.368.161-.551l1-3.057h.54l-1.443,4.107h-.568Z"
                    transform="translate(-644.019 -449.781)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_446"
                    data-name="Path 446"
                    d="M647.44,452.453a1.449,1.449,0,0,1,1.407-1.562c.891,0,1.368.689,1.368,1.672h-2.3c.035.6.362,1.062.96,1.062a.771.771,0,0,0,.822-.609h.453a1.207,1.207,0,0,1-1.275,1A1.434,1.434,0,0,1,647.44,452.453Zm.489-.241h1.786c-.028-.592-.332-.954-.867-.954C648.3,451.258,648,451.672,647.929,452.212Z"
                    transform="translate(-644.163 -449.828)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_447"
                    data-name="Path 447"
                    d="M651.276,451.461h.012a1.054,1.054,0,0,1,.884-.528.441.441,0,0,1,.191.034v.448h-.018a.537.537,0,0,0-.207-.03.857.857,0,0,0-.861.9v1.648h-.466v-2.969h.466Z"
                    transform="translate(-644.305 -449.83)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_448"
                    data-name="Path 448"
                    d="M654.562,453.522h-.006a1.191,1.191,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.553-.844,1.293-.959.419-.064.712-.1.712-.425s-.24-.523-.666-.523c-.483,0-.724.207-.741.655h-.447c.016-.575.4-1.006,1.183-1.006.631,0,1.1.236,1.1.943v1.413c0,.241.056.374.316.316h.016v.339a.645.645,0,0,1-.252.046A.457.457,0,0,1,654.562,453.522Zm.012-.684v-.5a2.164,2.164,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.31.483.616.483A.837.837,0,0,0,654.575,452.838Z"
                    transform="translate(-644.379 -449.828)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_449"
                    data-name="Path 449"
                    d="M655.636,452.453a1.447,1.447,0,0,1,1.436-1.562,1.193,1.193,0,0,1,1.269,1.034h-.471a.743.743,0,0,0-.8-.65c-.632,0-.96.523-.96,1.178s.328,1.172.96,1.172a.744.744,0,0,0,.809-.735h.46a1.16,1.16,0,0,1-1.269,1.125A1.446,1.446,0,0,1,655.636,452.453Z"
                    transform="translate(-644.508 -449.828)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_450"
                    data-name="Path 450"
                    d="M659.389,451.461h.012a1.055,1.055,0,0,1,.885-.528.436.436,0,0,1,.19.034v.448h-.018a.534.534,0,0,0-.206-.03.857.857,0,0,0-.862.9v1.648h-.465v-2.969h.465Z"
                    transform="translate(-644.646 -449.83)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_451"
                    data-name="Path 451"
                    d="M662.87,453.938v-.407h-.011a1.031,1.031,0,0,1-.9.477.919.919,0,0,1-1-.983v-2.056h.466v2.045a.584.584,0,0,0,.666.608.768.768,0,0,0,.776-.828v-1.826h.465v2.969Z"
                    transform="translate(-644.732 -449.831)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_452"
                    data-name="Path 452"
                    d="M663.976,453.571l1.653-2.177v-.016h-1.557v-.408h2.206v.275l-1.706,2.251v.018h1.769v.424h-2.366Z"
                    transform="translate(-644.859 -449.831)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_319"
                data-name="Group 319"
                transform="translate(676.045 654.366)"
              >
                <g
                  id="Group_318"
                  data-name="Group 318"
                  transform="translate(0)"
                >
                  <path
                    id="Path_453"
                    data-name="Path 453"
                    d="M718.2,478.523a1.949,1.949,0,0,1,1.946-2.143,1.523,1.523,0,0,1,1.649,1.31h-.506a1.071,1.071,0,0,0-1.154-.862c-.9,0-1.407.729-1.407,1.694,0,1.006.557,1.683,1.413,1.683a1.139,1.139,0,0,0,1.195-1.132h.512a1.744,1.744,0,0,1-.494,1.155,1.7,1.7,0,0,1-1.229.431A1.938,1.938,0,0,1,718.2,478.523Z"
                    transform="translate(-718.2 -476.38)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_454"
                    data-name="Path 454"
                    d="M723.009,478h.012a1.114,1.114,0,0,1,.942-.459.892.892,0,0,1,1,.953v2.085H724.5v-2.045c0-.384-.254-.6-.673-.6a.787.787,0,0,0-.816.822v1.827h-.465v-4.107h.465Z"
                    transform="translate(-718.383 -476.384)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_455"
                    data-name="Path 455"
                    d="M725.712,476.477h.465v.574h-.465Zm0,1.137h.465v2.97h-.465Z"
                    transform="translate(-718.516 -476.384)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_456"
                    data-name="Path 456"
                    d="M728.671,480.217h-.006a1.193,1.193,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.292-.959.42-.063.713-.1.713-.424s-.241-.523-.667-.523c-.483,0-.724.207-.74.654h-.448c.017-.574.4-1,1.183-1,.632,0,1.1.236,1.1.942v1.414c0,.241.057.373.316.316h.017v.338a.637.637,0,0,1-.253.046A.457.457,0,0,1,728.671,480.217Zm.012-.684v-.5a2.137,2.137,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.311.483.616.483A.837.837,0,0,0,728.683,479.533Z"
                    transform="translate(-718.557 -476.432)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_457"
                    data-name="Path 457"
                    d="M729.918,477.661h.465v.42h.011a1.114,1.114,0,0,1,.954-.489c.752,0,1.275.574,1.275,1.562,0,.953-.489,1.562-1.292,1.562a1.078,1.078,0,0,1-.937-.437h-.011v1.3h-.465Zm2.228,1.5c0-.667-.282-1.171-.844-1.171-.648,0-.948.529-.948,1.171s.26,1.166.942,1.166C731.865,480.326,732.146,479.833,732.146,479.16Z"
                    transform="translate(-718.693 -476.431)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_458"
                    data-name="Path 458"
                    d="M735.042,480.217h-.006a1.194,1.194,0,0,1-.988.471c-.563,0-1.006-.264-1.006-.828,0-.7.552-.844,1.293-.959.419-.063.712-.1.712-.424s-.24-.523-.667-.523c-.483,0-.723.207-.741.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.942v1.414c0,.241.057.373.316.316h.017v.338a.637.637,0,0,1-.253.046A.457.457,0,0,1,735.042,480.217Zm.012-.684v-.5a2.154,2.154,0,0,1-.678.217c-.466.086-.839.167-.839.6,0,.4.31.483.615.483A.837.837,0,0,0,735.055,479.533Z"
                    transform="translate(-718.825 -476.432)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_459"
                    data-name="Path 459"
                    d="M736.092,479.666h.477c.062.516.408.66.85.66.489,0,.689-.212.689-.488,0-.333-.229-.42-.775-.535-.586-.121-1.109-.241-1.109-.891,0-.493.38-.815,1.069-.815a1.013,1.013,0,0,1,1.166.93h-.477c-.045-.391-.258-.563-.7-.563-.424,0-.6.191-.6.425,0,.322.287.385.8.489.6.121,1.1.253,1.1.936,0,.6-.488.9-1.172.9C736.591,480.717,736.138,480.332,736.092,479.666Z"
                    transform="translate(-718.953 -476.431)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_321"
                data-name="Group 321"
                transform="translate(720.814 610.793)"
              >
                <g
                  id="Group_320"
                  data-name="Group 320"
                  transform="translate(0)"
                >
                  <path
                    id="Path_460"
                    data-name="Path 460"
                    d="M764.936,433.034a1.948,1.948,0,0,1,1.947-2.142,1.521,1.521,0,0,1,1.648,1.31h-.506a1.07,1.07,0,0,0-1.154-.862c-.9,0-1.407.73-1.407,1.695,0,1.006.556,1.684,1.413,1.684a1.139,1.139,0,0,0,1.195-1.132h.512a1.745,1.745,0,0,1-.494,1.154,1.7,1.7,0,0,1-1.229.431A1.938,1.938,0,0,1,764.936,433.034Z"
                    transform="translate(-764.936 -430.892)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_461"
                    data-name="Path 461"
                    d="M771.093,434.729h-.006a1.2,1.2,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.845,1.292-.96.42-.063.712-.1.712-.425s-.24-.522-.666-.522c-.483,0-.724.206-.74.654h-.448c.017-.574.4-1,1.183-1,.631,0,1.1.235,1.1.943v1.412c0,.241.057.374.316.316h.016v.339a.628.628,0,0,1-.252.046A.455.455,0,0,1,771.093,434.729Zm.011-.684v-.5a2.162,2.162,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.311.482.616.482A.837.837,0,0,0,771.1,434.045Z"
                    transform="translate(-765.111 -430.943)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_462"
                    data-name="Path 462"
                    d="M772.34,432.172h.465v.424h.011a1.041,1.041,0,0,1,.874-.493.782.782,0,0,1,.776.528h.011a1.042,1.042,0,0,1,.908-.528.863.863,0,0,1,.9.954v2.084h-.465v-2.062a.542.542,0,0,0-.546-.585.761.761,0,0,0-.729.8v1.85h-.467v-2.062a.514.514,0,0,0-.511-.585.805.805,0,0,0-.764.8v1.85h-.465Z"
                    transform="translate(-765.248 -430.943)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_463"
                    data-name="Path 463"
                    d="M777.1,432.172h.466v.419h.011a1.116,1.116,0,0,1,.954-.488c.752,0,1.275.574,1.275,1.562,0,.954-.489,1.562-1.293,1.562a1.075,1.075,0,0,1-.936-.437h-.011v1.3H777.1Zm2.229,1.5c0-.667-.282-1.172-.844-1.172-.649,0-.948.528-.948,1.172s.259,1.166.942,1.166C779.051,434.837,779.333,434.343,779.333,433.671Z"
                    transform="translate(-765.448 -430.943)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_464"
                    data-name="Path 464"
                    d="M780.242,433.659a1.448,1.448,0,0,1,1.407-1.562c.891,0,1.368.689,1.368,1.672h-2.3c.034.6.362,1.062.96,1.062a.771.771,0,0,0,.822-.609h.453a1.208,1.208,0,0,1-1.275,1A1.434,1.434,0,0,1,780.242,433.659Zm.489-.24h1.786c-.028-.593-.333-.954-.867-.954C781.1,432.465,780.8,432.878,780.731,433.419Z"
                    transform="translate(-765.58 -430.943)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_465"
                    data-name="Path 465"
                    d="M783.44,433.659a1.447,1.447,0,0,1,1.436-1.562,1.194,1.194,0,0,1,1.27,1.035h-.472a.742.742,0,0,0-.8-.649c-.632,0-.96.522-.96,1.177s.328,1.172.96,1.172a.745.745,0,0,0,.81-.735h.46a1.161,1.161,0,0,1-1.27,1.126A1.447,1.447,0,0,1,783.44,433.659Z"
                    transform="translate(-765.715 -430.943)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_466"
                    data-name="Path 466"
                    d="M787.194,432.516h.012a1.113,1.113,0,0,1,.942-.46.893.893,0,0,1,1,.954v2.084h-.465V433.05c0-.386-.254-.6-.673-.6a.785.785,0,0,0-.815.821v1.827h-.466v-4.107h.466Z"
                    transform="translate(-765.853 -430.896)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_467"
                    data-name="Path 467"
                    d="M789.723,433.659a1.448,1.448,0,0,1,1.406-1.562c.891,0,1.368.689,1.368,1.672h-2.3c.034.6.362,1.062.96,1.062a.77.77,0,0,0,.821-.609h.454a1.209,1.209,0,0,1-1.275,1A1.434,1.434,0,0,1,789.723,433.659Zm.488-.24H792c-.029-.593-.333-.954-.868-.954C790.578,432.465,790.28,432.878,790.211,433.419Z"
                    transform="translate(-765.979 -430.943)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_323"
                data-name="Group 323"
                transform="translate(763.711 589.066)"
              >
                <g
                  id="Group_322"
                  data-name="Group 322"
                  transform="translate(0)"
                >
                  <path
                    id="Path_468"
                    data-name="Path 468"
                    d="M812.746,412.248a2.066,2.066,0,0,1-1.017.247,2.147,2.147,0,1,1,2.011-2.144,2.194,2.194,0,0,1-.638,1.631l.546.438-.264.332Zm-.4-.322-.414-.333.264-.332.523.419a1.789,1.789,0,0,0,.493-1.328,1.495,1.495,0,1,0-1.482,1.69A1.586,1.586,0,0,0,812.344,411.927Z"
                    transform="translate(-809.719 -408.21)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_469"
                    data-name="Path 469"
                    d="M816.377,412.463v-.408h-.012a1.029,1.029,0,0,1-.9.476.919.919,0,0,1-1-.982v-2.057h.466v2.045a.585.585,0,0,0,.666.609.768.768,0,0,0,.776-.828v-1.827h.465v2.97Z"
                    transform="translate(-809.919 -408.264)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_470"
                    data-name="Path 470"
                    d="M817.608,408.306h.466v.574h-.466Zm0,1.137h.466v2.97h-.466Z"
                    transform="translate(-810.051 -408.214)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_471"
                    data-name="Path 471"
                    d="M819.219,409.881h.012a1.115,1.115,0,0,1,.942-.46.892.892,0,0,1,1,.954v2.085h-.465v-2.045c0-.385-.254-.6-.672-.6a.787.787,0,0,0-.816.822v1.827h-.465v-2.97h.465Z"
                    transform="translate(-810.099 -408.261)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_472"
                    data-name="Path 472"
                    d="M821.664,409.452h.431v-.936h.465v.936h.574v.379h-.574v1.93c0,.207.109.275.3.275a.845.845,0,0,0,.247-.04h.017v.4a1.748,1.748,0,0,1-.373.04c-.38,0-.655-.155-.655-.574v-2.034h-.431Z"
                    transform="translate(-810.222 -408.223)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_473"
                    data-name="Path 473"
                    d="M825.516,412.047h0a1.2,1.2,0,0,1-.989.47c-.563,0-1-.263-1-.827,0-.7.552-.845,1.292-.96.419-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.74.654h-.448c.016-.574.4-1,1.183-1,.631,0,1.1.235,1.1.942v1.413c0,.241.057.373.316.316h.016v.338a.613.613,0,0,1-.252.046A.456.456,0,0,1,825.516,412.047Zm.012-.684v-.5a2.114,2.114,0,0,1-.678.217c-.465.086-.839.167-.839.6,0,.4.31.482.616.482A.837.837,0,0,0,825.529,411.363Z"
                    transform="translate(-810.3 -408.262)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_474"
                    data-name="Path 474"
                    d="M827.23,409.881h.012a1.113,1.113,0,0,1,.941-.46.892.892,0,0,1,1,.954v2.085h-.465v-2.045c0-.385-.254-.6-.672-.6a.786.786,0,0,0-.816.822v1.827h-.465v-2.97h.465Z"
                    transform="translate(-810.437 -408.261)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_475"
                    data-name="Path 475"
                    d="M831.745,412.047h-.006a1.194,1.194,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.845,1.293-.96.419-.062.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.654H829.9c.017-.574.4-1,1.184-1,.631,0,1.1.235,1.1.942v1.413c0,.241.058.373.316.316h.017v.338a.622.622,0,0,1-.253.046A.456.456,0,0,1,831.745,412.047Zm.013-.684v-.5a2.123,2.123,0,0,1-.678.217c-.466.086-.839.167-.839.6,0,.4.31.482.615.482A.837.837,0,0,0,831.758,411.363Z"
                    transform="translate(-810.562 -408.262)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_476"
                    data-name="Path 476"
                    d="M816.219,415.506h1.919a1.111,1.111,0,0,1,1.212,1.126.969.969,0,0,1-.712,1.016v.017c.419.149.551.437.591,1.046.046.66.115.827.191.878v.023h-.558c-.085-.075-.1-.23-.149-.92-.04-.6-.281-.816-.832-.816h-1.143v1.736h-.517Zm.517,1.953h1.218c.575,0,.862-.287.862-.752,0-.5-.231-.753-.832-.753h-1.247Z"
                    transform="translate(-809.993 -408.517)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_477"
                    data-name="Path 477"
                    d="M819.867,418.177a1.438,1.438,0,1,1,1.436,1.563A1.447,1.447,0,0,1,819.867,418.177Zm2.389,0c0-.654-.322-1.176-.953-1.176s-.96.522-.96,1.176.328,1.173.96,1.173S822.256,418.833,822.256,418.177Z"
                    transform="translate(-810.146 -408.564)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_478"
                    data-name="Path 478"
                    d="M823.174,418.177a1.438,1.438,0,1,1,1.436,1.563A1.447,1.447,0,0,1,823.174,418.177Zm2.389,0c0-.654-.322-1.176-.953-1.176s-.96.522-.96,1.176.328,1.173.96,1.173S825.563,418.833,825.563,418.177Z"
                    transform="translate(-810.285 -408.564)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_325"
                data-name="Group 325"
                transform="translate(752.614 558.199)"
              >
                <g id="Group_324" data-name="Group 324">
                  <path
                    id="Path_479"
                    data-name="Path 479"
                    d="M799.622,378.415l-1.488-2.367h.574L799.886,378h.012l1.195-1.953h.551l-1.5,2.378v1.729h-.517Z"
                    transform="translate(-798.134 -375.99)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_480"
                    data-name="Path 480"
                    d="M803.719,380.2V379.8h-.011a1.031,1.031,0,0,1-.9.477.92.92,0,0,1-1-.982v-2.057h.466v2.045a.585.585,0,0,0,.666.609.769.769,0,0,0,.776-.828v-1.827h.465V380.2Z"
                    transform="translate(-798.289 -376.04)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_481"
                    data-name="Path 481"
                    d="M804.776,378.719a1.448,1.448,0,0,1,1.436-1.562,1.194,1.194,0,0,1,1.27,1.034h-.472a.742.742,0,0,0-.8-.648c-.632,0-.96.523-.96,1.177s.328,1.172.96,1.172a.745.745,0,0,0,.81-.736h.46a1.162,1.162,0,0,1-1.27,1.126A1.447,1.447,0,0,1,804.776,378.719Z"
                    transform="translate(-798.414 -376.036)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_482"
                    data-name="Path 482"
                    d="M809.936,379.788h0a1.192,1.192,0,0,1-.989.472c-.563,0-1.006-.265-1.006-.829,0-.7.553-.844,1.293-.959.42-.063.712-.1.712-.424s-.241-.523-.666-.523c-.483,0-.724.207-.742.655h-.447c.016-.575.4-1.006,1.183-1.006.631,0,1.1.236,1.1.942v1.414c0,.241.057.373.316.316h.016v.338a.629.629,0,0,1-.252.046A.456.456,0,0,1,809.936,379.788Zm.013-.683V378.6a2.138,2.138,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.31.482.616.482A.837.837,0,0,0,809.949,379.105Z"
                    transform="translate(-798.547 -376.037)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_483"
                    data-name="Path 483"
                    d="M810.926,377.194h.431v-.936h.465v.936h.575v.379h-.575v1.93c0,.206.11.276.3.276a.921.921,0,0,0,.247-.04h.017v.4a1.884,1.884,0,0,1-.373.04c-.38,0-.655-.156-.655-.575v-2.034h-.431Z"
                    transform="translate(-798.672 -375.998)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_484"
                    data-name="Path 484"
                    d="M814.778,379.738h-.006a1.19,1.19,0,0,1-.988.472c-.563,0-1.006-.265-1.006-.829,0-.7.552-.844,1.293-.959.419-.063.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.655h-.447c.016-.575.4-1.006,1.183-1.006.631,0,1.1.236,1.1.942v1.414c0,.241.057.373.316.316h.017v.338a.637.637,0,0,1-.253.046A.456.456,0,0,1,814.778,379.738Zm.013-.683v-.506a2.138,2.138,0,0,1-.678.218c-.464.086-.839.167-.839.6,0,.4.311.482.615.482A.837.837,0,0,0,814.791,379.055Zm-.569-3.068h.575l-.471.73h-.4Z"
                    transform="translate(-798.751 -375.987)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_485"
                    data-name="Path 485"
                    d="M816.492,377.623h.01a1.116,1.116,0,0,1,.943-.46.892.892,0,0,1,1,.953V380.2h-.466v-2.045c0-.384-.253-.6-.672-.6a.787.787,0,0,0-.815.823V380.2h-.466v-2.969h.466Z"
                    transform="translate(-798.887 -376.036)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_327"
                data-name="Group 327"
                transform="translate(658.218 624.303)"
              >
                <g
                  id="Group_326"
                  data-name="Group 326"
                  transform="translate(0)"
                >
                  <path
                    id="Path_486"
                    data-name="Path 486"
                    d="M699.589,445h3.251v.454h-1.367V449.1h-.516V445.45h-1.368Z"
                    transform="translate(-699.589 -444.996)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_487"
                    data-name="Path 487"
                    d="M704.569,448.737h-.006a1.192,1.192,0,0,1-.988.471c-.563,0-1.006-.265-1.006-.828,0-.7.552-.844,1.293-.959.419-.064.712-.1.712-.425s-.24-.523-.666-.523c-.483,0-.724.207-.741.655h-.447c.016-.575.4-1.006,1.183-1.006.631,0,1.1.236,1.1.942v1.414c0,.241.057.374.316.316h.017v.339a.655.655,0,0,1-.253.046A.457.457,0,0,1,704.569,448.737Zm.012-.684v-.506a2.134,2.134,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.31.483.615.483A.838.838,0,0,0,704.582,448.053Z"
                    transform="translate(-699.715 -445.044)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_488"
                    data-name="Path 488"
                    d="M706.293,448.713h-.011v.39h-.465V445h.465v1.562h.011a1.124,1.124,0,0,1,.955-.494c.752,0,1.264.575,1.264,1.562,0,.953-.477,1.562-1.282,1.562A1.064,1.064,0,0,1,706.293,448.713Zm1.741-1.081c0-.666-.27-1.171-.832-1.171-.649,0-.949.529-.949,1.171,0,.667.311,1.167.943,1.167C707.764,448.8,708.035,448.3,708.035,447.632Z"
                    transform="translate(-699.851 -444.996)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_489"
                    data-name="Path 489"
                    d="M710.93,448.737h-.006a1.194,1.194,0,0,1-.989.471c-.562,0-1-.265-1-.828,0-.7.552-.844,1.292-.959.42-.064.712-.1.712-.425s-.24-.523-.666-.523c-.483,0-.724.207-.74.655h-.448c.016-.575.4-1.006,1.183-1.006.631,0,1.1.236,1.1.942v1.414c0,.241.057.374.316.316h.016v.339a.647.647,0,0,1-.252.046A.456.456,0,0,1,710.93,448.737Zm.012-.684v-.506a2.136,2.136,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.31.483.616.483A.837.837,0,0,0,710.942,448.053Z"
                    transform="translate(-699.982 -445.044)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_490"
                    data-name="Path 490"
                    d="M711.978,448.185h.478c.062.516.407.661.85.661.489,0,.689-.213.689-.488,0-.333-.229-.419-.776-.535-.585-.12-1.108-.24-1.108-.89,0-.494.38-.816,1.068-.816a1.013,1.013,0,0,1,1.167.931h-.477c-.046-.391-.258-.563-.7-.563-.425,0-.6.19-.6.424,0,.322.286.386.8.489.6.121,1.1.254,1.1.937,0,.6-.489.9-1.172.9C712.478,449.236,712.024,448.852,711.978,448.185Z"
                    transform="translate(-700.111 -445.043)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_491"
                    data-name="Path 491"
                    d="M714.931,447.667a1.446,1.446,0,0,1,1.436-1.561,1.193,1.193,0,0,1,1.269,1.034h-.471a.743.743,0,0,0-.8-.649c-.633,0-.96.523-.96,1.177s.327,1.172.96,1.172a.745.745,0,0,0,.809-.736h.46a1.161,1.161,0,0,1-1.269,1.126A1.447,1.447,0,0,1,714.931,447.667Z"
                    transform="translate(-700.235 -445.043)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_492"
                    data-name="Path 492"
                    d="M718.045,447.667a1.438,1.438,0,1,1,1.436,1.562A1.446,1.446,0,0,1,718.045,447.667Zm2.389,0c0-.654-.322-1.177-.953-1.177s-.96.523-.96,1.177.328,1.172.96,1.172S720.434,448.323,720.434,447.667Z"
                    transform="translate(-700.366 -445.043)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_329"
                data-name="Group 329"
                transform="translate(435.687 527.702)"
              >
                <g id="Group_328" data-name="Group 328">
                  <path
                    id="Path_493"
                    data-name="Path 493"
                    d="M468.789,344.149h.581l1.516,4.108h-.563L469.886,347h-1.637l-.443,1.252h-.529Zm-.39,2.435h1.345l-.472-1.377c-.081-.23-.183-.58-.183-.58h-.013s-.108.345-.189.58Z"
                    transform="translate(-467.278 -344.149)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_494"
                    data-name="Path 494"
                    d="M471.265,348.469h.43c.047.328.356.454.815.454.547,0,.851-.247.851-.763v-.42h-.012a1.011,1.011,0,0,1-.879.46,1.334,1.334,0,0,1-1.3-1.47,1.315,1.315,0,0,1,1.269-1.465,1.029,1.029,0,0,1,.907.447h.012v-.378h.465v2.809c0,.781-.517,1.148-1.3,1.148C471.753,349.291,471.316,348.982,471.265,348.469Zm2.13-1.763c0-.649-.276-1.052-.907-1.052-.557,0-.839.437-.839,1.081s.293,1.074.845,1.074C473.178,347.809,473.4,347.332,473.4,346.707Z"
                    transform="translate(-467.442 -344.196)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_495"
                    data-name="Path 495"
                    d="M474.406,347.339h.477c.063.516.408.66.851.66.489,0,.689-.212.689-.489,0-.332-.23-.419-.776-.534-.585-.121-1.108-.241-1.108-.891,0-.494.379-.815,1.068-.815a1.013,1.013,0,0,1,1.167.93H476.3c-.046-.391-.259-.563-.7-.563-.424,0-.6.191-.6.425,0,.322.287.385.8.488.6.121,1.1.254,1.1.937,0,.6-.489.9-1.172.9C474.906,348.389,474.452,348,474.406,347.339Z"
                    transform="translate(-467.578 -344.196)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_331"
                data-name="Group 331"
                transform="translate(371.939 476.026)"
              >
                <g id="Group_330" data-name="Group 330">
                  <path
                    id="Path_496"
                    data-name="Path 496"
                    d="M400.729,290.2h1.494c1.235,0,1.821.838,1.821,2.067a1.785,1.785,0,0,1-1.861,2.039h-1.454Zm1.437,3.653c.965,0,1.35-.621,1.35-1.586s-.345-1.62-1.309-1.62h-.96v3.206Z"
                    transform="translate(-400.729 -290.202)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_497"
                    data-name="Path 497"
                    d="M406.65,294.358v-.407h-.011a1.031,1.031,0,0,1-.9.476.919.919,0,0,1-1-.982v-2.057h.465v2.045a.585.585,0,0,0,.666.609.769.769,0,0,0,.776-.828v-1.827h.465v2.97Z"
                    transform="translate(-400.898 -290.252)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_498"
                    data-name="Path 498"
                    d="M408.347,291.882h.011a1.056,1.056,0,0,1,.885-.529.448.448,0,0,1,.19.034v.448h-.017a.559.559,0,0,0-.207-.029.856.856,0,0,0-.861.9v1.649h-.466v-2.969h.466Z"
                    transform="translate(-401.03 -290.25)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_499"
                    data-name="Path 499"
                    d="M411.632,293.943h-.006a1.194,1.194,0,0,1-.988.47c-.563,0-1.006-.264-1.006-.827,0-.7.552-.845,1.293-.96.419-.063.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.654h-.447c.016-.574.4-1,1.183-1,.631,0,1.1.236,1.1.943v1.413c0,.241.057.373.316.316h.017v.338a.637.637,0,0,1-.253.046A.456.456,0,0,1,411.632,293.943Zm.012-.684v-.506a2.137,2.137,0,0,1-.678.218c-.465.086-.839.167-.839.6,0,.4.31.482.615.482A.837.837,0,0,0,411.645,293.259Z"
                    transform="translate(-401.104 -290.249)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_500"
                    data-name="Path 500"
                    d="M413.345,291.777h.011a1.116,1.116,0,0,1,.943-.46.893.893,0,0,1,1,.954v2.084h-.466v-2.044c0-.385-.253-.6-.672-.6a.786.786,0,0,0-.815.822v1.826h-.466v-2.969h.466Z"
                    transform="translate(-401.24 -290.249)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_501"
                    data-name="Path 501"
                    d="M415.965,294.522h.43c.047.328.356.454.816.454.546,0,.851-.247.851-.764v-.42h-.011a1.014,1.014,0,0,1-.879.46,1.334,1.334,0,0,1-1.3-1.47,1.316,1.316,0,0,1,1.27-1.465,1.028,1.028,0,0,1,.907.448h.011v-.379h.466v2.809c0,.782-.517,1.149-1.3,1.149C416.454,295.344,416.017,295.033,415.965,294.522Zm2.131-1.763c0-.649-.276-1.051-.907-1.051-.557,0-.839.437-.839,1.08s.293,1.075.844,1.075C417.878,293.862,418.1,293.385,418.1,292.759Z"
                    transform="translate(-401.367 -290.249)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_502"
                    data-name="Path 502"
                    d="M419.131,292.873a1.438,1.438,0,1,1,1.436,1.562A1.447,1.447,0,0,1,419.131,292.873Zm2.389,0c0-.655-.321-1.177-.953-1.177s-.96.522-.96,1.177.328,1.172.96,1.172S421.52,293.529,421.52,292.873Z"
                    transform="translate(-401.504 -290.249)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <g
                id="Group_333"
                data-name="Group 333"
                transform="translate(331.837 475.305)"
              >
                <g id="Group_332" data-name="Group 332">
                  <path
                    id="Path_503"
                    data-name="Path 503"
                    d="M358.864,292.247h.506a1.107,1.107,0,0,0,1.27,1.057c.568,0,.975-.259.975-.77,0-.563-.493-.7-1.229-.856s-1.344-.367-1.344-1.143c0-.677.563-1.085,1.4-1.085A1.37,1.37,0,0,1,362,290.731h-.489a.946.946,0,0,0-1.057-.878c-.557,0-.924.235-.924.66,0,.466.46.6,1.075.724.8.16,1.527.356,1.527,1.281,0,.77-.625,1.2-1.511,1.2A1.539,1.539,0,0,1,358.864,292.247Z"
                    transform="translate(-358.864 -289.45)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_504"
                    data-name="Path 504"
                    d="M362.818,289.534h.466v.574h-.466Zm0,1.137h.466v2.969h-.466Z"
                    transform="translate(-359.03 -289.453)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_505"
                    data-name="Path 505"
                    d="M364.43,291.109h.012a1.114,1.114,0,0,1,.942-.46.892.892,0,0,1,1,.954v2.084h-.465v-2.044c0-.385-.254-.6-.672-.6a.787.787,0,0,0-.816.822v1.826h-.465v-2.97h.465Z"
                    transform="translate(-359.079 -289.5)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_506"
                    data-name="Path 506"
                    d="M368.945,293.275h-.006a1.194,1.194,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.845,1.293-.96.419-.063.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.654H367.1c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.943v1.413c0,.241.057.373.316.316h.017v.338a.638.638,0,0,1-.253.046A.456.456,0,0,1,368.945,293.275Zm.012-.684v-.506a2.138,2.138,0,0,1-.678.218c-.466.086-.839.167-.839.6,0,.4.31.482.615.482A.837.837,0,0,0,368.958,292.591Z"
                    transform="translate(-359.204 -289.501)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_507"
                    data-name="Path 507"
                    d="M370.21,289.534h.466v4.107h-.466Z"
                    transform="translate(-359.342 -289.453)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_508"
                    data-name="Path 508"
                    d="M371.2,292.205a1.438,1.438,0,1,1,1.436,1.562A1.447,1.447,0,0,1,371.2,292.205Zm2.389,0c0-.655-.322-1.177-.953-1.177s-.96.522-.96,1.177.328,1.172.96,1.172S373.589,292.861,373.589,292.205Z"
                    transform="translate(-359.383 -289.5)"
                    fill="#707070"
                  ></path>
                  <path
                    id="Path_509"
                    data-name="Path 509"
                    d="M376.493,293.275h-.006a1.194,1.194,0,0,1-.988.47c-.563,0-1.006-.263-1.006-.827,0-.7.552-.845,1.293-.96.419-.063.712-.1.712-.424s-.24-.523-.666-.523c-.483,0-.724.207-.741.654h-.448c.017-.574.4-1,1.184-1,.631,0,1.1.236,1.1.943v1.413c0,.241.057.373.316.316h.017v.338a.638.638,0,0,1-.253.046A.456.456,0,0,1,376.493,293.275Zm.012-.684v-.506a2.145,2.145,0,0,1-.678.218c-.466.086-.839.167-.839.6,0,.4.31.482.615.482A.837.837,0,0,0,376.506,292.591Z"
                    transform="translate(-359.522 -289.501)"
                    fill="#707070"
                  ></path>
                </g>
              </g>
              <rect id="overlay" transform="translate(82.29 222.25)"></rect>
              <g id="MARKERS">
                {/* <!--BAJA CALIFORNIA--> */}
                <g transform="translate(82 226.073)">
                  <g className="svg-marker baja-california">
                    <path
                      d="M106.249,29.264a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.652,14.2a.74.74,0,0,0,1.1,0c.312-.348,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,106.249,29.264Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,106.249,41.592Z"
                      transform="translate(-98.047 -29.264)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--BAJA CALIFORNIA--> */}

                {/* <!--BAJA CALIFORNIA */}
                {/* <g transform="translate(125.003 222.25)">
                  <g className="svg-marker baja-california">
                    <path
                      d="M151.142,25.273a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.339,13.852,7.652,14.2a.74.74,0,0,0,1.1,0c.312-.349,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,151.142,25.273Zm0,12.328a4.126,4.126,0,1,1,4.127-4.127A4.131,4.131,0,0,1,151.142,37.6Z"
                      transform="translate(-142.94 -25.273)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* BAJA CALIFORNIA--> */}

                {/* <!--CHIHUAHUA--> */}
                {/* <g transform="translate(317.401 249.008)">
                  <g className="svg-marker chihuahua">
                    <path
                      d="M352,53.207a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.313-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,352,53.207Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,352,65.535Z"
                      transform="translate(-343.794 -53.207)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g> */}
                {/* <!--CHIHUAHUA--> */}

                {/* <!--SONORA--> */}
                <g transform="translate(225.024 327.05)">
                  <g className="svg-marker sonora">
                    <path
                      d="M255.56,134.679a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.34,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.313-.349,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,255.56,134.679Zm0,12.328a4.126,4.126,0,1,1,4.127-4.127A4.131,4.131,0,0,1,255.56,147.007Z"
                      transform="translate(-247.357 -134.679)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--SONORA--> */}

                {/* <!--SONORA */}
                {/* <g transform="translate(245.411 359.223)">
                  <g className="svg-marker sonora">
                    <path
                      d="M276.842,168.266a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,276.842,168.266Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,276.842,180.594Z"
                      transform="translate(-268.64 -168.266)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* SONORA--> */}

                {/* <!--CHIHUAHUA */}
                <g transform="translate(340.336 332.784)">
                  <g className="svg-marker chihuahua">
                    <path
                      d="M375.939,140.665a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.313-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,375.939,140.665Zm0,12.328a4.127,4.127,0,1,1,4.126-4.127A4.131,4.131,0,0,1,375.939,152.993Z"
                      transform="translate(-367.737 -140.665)"
                      fill="#0b2343"
                    />
                  </g>
                </g>
                {/* CHIHUAHUA--> */}

                {/* <!--SINALOA--> */}
                {/* <g transform="translate(274.398 404.774)">
                  <g className="svg-marker sinaloa">
                    <path
                      d="M307.1,215.819a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.34,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.313-.349,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,307.1,215.819Zm0,12.328a4.126,4.126,0,1,1,4.126-4.127A4.131,4.131,0,0,1,307.1,228.147Z"
                      transform="translate(-298.901 -215.819)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g> */}
                {/* <!--SINALOA--> */}

                {/* <!--BAJA CALIFORNIA SUR--> */}
                <g transform="translate(232.988 442.043)">
                  <g className="svg-marker baja-california-sur">
                    <path
                      d="M263.873,254.726a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,263.873,254.726Zm0,12.329A4.127,4.127,0,1,1,268,262.929,4.132,4.132,0,0,1,263.873,267.055Z"
                      transform="translate(-255.671 -254.726)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--BAJA CALIFORNIA SUR--> */}

                {/* <!--BAJA CALIFORNIA SUR */}
                {/* <g transform="translate(242.544 475.49)">
                  <g className="svg-marker baja-california-sur">
                    <path
                      d="M273.849,289.643a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.851,7.653,14.2a.739.739,0,0,0,1.1,0c.312-.349,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,273.849,289.643Zm0,12.328a4.126,4.126,0,1,1,4.126-4.126A4.13,4.13,0,0,1,273.849,301.971Z"
                      transform="translate(-265.647 -289.643)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* BAJA CALIFORNIA SUR--> */}

                {/* <!--BAJA CALIFORNIA SUR */}
                {/* <g transform="translate(264.206 472.305)">
                  <g className="svg-marker baja-california-sur">
                    <path
                      d="M296.462,286.318a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,296.462,286.318Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.132,4.132,0,0,1,296.462,298.646Z"
                      transform="translate(-288.26 -286.318)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* BAJA CALIFORNIA SUR--> */}

                {/* <!--JALISCO */}
                {/* <g transform="translate(359.343 558.948)">
                  <g className="svg-marker jalisco">
                    <path
                      d="M395.781,376.769a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.212,8.212,0,0,0,395.781,376.769Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,395.781,389.1Z"
                      transform="translate(-387.579 -376.769)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* JALISCO--> */}

                {/* <!--JALISCO--> */}
                <g transform="translate(396.718 552.153)">
                  <g className="svg-marker jalisco">
                    <path
                      d="M434.8,369.675a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,434.8,369.675Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,434.8,382Z"
                      transform="translate(-426.597 -369.675)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--JALISCO--> */}

                {/* <!--AGUASCALIENTES--> */}
                <g transform="translate(431.546 508.406)">
                  <g className="svg-marker aguascalientes">
                    <path
                      d="M471.156,324.006a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.212,8.212,0,0,0,471.156,324.006Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.132,4.132,0,0,1,471.156,336.334Z"
                      transform="translate(-462.955 -324.006)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--AGUASCALIENTES--> */}

                {/* <!--SAN LUIS POTOSÍ--> */}
                <g transform="translate(474.018 470.606)">
                  <g className="svg-marker san-luis-potosi">
                    <path
                      d="M515.5,284.544a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.851,7.652,14.2a.74.74,0,0,0,1.1,0c.312-.349,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,515.5,284.544Zm0,12.328a4.126,4.126,0,1,1,4.127-4.126A4.131,4.131,0,0,1,515.5,296.872Z"
                      transform="translate(-507.294 -284.544)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--SAN LUIS POTOSÍ--> */}

                {/* <!--TAMAULIPAS--> */}
                {/* <g transform="translate(538.15 488.869)">
                  <g className="svg-marker tamaluipas">
                    <path
                      d="M582.448,303.61a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.313-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,582.448,303.61Zm0,12.328a4.127,4.127,0,1,1,4.126-4.127A4.131,4.131,0,0,1,582.448,315.938Z"
                      transform="translate(-574.245 -303.61)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g> */}
                {/* <!--TAMAULIPAS--> */}

                {/* <!--GUANAJUATO--> */}
                <g transform="translate(454.056 540.685)">
                  <g className="svg-marker guanajuato">
                    <path
                      d="M494.656,357.7a8.212,8.212,0,0,0-8.2,8.2c0,5.612,7.34,13.851,7.653,14.2a.739.739,0,0,0,1.1,0c.313-.349,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,494.656,357.7Zm0,12.328a4.126,4.126,0,1,1,4.127-4.126A4.13,4.13,0,0,1,494.656,370.031Z"
                      transform="translate(-486.454 -357.703)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--GUANAJUATO--> */}

                {/* <!--QUERETARO--> */}
                <g transform="translate(496.953 533.89)">
                  <g className="svg-marker queretaro">
                    <path
                      d="M539.439,350.609a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.339,13.852,7.652,14.2a.74.74,0,0,0,1.1,0c.312-.349,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,539.439,350.609Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,539.439,362.937Z"
                      transform="translate(-531.237 -350.609)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--QUERETARO--> */}

                {/* <!--TOLUCA--> */}
                <g transform="translate(488.883 585.281)">
                  <g className="svg-marker estado-de-mexico toluca">
                    <path
                      d="M531.015,404.259a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.34,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.312-.349,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,531.015,404.259Zm0,12.328a4.127,4.127,0,1,1,4.126-4.127A4.131,4.131,0,0,1,531.015,416.587Z"
                      transform="translate(-522.812 -404.259)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--TOLUCA--> */}

                {/* <!--GUERRERO--> */}
                <g transform="translate(459.578 613.313)">
                  <g className="svg-marker guerrero">
                    <path
                      d="M500.421,433.523a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.652,14.2a.738.738,0,0,0,1.1,0c.313-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,500.421,433.523Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,500.421,445.851Z"
                      transform="translate(-492.219 -433.523)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--GUERRERO--> */}

                {/* <!--GUERRERO */}
                {/* <g transform="translate(513.517 634.549)">
                  <g className="svg-marker guerrero">
                    <path
                      d="M556.731,455.692a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.34,13.852,7.652,14.2a.74.74,0,0,0,1.1,0c.312-.349,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,556.731,455.692Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,556.731,468.02Z"
                      transform="translate(-548.529 -455.692)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* GUERRERO--> */}

                {/* <!--MORELOS--> */}
                <g transform="translate(511.818 587.405)">
                  <g className="svg-marker morelos">
                    <path
                      d="M554.958,406.476a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,554.958,406.476Zm0,12.328a4.127,4.127,0,1,1,4.126-4.127A4.131,4.131,0,0,1,554.958,418.8Z"
                      transform="translate(-546.755 -406.476)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--MORELOS--> */}

                {/* <!--PUEBLA--> */}
                <g transform="translate(533.055 603.969)">
                  <g className="svg-marker puebla">
                    <path
                      d="M577.127,423.768a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.339,13.852,7.652,14.2a.74.74,0,0,0,1.1,0c.312-.349,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,577.127,423.768Zm0,12.328a4.126,4.126,0,1,1,4.127-4.127A4.131,4.131,0,0,1,577.127,436.1Z"
                      transform="translate(-568.925 -423.768)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--PUEBLA--> */}

                {/* <!--ESTADO DE MÉXICO */}
                {/* <g transform="translate(567.881 554.277)">
                  <g className="svg-marker estado-de-mexico">
                    <path
                      d="M613.484,371.892a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.313-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,613.484,371.892Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,613.484,384.22Z"
                      transform="translate(-605.282 -371.892)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* ESTADO DE MÉXICO--> */}

                {/* <!--VERACRUZ--> */}
                <g transform="translate(584.87 586.131)">
                  <g className="svg-marker veracruz">
                    <path
                      d="M631.22,405.146a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,631.22,405.146Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.132,4.132,0,0,1,631.22,417.474Z"
                      transform="translate(-623.018 -405.146)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--VERACRUZ--> */}

                {/* <!--TABASCO--> */}
                {/* <g transform="translate(639.235 603.544)">
                  <g className="svg-marker tabasco">
                    <path
                      d="M687.973,423.325a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.652,14.2a.739.739,0,0,0,1.1,0c.312-.348,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,687.973,423.325Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.132,4.132,0,0,1,687.973,435.653Z"
                      transform="translate(-679.772 -423.325)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g> */}
                {/* <!--TABASCO--> */}

                {/* <!--VERACRUZ */}
                {/* <g transform="translate(621.821 620.533)">
                  <g className="svg-marker veracruz">
                    <path
                      d="M669.795,441.06a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.851,7.652,14.2a.74.74,0,0,0,1.1,0c.312-.349,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,669.795,441.06Zm0,12.328a4.126,4.126,0,1,1,4.127-4.126A4.131,4.131,0,0,1,669.795,453.388Z"
                      transform="translate(-661.593 -441.06)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* VERACRUZ--> */}

                {/* <!--OAXACA--> */}
                <g transform="translate(553.44 647.715)">
                  <g className="svg-marker oaxaca">
                    <path
                      d="M598.41,469.437a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.34,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.312-.349,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,598.41,469.437Zm0,12.328a4.127,4.127,0,1,1,4.126-4.127A4.131,4.131,0,0,1,598.41,481.765Z"
                      transform="translate(-590.207 -469.437)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--OAXACA--> */}

                {/* <!--CHIAPAS--> */}
                <g transform="translate(643.907 651.113)">
                  <g className="svg-marker chiapas">
                    <path
                      d="M692.851,472.984a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.339,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.312-.349,7.653-8.588,7.653-14.2A8.212,8.212,0,0,0,692.851,472.984Zm0,12.328a4.126,4.126,0,1,1,4.127-4.126A4.131,4.131,0,0,1,692.851,485.312Z"
                      transform="translate(-684.649 -472.984)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--CHIAPAS--> */}

                {/* <!--TABASCO */}
                <g transform="translate(659.621 595.474)">
                  <g className="svg-marker tabasco">
                    <path
                      d="M709.256,414.9a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,709.256,414.9Zm0,12.329a4.127,4.127,0,1,1,4.126-4.127A4.131,4.131,0,0,1,709.256,427.229Z"
                      transform="translate(-701.054 -414.9)"
                      fill="#0b2343"
                    />
                  </g>
                </g>
                {/* TABASCO--> */}

                {/* <!--CAMPECHE--> */}
                <g transform="translate(717.383 561.496)">
                  <g className="svg-marker campeche">
                    <path
                      d="M769.557,379.429a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.851,7.653,14.2a.739.739,0,0,0,1.1,0c.312-.349,7.653-8.587,7.653-14.2A8.211,8.211,0,0,0,769.557,379.429Zm0,12.329a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,769.557,391.758Z"
                      transform="translate(-761.355 -379.429)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--CAMPECHE--> */}

                {/* <!--YUCATAN--> */}
                <g transform="translate(734.373 529.218)">
                  <g className="svg-marker yucatan">
                    <path
                      d="M787.293,345.732a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.652,14.2a.739.739,0,0,0,1.1,0c.312-.348,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,787.293,345.732Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.132,4.132,0,0,1,787.293,358.06Z"
                      transform="translate(-779.091 -345.732)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--YUCATAN--> */}

                {/* <!--YUCATAN */}
                {/* <g transform="translate(787.887 524.971)">
                  <g className="svg-marker yucatan">
                    <path
                      d="M843.16,341.3a8.212,8.212,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.313-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,843.16,341.3Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,843.16,353.626Z"
                      transform="translate(-834.957 -341.298)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* YUCATAN--> */}

                {/* <!--QUINTANA ROO--> */}
                <g transform="translate(782.791 571.69)">
                  <g className="svg-marker quintana-roo">
                    <path
                      d="M837.839,390.071a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.652,14.2a.739.739,0,0,0,1.1,0c.312-.348,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,837.839,390.071Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.132,4.132,0,0,1,837.839,402.4Z"
                      transform="translate(-829.637 -390.071)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--QUINTANA ROO--> */}

                {/* <!--SINALOA */}
                <g transform="translate(298.29 435.354)">
                  <g className="svg-marker sinaloa">
                    <path
                      d="M332.044,247.743a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.339,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.312-.349,7.653-8.588,7.653-14.2A8.212,8.212,0,0,0,332.044,247.743Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,332.044,260.071Z"
                      transform="translate(-323.842 -247.743)"
                      fill="#0b2343"
                    />
                  </g>
                </g>
                {/* SINALOA--> */}

                {/* <!--DURANGO--> */}
                <g transform="translate(371.873 434.398)">
                  <g className="svg-marker durango">
                    <path
                      d="M408.861,246.745a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.213,8.213,0,0,0,408.861,246.745Zm0,12.329a4.127,4.127,0,1,1,4.127-4.127A4.132,4.132,0,0,1,408.861,259.074Z"
                      transform="translate(-400.659 -246.745)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--DURANGO--> */}

                {/* <!--DURANGO */}
                {/* <g transform="translate(400.859 407.322)">
                  <g className="svg-marker durango">
                    <path
                      d="M439.122,218.479a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,439.122,218.479Zm0,12.329a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,439.122,230.808Z"
                      transform="translate(-430.92 -218.479)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* DURANGO--> */}

                {/* <!--COAHUILA--> */}
                <g transform="translate(442.27 411.782)">
                  <g className="svg-marker coahuila">
                    <path
                      d="M482.352,223.135a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.313-.348,7.653-8.587,7.653-14.2A8.211,8.211,0,0,0,482.352,223.135Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,482.352,235.463Z"
                      transform="translate(-474.15 -223.135)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--COAHUILA--> */}

                {/* <!--NUEVO LEON--> */}
                <g transform="translate(481.45 407.641)">
                  <g className="svg-marker nuevo-leon">
                    <path
                      d="M523.255,218.812a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.339,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,523.255,218.812Zm0,12.328a4.127,4.127,0,1,1,4.127-4.127A4.131,4.131,0,0,1,523.255,231.14Z"
                      transform="translate(-515.053 -218.812)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g>
                {/* <!--NUEVO LEON--> */}

                {/* <!--COAHUILA */}
                {/* <g transform="translate(479.54 358.267)">
                  <g className="svg-marker coahuila">
                    <path
                      d="M521.26,167.268a8.211,8.211,0,0,0-8.2,8.2c0,5.613,7.339,13.852,7.652,14.2a.74.74,0,0,0,1.1,0c.312-.349,7.652-8.588,7.652-14.2A8.211,8.211,0,0,0,521.26,167.268Zm0,12.328a4.126,4.126,0,1,1,4.127-4.127A4.131,4.131,0,0,1,521.26,179.6Z"
                      transform="translate(-513.058 -167.268)"
                      fill="#0b2343"
                    />
                  </g>
                </g> */}
                {/* COAHUILA--> */}

                {/* <!--TAMAULIPAS--> */}
                {/* <g transform="translate(506.296 379.609)">
                  <g className="svg-marker tamaulipas">
                    <path
                      d="M549.193,189.548a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.739.739,0,0,0,1.1,0c.312-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,549.193,189.548Zm0,12.329a4.127,4.127,0,1,1,4.126-4.127A4.131,4.131,0,0,1,549.193,201.877Z"
                      transform="translate(-540.991 -189.548)"
                      fill="#0b2343"
                    ></path>
                  </g>
                </g> */}
                {/* <!--TAMAULIPAS--> */}

                {/* <!--TAMAULIPAS */}
                <g transform="translate(549.937 409.628)">
                  <g className="svg-marker tamaulipas">
                    <path
                      d="M594.751,224.133a8.211,8.211,0,0,0-8.2,8.2c0,5.612,7.34,13.852,7.653,14.2a.738.738,0,0,0,1.1,0c.313-.348,7.653-8.588,7.653-14.2A8.211,8.211,0,0,0,594.751,224.133Zm0,12.328a4.127,4.127,0,1,1,4.126-4.127A4.131,4.131,0,0,1,594.751,236.461Z"
                      transform="translate(-586.549 -224.133)"
                      fill="#0b2343"
                    />
                  </g>
                </g>
                {/* TAMAULIPAS--> */}
              </g>
            </g>
          </svg>
            {mapTooltip.visible && (
              <div
                className={`map-pin-tooltip map-pin-tooltip--active map-pin-tooltip--placement-${
                  mapTooltip.placement || "right"
                }`}
                style={{
                  left: mapTooltip.left,
                  top: mapTooltip.top,
                  maxWidth: mapTooltip.maxW != null ? mapTooltip.maxW : 300,
                }}
                role="status"
                aria-label={`(${mapTooltip.code}) ${mapTooltip.name}`}
                onMouseEnter={cancelHoverLeaveTimer}
                onMouseLeave={handleMapTooltipMouseLeave}
              >
                <span className="map-pin-tooltip__label">
                  {mapTooltip.code ? `(${mapTooltip.code}) ` : ""}
                  {mapTooltip.name || ""}
                </span>
              </div>
            )}
        </div>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default StatiosNationalsHome;

// COMMENTS
// SVG del header title
